import { IMAGE_UPLOAD_ACCEPT, ARTWORK_UPLOAD_ACCEPT, IMAGE_UPLOAD_MESSAGES, MAX_IMAGE_UPLOAD_BYTES } from '../../../packages/imaging/src/upload-policy.js';
import {
  DESIGN_AREA_OPTION_TYPE,
  DESIGN_AREA_PROPERTY,
  authoredLayerIds,
  designCutoutAssetId,
  MAP_DEFAULTS,
  clampMapZoom,
  designAreaPrice,
  designAreasOf,
  describeDesign,
  formatZoneSize,
  inSpotifyScope,
  isDesignAreaAnswer,
  isEmptyDesign,
  activePrintLocation,
  movePrintLocationDesign,
  mapLocationOf,
  mapViewportOf,
  mapPickerPin,
  applyShopperPlacements,
  readPlacements,
  spotifyPlayerScope,
  normalizeDesignAnswer,
  resolveDesignAreaConfig,
  zoneScene,
  annotateFontFallbacks,
  choiceDisplayOf,
  choiceFontId,
  drawnFontId,
  fontChoiceSample,
  captionsOf,
  showsCaptions,
  CHOICE_SIZE_SCALE,
  describeCollage,
  isCollageAnswer,
  PHOTO_COLLAGE_OPTION_TYPE,
} from '@pz/scene';
import { countedLetters, dropLoneSurrogates, emojiAllowed, fromAttributeOf, isEmojiGrapheme, stripEmoji, textLength, truncateText, variationAnswer } from '@pz/scene';
import type {
  DeferredElement,
  DesignAreaAnswer,
  DesignAreaConfig,
  DesignAreaElement,
  DesignLayer,
  ResolvedDesignAreaConfig,
  Scene,
  TextElement,
} from '@pz/scene';
import { round3, round5 } from './canvas-edit.js';
import { planPhotoSpill, runLimited } from './photospill.js';
import {
  donePicking,
  dropPending,
  fitForUpload,
  inAppBrowser,
  keepPending,
  markPicking,
  pendingFor,
  pictureSize,
  smallBitmap,
  type PendingPicture,
  type PictureSize,
} from './photo-intake.js';
import { cameraAvailable, takePhoto } from './camera.js';
import { applyTileArt } from './tile-art.js';
import type { CollageRender } from './collage/board-types.js';
import { CollageHost } from './collage/host.js';
import { CollageIntake, type CollageMedia } from './collage/intake.js';
import { createCollageStep, type CollageStep } from './collage/step.js';
import { COLLAGE_TEXTS } from './collage/texts.js';
import type { Api } from './api.js';
import { createAudioControl } from './audio-control.js';
import { createDesigner } from './designer/index.js';
import type {
  ArtworkItem,
  ArtworkLibrary,
  CreateDesigner,
  DesignerArtwork,
  DesignerFontCategory,
  DesignerFonts,
  DesignerHandle,
  DesignerLibraryEntry,
  DesignerProductBackdrop,
  DesignerProductOptions,
  DesignerRenderer,
  DesignerTexts,
  DesignerUploads,
} from './designer-types.js';
import { createLayer, pointerIsCoarse, revealFocused, showToast, type LayerHandle } from './layers.js';
import { applyMoment, captureMoment, momentFieldsOf, momentOffers, readMomentStore, writeMomentStore } from './moment.js';
import {
  requirementFor,
  assessPhoto,
  sharpnessOf,
  measurementPixels,
  PHOTO_TEXTS,
  type PhotoAssessment,
} from './photoquality.js';
import type {
  AudioSelection,
  ConditionRule,
  OptionDef,
  ProductDefinition,
  Values,
  VariationContext,
} from './types.js';

/**
 * The shopper-facing form.
 *
 * Built from the option set the template author defined, so adding a field to a
 * product is a data change rather than a code change.
 */

/**
 * What is happening to a shopper's photo after it has been uploaded.
 *
 * The cut-out runs against the preview, but on a wizard step — or an inline page
 * whose preview is a collapsed card — the shopper is looking at the upload card,
 * not the product. Without this they get an unexplained pause.
 */
export interface PhotoState {
  /** Explicit action after an AI cache miss; absent for ordinary upload/cut-out states. */
  action?: { label: string; run: () => void };
  phase: 'cutting' | 'ready' | 'failed';
  /** For `ready`: the cut-out to show in place of the original thumbnail. */
  url?: string;
  message?: string;
  /**
   * For `ready`: the one calm quality sentence, when the placed picture will
   * print soft (a small head enlarged into a Mini Me slot). Kept on the card
   * after the cut instead of being cleared with the progress text.
   */
  advice?: string;
  /** The photo this is about; news about any other photo is turned down. */
  assetId?: string;
}

export interface FormOptions {
  product: ProductDefinition;
  api: Api;
  onChange: (values: Values) => void;
  /** Every sound the shopper picked, in delivery order. */
  /** One sound question's picks; `optionId` says which (a product may have two). */
  onAudioChange: (audios: AudioSelection[], optionId?: string) => void;
  /** A fresh upload's id and URL — the preview registers it so the photo can draw. */
  /**
   * A shopper upload landed. `optionId` says which question it answered. `url`
   * is what the page draws (the server's preview copy when it made one);
   * `original` is the uploaded picture's own pixel size, for print-quality
   * sums that must not be made on the smaller copy.
   */
  onAsset?: (id: string, url: string, optionId: string, original?: { w: number; h: number }) => void;
  /**
   * Save the answers NOW, not after the usual pause: the phone may close the
   * page while its camera or gallery is open, or right after a photo lands.
   */
  flushDraft?: () => void;
  /** The shopper asked to keep (or stop keeping) their original background. */
  formatPrice: (amount: number) => string;
  /** Sounds restored from an earlier commit — the edit-from-cart flow. */
  initialAudios?: AudioSelection[];
  /**
   * The sound question the restored sounds belong to (restore-sounds.ts). The
   * others start empty: an order's sounds are one list, and giving it to every
   * sound question doubled it.
   */
  initialAudiosOption?: string;
  /**
   * Sounds from this browser's draft, per sound question — a reload must not
   * lose a recording the shopper already made. Used when `initialAudios` is not.
   */
  draftAudios?: Record<string, AudioSelection[]>;
  /** Where an uploaded asset's pixels live, for restored photo thumbnails. */
  resolveAssetUrl?: (id: string) => string | undefined;
  /**
   * A picture uploaded INSIDE the designer (or its cut-out), so the product
   * preview can draw it.
   *
   * Deliberately not `onAsset`: that one answers a photo question and starts the
   * whole photo pipeline behind it — resolution check, background cut-out, the
   * author's initial framing, and on a compact page the lightbox opening so the
   * shopper can drag the picture into place. A layer inside a design area wants
   * none of that; the designer owns its own framing and its own cut-out button.
   */
  onDesignAsset?: (id: string, url: string) => void;
  /**
   * A photo collage's photo landed (docs/photo-collage.md): the preview learns
   * its 1024 px draw copy under the asset id — and that is all. Deliberately
   * not `onAsset`: no cut-out, no style, no lightbox and no editor selection
   * ever runs for a collage photo (D8: no paid call for a collage slot).
   */
  onCollageAsset?: (optionId: string, media: CollageMedia) => void;
  /** The page's side of the collage board ("Raspored slika"). */
  collage?: {
    /** Pixels of a scene or a part of it (Preview.renderView); undefined without the print renderer. */
    render?(
      scene: Scene,
      opts: { scale: number; viewport?: { x: number; y: number; w: number; h: number } },
    ): Promise<CollageRender | undefined>;
    /** The board opened or closed: the page pauses its own preview meanwhile. */
    onBoard?(optionId: string, open: boolean): void;
  };
  /**
   * Loads the print renderer if the page has not already.
   *
   * The designer draws every layer with Skia — the same engine as the print
   * file — and a device the widget skipped the upgrade for (very little
   * reported memory) would otherwise open a designer that can only ever show
   * approximations.
   */
  loadRenderer?: () => Promise<void>;
  /** True in Studio's preview frame: artwork is scoped by template, not product. */
  previewing?: boolean;
  onDesignShare?: () => Promise<string>;
  /**
   * The product picture a design question is edited on — the mockup with every
   * other answer applied and this zone left empty — and where the zone lies on
   * it. Undefined when the template has no flat mockup copy of the zone.
   */
  designBackdrop?: (optionId: string, values: Values) => Promise<DesignerProductBackdrop | undefined>;
  /**
   * The page's own product choices (WooCommerce's variation selects), offered
   * in the designer's Options step next to this form's choice questions.
   */
  pageOptions?: { available(): boolean; mount(host: HTMLElement): () => void };
  /** "Moji dizajni": saved designs of this shop, in this browser. */
  library?: {
    save(name: string): Promise<void>;
    list(): DesignerLibraryEntry[];
    remove(id: string): void;
    suggestName(): string;
  };
}

/**
 * Questions that choose the PRODUCT rather than personalise it — Zazzle's
 * Options step (size, colour, style). Text, photos and the design areas stay
 * where they are; a swatch or a select may also pick a layout, and that is
 * still a choice about what is being bought.
 */
const PRODUCT_CHOICE_TYPES = new Set(['swatch', 'dropdown', 'color', 'checkbox']);

/**
 * The song picker's words.
 *
 * The widget speaks Serbian everywhere else (its texts live in main.ts, which
 * the studio can override per product), so these do too; each one carries the
 * English original from the control this is parity with, so a translation pass
 * can line the two up.
 */
export const SONG_TEXTS = {
  /** "Search for a track" — used when the option itself sets no placeholder. */
  fieldPlaceholder: 'Pretraži pesmu',
  /** "Search for a track" — the modal's own box; the same words in both places. */
  searchPlaceholder: 'Pretraži pesmu',
  /** Names the dialog for a screen reader when the option has no label. */
  dialogLabel: 'Izaberi pesmu',
  /** "Close" */
  close: 'Zatvori',
  /** "No tracks found." */
  empty: 'Ništa nije pronađeno.',
  /** "Song search is not working right now." */
  failed: 'Pretraga trenutno ne radi.',
  /** "Song search is not set up for this shop yet." */
  unconfigured: 'Pretraga pesama još nije podešena za ovu prodavnicu.',
  /** "The text on the product" — heads the lines the plaque actually prints. */
  textHeading: 'Tekst na proizvodu',
  /** "Copied from Spotify — change it freely, the preview follows at once." */
  prefilled: 'Prepisano sa Spotify-a — slobodno izmeni, promena se odmah vidi na pregledu.',
  /** "Take it back from Spotify" — undoes the shopper's own wording. */
  reset: 'Vrati sa Spotify-a',
  /** "Too many searches in a short time — wait a few seconds and try again." */
  rateLimited: 'Previše pretraga za kratko vreme — sačekaj par sekundi pa pokušaj ponovo.',
  /** "Popular" — the shop's curated songs, before the shopper types. */
  popular: 'Popularno',
  /** "Recently picked" — songs picked on this device before. */
  recent: 'Nedavno birano',
  /** Release tags beside a row: a single and a compilation look like the album otherwise. */
  single: 'Singl',
  compilation: 'Kompilacija',
  /** "Explicit" — Spotify's own E badge, spelled out for a screen reader. */
  explicit: 'Eksplicitan sadržaj',
  /** The picked-song card. */
  cardLabel: 'Izabrana pesma',
  change: 'Promeni',
  remove: 'Ukloni',
  /** Opens Spotify's own player for the picked song, in the card. */
  listen: 'Poslušaj',
  hideListen: 'Sakrij plejer',
  /** Spotify's attribution rule: shown metadata and artwork link back to Spotify. */
  openOnSpotify: 'Otvori na Spotify-u',
  /** Under the card of a product with no sound of its own (a poster, a plaque). */
  noSound: 'Proizvod ne pušta zvuk: Spotify kod na njemu otvara ovu pesmu u Spotify aplikaciji.',
};

/** "4:23" from milliseconds; empty for an unknown length. */
export function formatDuration(ms: unknown): string {
  const total = Math.round(Number(ms) / 1000);
  if (!Number.isFinite(total) || total <= 0) return '';
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * The second line of a result row and of the picked card: "÷ · 2017 · 4:23".
 *
 * The same recording is released on an album, a single and a string of
 * compilations, and title plus artist alone left the shopper three identical
 * rows to guess between. Album, year and length are what tell them apart.
 */
export function songMeta(track: { album?: unknown; year?: unknown; durationMs?: unknown }): string {
  return [
    typeof track.album === 'string' ? track.album : '',
    typeof track.year === 'string' ? track.year : '',
    formatDuration(track.durationMs),
  ].filter(Boolean).join(' · ');
}

/**
 * The sentence for a failed search.
 *
 * The API answers with a code; the words are ours. Its English `error` text
 * ("Spotify search failed (429)") is for logs and never reaches the shopper.
 */
export function songErrorText(code: unknown): string {
  if (code === 'rate_limited') return SONG_TEXTS.rateLimited;
  if (code === 'unconfigured') return SONG_TEXTS.unconfigured;
  return SONG_TEXTS.failed;
}

/** Songs picked on this device, newest first, for "Nedavno birano". */
const RECENT_SONGS_KEY = 'pz:recent-songs';
const RECENT_SONGS_MAX = 4;

function readRecentSongs(): SpotifyTrackResult[] {
  try {
    const raw = globalThis.localStorage?.getItem(RECENT_SONGS_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list)
      ? list.filter((t): t is SpotifyTrackResult =>
          Boolean(t && typeof t === 'object' && typeof (t as { uri?: unknown }).uri === 'string')).slice(0, RECENT_SONGS_MAX)
      : [];
  } catch {
    // Private mode, blocked storage: there is simply no history.
    return [];
  }
}

function rememberSong(track: SpotifyTrackResult): void {
  try {
    const next = [track, ...readRecentSongs().filter((t) => t.uri !== track.uri)].slice(0, RECENT_SONGS_MAX);
    globalThis.localStorage?.setItem(RECENT_SONGS_KEY, JSON.stringify(next));
  } catch {
    /* storage refused; nothing to remember */
  }
}

/**
 * The typing fields' words. `{letters}` is replaced with the letters found.
 * Overridable per product as `text.missingLetters`.
 */
export const TEXT_FIELD_TEXTS = {
  /** "The font on this product has no …; write them without the accent marks or pick another font." */
  missingLetters:
    'Font na ovom proizvodu nema slova: {letters}. Neće se odštampati kako treba — napiši ih bez kvačica (npr. c umesto ć) ili izaberi drugi font, ako ga možeš birati.',
  /**
   * "We cannot print this emoji; pick another." Emoji are drawn from the
   * bundled Noto set, so only one newer than that set — or one whose font did
   * not load — lands here.
   */
  missingEmoji: 'Ovaj emoji ne možemo da odštampamo: {letters}. Izaberi neki drugi.',
};

/**
 * The place picker's words.
 *
 * These used to keep Customily's English verbatim ("Search Location",
 * CANCEL / SELECT) so a shop moving over would see no change. BearBox's shop is
 * Serbian, and English buttons in the middle of a Serbian page read as a
 * broken plugin — so the defaults are Serbian now. A merchant still words any
 * of them per product through `display.labels` (`map.cancel`, …), and a
 * question's own placeholder always wins over `fieldPlaceholder`.
 */
export const MAP_TEXTS = {
  /** "Search location" — the field, when the question sets no placeholder. */
  fieldPlaceholder: 'Pretraži lokaciju',
  /** "Search location" — the dialog's geocoder; the same words in both places. */
  searchPlaceholder: 'Pretraži lokaciju',
  /** Names the dialog for a screen reader when the option has no label of its own. */
  dialogLabel: 'Izaberi lokaciju',
  /** The dialog's bottom bar: "Cancel" / "Select". */
  cancel: 'Otkaži',
  select: 'Izaberi',
  /** "Zoom in" / "Zoom out" — the overlay buttons' screen-reader names. */
  zoomIn: 'Uvećaj mapu',
  zoomOut: 'Umanji mapu',
  /** "Nothing found." */
  empty: 'Ništa nije pronađeno.',
  /** "Search is not working right now." */
  failed: 'Pretraga trenutno ne radi.',
  /** "Map search is not set up for this shop yet." */
  unconfigured: 'Pretraga mape još nije podešena za ovu prodavnicu.',
  /** "Drag the pin to the exact location" — the pin's screen-reader name and tooltip. */
  pinLabel: 'Prevuci pin na tačnu lokaciju',
  /** "Drag the pin to the exact location. Move and zoom the map to look around." */
  pinHint: 'Prevuci pin na tačnu lokaciju. Mapu možeš pomerati i uvećavati.',
};

/**
 * The upload controls' words.
 *
 * Serbian, like the rest of the widget ("SELECT IMAGE", Customily's English
 * default, was the one English phrase shoppers met on bearbox.rs). A merchant
 * overrides the button per option with `config.buttonText`, or shop-wide per
 * product with `display.labels['upload.imageButton']`.
 */
export const UPLOAD_TEXTS = {
  /** "Select a picture" — the upload button. */
  imageButton: 'Izaberi sliku',
  /** The vector placeholder's equivalent; same shape, different file kinds. */
  vectorButton: 'Izaberi fajl',
  /** "Move and zoom the picture right on the preview." */
  framingHint: 'Dodirni sliku na pregledu da je pomeriš, uvećaš ili okreneš.',
  replace: 'Zameni sliku',
  /** "Remove picture" — the card's ✕. */
  remove: 'Ukloni sliku',
  /** "Take a photo" — the in-page camera, beside the picker on phones. */
  cameraButton: 'Slikaj',
  /** "Try again" — the same picture, sent again, without picking it again. */
  retry: 'Pokušaj ponovo',
  /** The page was reloaded mid-upload and is finishing it by itself. */
  resuming: 'Nastavljam slanje tvoje slike…',
  /** The page was reloaded while the phone's camera or gallery was open. */
  interrupted:
    'Telefon je osvežio stranicu dok je kamera bila otvorena, pa slika nije stigla. Tvoji ostali odgovori su sačuvani — slikaj ponovo dugmetom „Slikaj“ ili izaberi sliku iz galerije.',
  /** The same, where the page runs inside a social app's browser. */
  interruptedInApp:
    'Aplikacija je osvežila stranicu dok je kamera bila otvorena, pa slika nije stigla. Tvoji odgovori su sačuvani. Izaberi sliku iz galerije, ili otvori stranicu u Chrome/Safari pregledaču ako se ovo ponovi.',
  /** Slot N of M, for the multi-upload fan-out's empty cards. */
  slot: (index: number, total: number): string => `Slika ${index} od ${total}`,
  /** "Uploaded 3 of 15" — how far a many-photo product is. */
  count: (filled: number, total: number): string => `Otpremljeno ${filled} od ${total}`,
  /** Moving a photo one slot back / forward in a collage. */
  moveBack: 'Pomeri sliku unazad',
  moveForward: 'Pomeri sliku unapred',
  /**
   * The merchant's own minimum-size rule (Customily's Min. Image Width/Height)
   * still applies, but the shopper reads the same calm, numberless sentence as
   * every other quality advice — see the owner's rule in photoquality.ts.
   * The size arguments are accepted for callers that still have them, and
   * ignored: the wording never carries numbers.
   */
  tooSmall: (_w?: number, _h?: number, _minW?: number, _minH?: number): string => PHOTO_TEXTS.warn,
};

/**
 * The choice controls' words — swatch, dropdown, colour, checkbox.
 *
 * A question's own placeholder always wins over `dropdownPlaceholder`.
 */
export const CHOICE_TEXTS = {
  /** "Choose an option" — a dropdown with nothing picked yet. */
  dropdownPlaceholder: 'Izaberi opciju',
  /** "Pick a colour" — the colour button's screen-reader name. */
  colorLabel: 'Izaberi boju',
  /** The hex box beside the colour button. */
  hexLabel: 'HEX',
  /** "Yes" / "No" — how a checkbox answer reads on the cart line. */
  yes: 'Da',
  no: 'Ne',
};

/**
 * The design-area card's words.
 *
 * Customily has no equivalent of this control (it sells fixed templates, not a
 * free canvas), so there is nothing to keep verbatim: all of it is the widget's
 * usual Serbian, with the English original beside each line. A shop overrides
 * the button per option with `config.buttonText`, and the two states of it
 * shop-wide with the `designArea.open` / `designArea.edit` labels.
 */
export const DESIGN_TEXTS = {
  /** "Open the designer" — the empty card's button. */
  open: 'Otvori dizajner',
  /** "Edit the design" — once the shopper has put something in it. */
  edit: 'Uredi dizajn',
  /** "Add text, a picture or an ornament" — the empty card's summary line. */
  empty: 'Dodaj tekst, sliku ili ukras',
  /** "Print size: 7,5 × 5 cm" — the physical truth, on the card and in the designer. */
  size: (size: string): string => `Veličina štampe: ${size}`,
  /** "Surcharge" — the design's own price, which grows with what is in it. */
  surcharge: 'Doplata',
  /** Names the full-screen designer for a screen reader when the option has no label. */
  dialogLabel: 'Dizajner',
  /** "The designer could not be opened…" — the one failure the shopper can act on. */
  failed: 'Dizajner se nije otvorio. Osveži stranicu i probaj ponovo.',
  /** "Close" — the fallback card's only button. */
  close: 'Zatvori',
  /** "Required" — on a template field still holding the author's sample. */
  mustReplace: 'Obavezno',
  /** "Replace image" — the picture field's own button. */
  replaceImage: 'Zameni sliku',
  /** While a replacement picture is on its way to the server. */
  uploading: 'Šalje se…',
  /** …and when it did not arrive. */
  uploadFailed: 'Slika nije poslata. Pokušaj ponovo.',
  /** The designer's product-options step (size, colour…). */
  optionsTab: 'Opcije',
  /** Zazzle's Moment: details remembered on this device, offered on other products. */
  momentApply: (labels: string): string => `Popuni iz mojih detalja: ${labels}`,
  momentSave: 'Zapamti za druge proizvode',
  momentSaved: 'Zapamćeno na ovom uređaju — ponudićemo ih i na drugim proizvodima.',
  momentApplied: 'Tvoji detalji su upisani. Proveri dizajn pre dodavanja u korpu.',
};

/**
 * How often a design being built writes itself back into the answers, ms.
 *
 * The first change of a gesture lands at once (the product behind the dialog
 * should react the moment the shopper drops a layer), and further ones no more
 * than four times a second: each write re-renders the whole product preview
 * through WASM, and a drag fires changes per frame.
 */
const DESIGN_LIVE_MS = 250;
/** How wide the card's design thumbnail is rendered, in CSS pixels. */
const DESIGN_THUMB_PX = 320;
/** How often the designer asks whether the paired phone has sent a picture, ms. */
const PHONE_POLL_MS = 2000;
/** The print renderer is polled for at this cadence while a designer is open, ms. */
const RENDERER_POLL_MS = 400;
/** …and given up on after this many polls (≈ 40 s) — the DOM draft stays. */
const RENDERER_POLLS = 100;

/** How long after the last keystroke the geocoder runs, ms. */
const MAP_DEBOUNCE_MS = 300;
/** Below this many characters every query matches everything; don't ask. */
const MAP_MIN_QUERY = 2;
/** Suggestions per search — the number Mapbox's own geocoder shows. */
const MAP_RESULT_COUNT = 5;
/** Where picking a suggestion lands: a town fills the frame, a street is legible. */
const MAP_PLACE_ZOOM = 13;

/**
 * The half of `@pz/scene-render` the dialog's map needs.
 *
 * Named as its own type so the engine can be handed in: the widget has no
 * canvas in tests, and a page that never opens the dialog should never run the
 * tile engine at all.
 */
type TileEngine = Pick<
  typeof import('@pz/scene-render'),
  'createTileMap' | 'canCreateTileMap' | 'resolveMapStyle'
>;
/** The live map inside the dialog. */
type TileMap = ReturnType<TileEngine['createTileMap']>;

let tileEngine: TileEngine | undefined;

/**
 * Hands the map dialog its tile engine instead of letting it load the real one.
 *
 * The only seam of its kind in the form, and production never sets it: it
 * exists so the control itself — not a copy of its logic — can be driven where
 * there is no canvas to draw on.
 */
export function useTileEngine(engine: TileEngine | undefined): void {
  tileEngine = engine;
}

let designerFactory: CreateDesigner | undefined;

/**
 * Hands the design-area control its designer instead of the shipped one.
 *
 * The same seam as `useTileEngine`, for the same reason: the designer needs a
 * canvas, a Skia bundle and a pointer, none of which exist where the control is
 * driven under test — and a test that reimplemented the card's half of the
 * conversation would prove nothing about the card. Production never sets it.
 */
export function useDesigner(factory: CreateDesigner | undefined): void {
  designerFactory = factory;
}

/**
 * The scene element a design-area question fills, found through its binding.
 *
 * The PRINT side first: that is where the zone's transform is kept equal to its
 * `config.sizeMm`, and the config is what the designer and the price are built
 * from. A mockup's copy carries the same config (the design→product sync copies
 * it), so either would do — but only one of them is the physical truth, and
 * reading the other would make a 7.5 cm zone look like whatever size the owner
 * dragged it to on the product photo.
 */
export function designZoneOf(
  product: ProductDefinition,
  optionId: string,
): { zone: DesignAreaElement; sideId: string } | undefined {
  const wanted = new Set(
    (product.bindings ?? [])
      .filter((b) => b.optionId === optionId && b.property === DESIGN_AREA_PROPERTY)
      .map((b) => b.elementId),
  );
  if (wanted.size === 0) return undefined;
  const sides = [...(product.sides ?? [])].sort(
    (a, b) => Number(b.target === 'print') - Number(a.target === 'print'),
  );
  for (const side of sides) {
    if (!Array.isArray(side.elements)) continue;
    for (const zone of designAreasOf(side)) {
      if (wanted.has(zone.id)) return { zone, sideId: side.id };
    }
  }
  return undefined;
}

/** The picker groups the designer knows; anything else is "nobody said". */
const FONT_CATEGORIES: readonly DesignerFontCategory[] = [
  'serif', 'sans', 'script', 'handwriting', 'display', 'decorative', 'mono', 'symbols',
];

function isFontCategory(value: unknown): value is DesignerFontCategory {
  return typeof value === 'string' && (FONT_CATEGORIES as readonly string[]).includes(value);
}

/**
 * The design a zone STARTS with — what its author composed in Studio.
 *
 * Zazzle's product page shows the template's own design before the shopper
 * touches anything, and their edits replace parts of it. Ours has to do the
 * same for a harder reason than appearance: the commit writes the shopper's
 * ANSWER into the zone element, so a zone whose answer is empty prints empty,
 * and the greeting, the frame and the sample photograph the author placed would
 * never reach the press. Seeding the answer from the authored composition is
 * what makes the template model work at all.
 */
export function startingDesign(
  product: ProductDefinition,
  optionId: string,
): DesignAreaAnswer | undefined {
  const value = designZoneOf(product, optionId)?.zone.value;
  if (!isDesignAreaAnswer(value) || isEmptyDesign(value)) return undefined;
  // A fresh copy per product page: the definition object is shared with the
  // preview and with the renderer, and the shopper is about to edit this.
  return structuredClone(value);
}

/**
 * The template fields a zone offers on the product card, in paint order.
 *
 * Only layers the author ticked "Dozvoli izmenu na kartici proizvoda" for.
 * Layers that share a `key` are ONE field — Zazzle's "URL parameter name" links
 * duplicates so a name printed on both sides is typed once — and the first of
 * them is the one the card shows and writes through.
 */
function cardTemplateFields(answer: DesignAreaAnswer | null): DesignLayer[] {
  if (!answer) return [];
  const fields: DesignLayer[] = [];
  const seen = new Set<string>();
  for (const layer of answer.layers) {
    const template = layer.template;
    if (!template?.editOnCard || !template.label) continue;
    const identity = template.key ? `key:${template.key}` : `id:${layer.id}`;
    if (seen.has(identity)) continue;
    seen.add(identity);
    fields.push(layer);
  }
  return fields;
}

/** Whether a template field still holds what the author put there. */
function isUnreplaced(layer: DesignLayer): boolean {
  const original = layer.template?.original;
  if (!layer.template?.mustReplace || original === undefined) return false;
  if (layer.kind === 'text') return layer.text === original;
  if (layer.kind === 'image' || layer.kind === 'artwork') return layer.assetId === original;
  return false;
}

/** The print renderer, if this page has loaded it yet. */
function renderBundle(): Window['PersonalizyRender'] | undefined {
  return typeof window === 'undefined' ? undefined : window.PersonalizyRender;
}

/**
 * Calls back once the print renderer arrives, or never.
 *
 * The designer opens on whatever is loaded at that moment: usually the Skia
 * bundle is already in (the widget starts fetching it as soon as the form is
 * usable), but on a slow connection — or on a device the widget deliberately
 * skipped the upgrade for — it is not, and the designer draws DOM
 * approximations until it is. Polling rather than a promise because the bundle
 * registers itself on `window` from its own script tag; there is no event.
 */
function whenRendererReady(listener: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.setInterval !== 'function') return () => {};
  if (renderBundle()?.renderRaw) return () => {};
  let left = RENDERER_POLLS;
  const timer = window.setInterval(() => {
    left -= 1;
    const ready = Boolean(renderBundle()?.renderRaw);
    if (!ready && left > 0) return;
    window.clearInterval(timer);
    if (!ready) return;
    try {
      listener();
    } catch (err) {
      console.warn('[personalizy] designer renderer listener failed', err);
    }
  }, RENDERER_POLL_MS);
  return () => window.clearInterval(timer);
}

/** Paints a raw RGBA render into a canvas; a canvas-less engine simply skips. */
function blitPixels(
  canvas: HTMLCanvasElement,
  result: { pixels: Uint8Array; width: number; height: number },
): boolean {
  try {
    canvas.width = result.width;
    canvas.height = result.height;
    const context = canvas.getContext?.('2d');
    if (!context) return false;
    // Copied out: the renderer's view is backed by WASM memory, which ImageData
    // will not accept directly.
    const bytes = new Uint8ClampedArray(result.pixels.byteLength);
    bytes.set(result.pixels);
    context.putImageData(new ImageData(bytes, result.width, result.height), 0, 0);
    return true;
  } catch {
    return false;
  }
}

/** One geocoder hit, as a suggestion row shows it. */
export interface MapPlace {
  lat: number;
  lng: number;
  /** The place itself: "Kalemegdan". Shown in bold. */
  name: string;
  /** The rest of its address: "Beograd, Srbija". Shown small under the name. */
  detail: string;
  /** The whole thing — what lands in the field, the cart and the order. */
  label: string;
}

/** Splits a full place name into the bold first line and the small rest. */
function toPlace(lat: number, lng: number, name: string, full: string): MapPlace {
  const detail = full.startsWith(`${name}, `) ? full.slice(name.length + 2) : full === name ? '' : full;
  return { lat, lng, name: name || full, detail, label: full || name };
}

/**
 * Mapbox's geocoder, on the shop's own public token.
 *
 * `autocomplete=true` is what makes it answer half-typed words, which is the
 * whole point of suggesting while the shopper types.
 */
async function mapboxPlaces(query: string, token: string): Promise<MapPlace[]> {
  const response = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json` +
      `?access_token=${encodeURIComponent(token)}&autocomplete=true&limit=${MAP_RESULT_COUNT}&language=sr,en`,
  );
  const body = (await response.json()) as {
    features?: Array<{ center: [number, number]; text?: string; place_name?: string }>;
  };
  return (body.features ?? []).map((feature) =>
    toPlace(
      feature.center[1],
      feature.center[0],
      feature.text ?? feature.place_name ?? '',
      feature.place_name ?? feature.text ?? '',
    ),
  );
}

/**
 * The no-token fallback.
 *
 * OSM has already blocked this project's tile use and Nominatim is the same
 * volunteer infrastructure, so a shop selling map products must have a Mapbox
 * token; this only keeps a token-less shop's field from being dead.
 */
async function nominatimPlaces(query: string): Promise<MapPlace[]> {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&limit=${MAP_RESULT_COUNT}&q=${encodeURIComponent(query)}`,
    { headers: { accept: 'application/json' } },
  );
  const results = (await response.json()) as Array<{ lat: string; lon: string; display_name: string }>;
  return results.map((hit) => {
    const full = hit.display_name;
    return toPlace(Number(hit.lat), Number(hit.lon), full.split(',')[0] ?? full, full);
  });
}

/** How a place the shopper never named reads in the field and on the order. */
function coordinateLabel(lat: number, lng: number): string {
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}

/** How long after the last keystroke the search runs, ms. */
const SONG_DEBOUNCE_MS = 300;
/** Below this many characters every query matches everything; don't ask. */
const SONG_MIN_QUERY = 2;
/** Rows fetched per search — five fit the list, the rest are one scroll away. */
const SONG_RESULT_COUNT = 10;
/** Ghost rows shown while Spotify answers. */
const SONG_SKELETON_ROWS = 4;

/**
 * How a picked song reads in the field, in the cart and on the order:
 * "Perfect, Ed Sheeran". One string, written once, so the field the shopper
 * approves and the line on their order cannot drift apart.
 */
export function songLabel(name: string, artists: string): string {
  return [name, artists].filter(Boolean).join(', ');
}

/**
 * The words a picked track offers the lines that print it.
 *
 * `artists` reaches us as a list from the search and as a plain string from an
 * answer saved before that, and an older answer may carry only `artist`; the
 * card has to read the same either way. Anything that is not a Spotify pick
 * offers nothing.
 */
export function trackWords(value: unknown): { title: string; artists: string } | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const track = value as { uri?: unknown; name?: unknown; artists?: unknown; artist?: unknown };
  if (typeof track.uri !== 'string' || !track.uri.startsWith('spotify:')) return null;
  return {
    title: typeof track.name === 'string' ? track.name : '',
    artists: Array.isArray(track.artists)
      ? track.artists.map(String).join(', ')
      : typeof track.artists === 'string'
        ? track.artists
        : typeof track.artist === 'string'
          ? track.artist
          : '',
  };
}

/**
 * Empties an element and refills it.
 *
 * `replaceChildren` only arrived in Chrome 86, and the storefront still has to
 * work in Samsung Internet 12 (Chromium 79) and the older Android WebViews a
 * lot of shoppers browse in — where the call is simply not a function and the
 * whole control dies with it.
 */
function replaceKids(el: HTMLElement, ...nodes: Node[]): void {
  if (typeof el.replaceChildren === 'function') {
    el.replaceChildren(...nodes);
    return;
  }
  while (el.firstChild) el.removeChild(el.firstChild);
  for (const node of nodes) el.appendChild(node);
}

/** Text-ish controls whose focus brings up the on-screen keyboard. */
export function isTextEntry(el: Element | null): boolean {
  const tag = (el?.tagName ?? '').toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  if (tag !== 'input') return false;
  const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
  return !NON_TEXT_INPUTS.has(type);
}

/**
 * A single-line text field: Enter in one of these means "done with this
 * question", never "submit the shop's add-to-cart form".
 *
 * Exported because the wizard needs the same judgement to turn Enter into
 * "Dalje", and two copies of the rule would drift.
 */
export function isSingleLineInput(el: Element | null): boolean {
  const tag = (el?.tagName ?? '').toLowerCase();
  if (tag !== 'input') return false;
  const input = el as HTMLInputElement;
  if (input.readOnly || input.disabled) return false;
  const type = (input.type || 'text').toLowerCase();
  return !NON_TEXT_INPUTS.has(type);
}

/** Input types that are buttons, pickers or toggles rather than typing. */
const NON_TEXT_INPUTS = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

export class OptionForm {
  readonly element: HTMLElement;
  private readonly values: Values = {};
  private readonly rows = new Map<string, HTMLElement>();
  /** Pushes a restored value into a control's DOM, per option. */
  private readonly setters = new Map<string, (value: unknown) => void>();
  /**
   * The "retype the title / the artist" questions absorbed into each song card,
   * keyed by the picker they belong to.
   *
   * They are not asked again further down the form: the words that will print
   * are edited inside the card, filled in from the pick, so the shopper reads
   * what the plaque says and changes it in place. Their own rows are hidden
   * (see `hiddenFields`) and never become a wizard step of their own.
   */
  private readonly songLines = new Map<
    string,
    Array<{ option: OptionDef; role: 'title' | 'artist' }>
  >();
  /**
   * Every question a song card absorbed, to the picker that asks it, so its own
   * row builds no second control and a missing answer points at the card.
   */
  private readonly absorbedLines = new Map<string, string>();
  /**
   * Controls that have to react to ANOTHER question's answer.
   *
   * The map dialog is the one that needs it: the style swatch bound to the same
   * map layer repaints the map the shopper is looking at, not only the preview
   * behind the dialog.
   */
  private readonly watchers = new Map<string, Array<(value: unknown) => void>>();
  /**
   * The Premium length price each sound question adds right now: the sounds
   * run past the included length, so the arrangement costs more.
   */
  private readonly soundFees = new Map<string, { amount: number; label: string }>();
  /**
   * Everything on the form that can be heard — the sound step's players,
   * cutters and recorder, a song card's Spotify player — each with the way to
   * silence it. See `stopMedia`.
   */
  private readonly mediaStops = new Set<() => void>();
  /** Photo-upload controls that can show what is happening to their picture. */
  private readonly photoCards = new Map<string, (state: PhotoState) => boolean>();
  /**
   * Each photo slot's own upload routine, so a 30-file selection made on one
   * slot can be fanned out to the other 29 — every card shows its own picture,
   * its own progress and its own failure.
   */
  private readonly mediaImporters = new Map<string, (file: File) => Promise<void>>();
  private readonly photoUploaders = new Map<string, (file: File, resumed?: boolean) => Promise<void>>();
  private readonly multiUploadResumers = new Map<string, (pictures: PendingPicture[]) => Promise<void>>();
  private readonly activePhotoUploads = new Set<string>();
  /** Each photo collage's step, its intake and its host (see `buildCollage`). */
  private readonly collages = new Map<string, { host: CollageHost; intake: CollageIntake; step: CollageStep }>();
  /**
   * Bumped whenever an answer or the variation changes: a collage's active
   * slots (the ones visible after resolve) are worked out once per version.
   */
  private answerVersion = 0;
  /**
   * Where a freshly uploaded asset's pixels are, for the controls that show
   * several at once. `resolveAssetUrl` only knows what the definition shipped;
   * a picture uploaded a second ago is not in it yet.
   */
  private readonly assetUrls = new Map<string, string>();
  /** Each design-area question's zone config, found once through its binding. */
  private readonly designConfigs = new Map<string, DesignAreaConfig>();
  /** The zone's own layers per design question — the ones nobody is billed for. */
  private readonly designAuthoredIds = new Map<string, string[]>();
  private readonly designOpeners = new Map<string, () => void>();
  /** Font faces the designer and the font choices have asked the browser for, one load per face. */
  private readonly fontLoads = new Map<string, Promise<void>>();
  /** Redraws each font question's samples in the words its layer now prints. */
  private readonly fontSampleRepaints: Array<() => void> = [];

  /**
   * The tooltip a choice shows, by the shop's tooltip type: none at all,
   * the choice's own tooltip text when it has one ("custom"), or the value
   * itself (the default).
   */
  private tooltipFor(value: { label: string; meta?: Record<string, unknown> }): string {
    const type = this.options.product.theme?.tooltipType;
    if (type === 'none') return '';
    const custom = typeof value.meta?.tooltip === 'string' ? value.meta.tooltip : '';
    if (type === 'custom') return custom || value.label;
    return value.label;
  }
  /** Options currently hidden because their conditions do not match. */
  private readonly conditionHidden = new Set<string>();
  /** When set, only these options' rows show — the wizard's current step. */
  private stepFilter: Set<string> | null = null;
  /** The Woo variation currently selected, for variation-conditioned options. */
  private variation: VariationContext = { id: null, attributes: {} };

  constructor(private readonly options: FormOptions) {
    this.element = document.createElement('div');
    this.element.className = 'pz-form';

    // Which retype lines belong to which song card, before a single row exists:
    // a line absorbed by a card must never build its own row, and the row order
    // says nothing about which picker owns it.
    this.planSongLines();

    for (const option of options.product.options) {
      const row = this.buildRow(option);
      this.rows.set(option.id, row);
      this.element.appendChild(row);
    }

    // Preselected choices are applied after every row exists, so the setters can
    // paint them, and BEFORE conditions run — a field gated on "5 slika" has to
    // see that "5 slika" is already the answer.
    const initial: Values = {};
    for (const option of options.product.options) {
      const value = this.initialValue(option);
      if (value !== undefined) initial[option.id] = value;
    }
    if (Object.keys(initial).length > 0) {
      for (const [id, value] of Object.entries(initial)) {
        this.values[id] = value;
        this.setters.get(id)?.(value);
      }
    }
    this.repaintFontSamples();

    this.applyConditions();
    // Each collage notes which of its slots are showing, so a later layout
    // switch can be told apart from the first look.
    this.syncCollages();

    // A field focused on a phone is routinely hidden behind the on-screen
    // keyboard: the browser scrolls the LAYOUT viewport, which inside a wizard
    // sheet or a sticky column moves nothing the shopper can see. Bringing the
    // field into the middle of its own scroller is the only reliable fix, and it
    // is pointless (and jarring) with a mouse, so it is gated on a coarse pointer.
    this.element.addEventListener?.('focusin', (event: Event) => {
      if (!pointerIsCoarse()) return;
      const target = event.target as HTMLElement | null;
      if (!target || !isTextEntry(target)) return;
      revealFocused(target);
    });
  }

  /**
   * Pairs every song picker with the retype lines that belong to it.
   *
   * The generator emits one optional text question per role line of a player
   * ("Naziv pesme", "Izvođač"), each bound to the layer that carries the role.
   * A line belongs to the picker whose scan-code layer sits in the same player
   * folder — two players on one design stay independent, exactly as the preview
   * and the server resolve them. A definition with no scene to read (a test
   * harness, a broken binding) falls back to option order: the generator writes
   * the lines directly after their picker.
   */
  private planSongLines(): void {
    const product = this.options.product;
    const options = product.options ?? [];
    const pickers = options.filter((o) => o.type === 'spotify_track');
    if (pickers.length === 0) return;

    // The print side is the one the plaque is made from; a preview-only side is
    // a photograph of it and carries no player of its own.
    const sides = product.sides ?? [];
    const side = sides.find((s) => s.target !== 'preview') ?? sides[0];
    const byId = new Map((side?.elements ?? []).map((el) => [el.id, el] as const));
    const elementOf = (optionId: string, property: string) => {
      const binding = (product.bindings ?? []).find(
        (b) => b.optionId === optionId && b.property === property,
      );
      return binding ? byId.get(binding.elementId) : undefined;
    };
    // The generator marks a retype line `spotifyOverride`. A line an option set
    // was built or re-saved without that mark is still one when the layer it
    // types into is a player's title or artist: the preview and the server fill
    // that layer from the pick either way, so asking for it as a blank field of
    // its own makes the shopper retype words the plaque already shows.
    const roleOf = (option: OptionDef): 'title' | 'artist' | null => {
      if (option.type !== 'text') return null;
      const layer = elementOf(option.id, 'text');
      const role = option.config?.spotifyOverride ?? (layer?.type === 'text' ? layer.spotifyRole : undefined);
      return role === 'title' || role === 'artist' ? role : null;
    };
    const overrides = options.filter((o) => roleOf(o) !== null);
    if (overrides.length === 0) return;

    const taken = new Set<string>();
    for (const picker of pickers) {
      const anchor = elementOf(picker.id, 'spotifyUri');
      let mine: OptionDef[] = [];
      if (side && anchor) {
        const scope = spotifyPlayerScope(side, anchor);
        mine = overrides.filter((o) => {
          if (taken.has(o.id)) return false;
          const el = elementOf(o.id, 'text');
          return Boolean(el && inSpotifyScope(scope, el));
        });
      }
      if (mine.length === 0) {
        for (let i = options.indexOf(picker) + 1; i < options.length; i++) {
          const next = options[i]!;
          if (roleOf(next) === null || taken.has(next.id)) break;
          mine.push(next);
        }
      }
      if (mine.length === 0) continue;
      const lines = mine.map((o) => ({ option: o, role: roleOf(o)! }));
      this.songLines.set(picker.id, lines);
      for (const line of lines) {
        taken.add(line.option.id);
        this.absorbedLines.set(line.option.id, picker.id);
        this.hiddenFields.add(line.option.id);
      }
    }
  }

  /**
   * Whether this question is a Spotify song card's retype line ("Naziv pesme",
   * "Izvođač"): its words are the Spotify song's as printed, not the name of
   * whatever song the sound step plays.
   */
  isSongLine(optionId: string): boolean {
    return this.absorbedLines.has(optionId);
  }

  /** The shopper's answers, excluding fields currently hidden by a condition. */
  get current(): Values {
    const visible: Values = {};
    for (const [id, value] of Object.entries(this.values)) {
      if (this.conditionHidden.has(id)) continue;
      visible[id] = value;
    }
    return visible;
  }

  /** Required fields that are still empty, by label. */
  missingRequired(): string[] {
    const labels: string[] = [];
    for (const o of this.options.product.options) {
      if (!this.needsAnswer(o) || this.conditionHidden.has(o.id) || this.locationHidden.has(o.id)) continue;
      if (this.isAnswered(o.id)) continue;
      // A song card's line is filled by the pick, so before one it is the song
      // that is missing — "Naziv pesme" names a field the shopper cannot see yet.
      const picker = this.absorbedLines.get(o.id);
      const label =
        picker && !this.isAnswered(picker)
          ? (this.options.product.options.find((p) => p.id === picker)?.label ?? o.label)
          : o.label;
      if (!labels.includes(label)) labels.push(label);
    }
    return labels;
  }

  /**
   * The first question the shopper still has to answer, as its row.
   *
   * "First" is DOM order — what they read down the page — and only rows they
   * can actually reach count: a row hidden by a condition, by the wizard's step
   * filter, or folded into another section is not where an error belongs.
   * Returns null when nothing is missing.
   */
  firstMissingRow(): HTMLElement | null {
    for (const option of this.options.product.options) {
      if (!this.needsAnswer(option) || this.isAnswered(option.id)) continue;
      // A song card's line is asked inside its picker's row.
      const id = this.absorbedLines.get(option.id) ?? option.id;
      const row = this.rows.get(id);
      if (!row || !this.rowIsReachable(id, row)) continue;
      return row;
    }
    return null;
  }

  /** The option ids of every unanswered required question the shopper can reach. */
  missingRowIds(): string[] {
    const ids: string[] = [];
    for (const option of this.options.product.options) {
      if (!this.needsAnswer(option) || this.isAnswered(option.id)) continue;
      const id = this.absorbedLines.get(option.id) ?? option.id;
      const row = this.rows.get(id);
      if (row && this.rowIsReachable(id, row) && !ids.includes(id)) ids.push(id);
    }
    return ids;
  }

  /**
   * Marks exactly these rows as missing and unmarks every other one.
   *
   * The mark is the field-level half of "you forgot something": the message at
   * the top or in the wizard's footer says what, this says where. It clears
   * itself as soon as the question is answered (see `set`), so a shopper who
   * fixes one field does not keep looking at a red box.
   */
  markMissing(ids: string[]): void {
    const wanted = new Set(ids);
    for (const [id, row] of this.rows) row.classList.toggle('is-missing', wanted.has(id));
  }

  /** Puts the shopper in front of one row: scrolled into view, cursor in the field. */
  focusRow(row: HTMLElement): void {
    // Instant, not smooth: a smooth scroll is dropped by some engines (background
    // frames, older WebViews, reduced motion), and the shopper must land on the
    // field every time.
    try {
      row.scrollIntoView?.({ block: 'center' });
    } catch {
      row.scrollIntoView?.();
    }
    const control = row.querySelector?.(
      'input, select, textarea, button',
    ) as HTMLElement | null;
    if (!control) return;
    try {
      control.focus({ preventScroll: true });
    } catch {
      control.focus?.();
    }
    // A text field on a phone has the keyboard coming up over it; the row's own
    // scrollIntoView happened before that, so it has to be redone afterwards.
    if (isSingleLineInput(control) || isTextEntry(control)) revealFocused(control);
  }

  /** A row the shopper can see and reach right now. */
  private rowIsReachable(optionId: string, row: HTMLElement): boolean {
    if (this.hiddenFields.has(optionId) || this.conditionHidden.has(optionId)) return false;
    if (this.stepFilter !== null && !this.stepFilter.has(optionId)) return false;
    if (row.hidden) return false;
    // Section navigation shows one section at a time; a required field two
    // sections further on is not what to point at.
    let node: HTMLElement | null = row.parentElement ?? null;
    while (node && node !== this.element) {
      if (node.hidden) return false;
      node = node.parentElement ?? null;
    }
    return true;
  }

  /**
   * One question's row, for whoever needs to point at it — the shopper guide
   * highlights the field it is explaining, and reads `hidden` off the row to
   * know whether that question is on screen at all.
   */
  rowFor(optionId: string): HTMLElement | null {
    return this.rows.get(optionId) ?? null;
  }

  /** Whether one option has a usable answer. */
  isAnswered(optionId: string): boolean {
    // A photo collage is answered once every active slot holds a photo (or
    // something does, where empty slots are allowed) and nothing is still on
    // its way: "Dalje" and the cart wait for the uploads.
    const collage = this.collages.get(optionId);
    if (collage) return collage.step.complete();
    const value = this.values[optionId];
    // A design with no layers is an object, and an object is not an answer: a
    // shopper who opened the designer and closed it again has not filled in the
    // print zone, and a required one must still block the cart.
    if (isDesignAreaAnswer(value)) return !isEmptyDesign(value);
    return value !== undefined && value !== null && value !== '';
  }

  /**
   * A photo question whose photos are still being sent. The shopper may go on
   * to the next step meanwhile — the texts, the sound — and the cart waits
   * for them (it asks `isAnswered`, which stays false until they are in).
   */
  inProgress(optionId: string): boolean {
    return this.collages.get(optionId)?.step.sending() ?? false;
  }

  /**
   * Options the shopper has answered THEMSELVES — typed, picked, uploaded, or
   * restored from their cart or draft. A choice the template preselected
   * (`meta.selected`, a ticked checkbox, a default colour) is an answer for
   * validation and rendering but not a personalization: a product page that
   * greets a first-time visitor with "Tvoja personalizacija: Krem" and an
   * "Izmeni" button reads as already filled in when nothing has been done.
   */
  private readonly touched = new Set<string>();

  /** Whether one option holds an answer the shopper made or restored. */
  hasShopperAnswer(optionId: string): boolean {
    // Photos picked for a collage are the shopper's doing even before every
    // slot is filled: the page reads "Nastavi", not "Personalizuj".
    const collage = this.collages.get(optionId);
    if (collage) return this.touched.has(optionId) && collage.step.started();
    return this.touched.has(optionId) && this.isAnswered(optionId);
  }

  /**
   * Must this question be answered before "Dalje" and the cart? A required
   * one — and a photo collage the shopper started even where it is optional:
   * the server refuses a collage with photos still missing (422
   * `collage-incomplete`), so a half-filled one has to be finished or emptied.
   */
  needsAnswer(option: Pick<OptionDef, 'id' | 'required'>): boolean {
    if (option.required) return true;
    const collage = this.collages.get(option.id);
    return Boolean(collage && collage.step.started());
  }

  /**
   * What is holding this question up, in its own words, when it has any: a
   * collage's "Sačekaj da se sve slike pošalju." or "Nedostaju još 3 slike.
   * Dodaj slike ili dodirni „Ponovi slike“." Undefined: the generic message.
   */
  missingMessage(optionId: string): string | undefined {
    return this.collages.get(optionId)?.step.missingMessage();
  }

  /** Shared media intake; only a visible question with an existing uploader is eligible. */
  hubMediaTargets(): {id:string;label:string;kind:'image'|'audio'}[] {
    return this.visibleOptions().flatMap<{id:string;label:string;kind:'image'|'audio'}>(o => this.photoUploaders.has(o.id) ? [{id:o.id,label:o.label,kind:'image' as const}] : this.mediaImporters.has(o.id) ? [{id:o.id,label:o.label,kind:'audio' as const}] : []);
  }
  async importHubMedia(optionId:string,file:File):Promise<void> {
    const target=this.hubMediaTargets().find(t=>t.id===optionId);
    if(!target)throw new Error('Polje nije dostupno u trenutnom proizvodu.');
    if(target.kind==='audio'){await this.mediaImporters.get(optionId)!(file);return;}
    if(!file.type.startsWith('image/'))throw new Error('Za ovo polje izaberite sliku.');
    if(this.current[optionId])throw new Error('Polje već ima sliku. Uklonite je u obrascu pre zamene.');
    await this.photoUploaders.get(optionId)!(file);
    if(!this.current[optionId])throw new Error('Slika nije uvezena. Proverite poruku uz polje i ponovite.');
  }

  /** The options a shopper can currently see, in order — the wizard's steps. */
  visibleOptions(): OptionDef[] {
    // `hiddenFields` counts as much as a condition here: a machine-only field,
    // or a retype line asked inside a song card, is not a question the shopper
    // can answer — as a wizard step it is a screen nobody can get past.
    return this.options.product.options.filter(
      (o) => !this.conditionHidden.has(o.id) && !this.hiddenFields.has(o.id) && !this.locationHidden.has(o.id),
    );
  }

  // ── Print locations ("Na grudima" / "Džep") ──────────────────────────────
  // Design questions whose zones share `config.printLocation.group` are
  // alternatives: one is designed, the others stay explicitly empty (so they
  // print nothing, not their authored starting design) and out of sight.

  /** The alternatives not currently chosen. */
  private readonly locationHidden = new Set<string>();
  /** A location picked before anything was designed in it, per group. */
  private readonly locationChoice = new Map<string, string>();

  /** Design questions grouped by print location, in option order, among those conditions show. */
  private printLocationGroups(): Map<string, string[]> {
    const groups = new Map<string, string[]>();
    for (const option of this.options.product.options) {
      if (option.type !== DESIGN_AREA_OPTION_TYPE || this.conditionHidden.has(option.id)) continue;
      const group = this.designConfigFor(option.id).printLocation?.group;
      if (!group) continue;
      groups.set(group, [...(groups.get(group) ?? []), option.id]);
    }
    return groups;
  }

  private applyPrintLocations(): void {
    this.locationHidden.clear();
    const empty = (id: string): boolean => {
      const value = this.values[id];
      return !isDesignAreaAnswer(value) || isEmptyDesign(value);
    };
    for (const [group, ids] of this.printLocationGroups()) {
      if (ids.length < 2) continue;
      const active = activePrintLocation(ids, empty, this.locationChoice.get(group));
      for (const id of ids) {
        if (id === active) continue;
        this.locationHidden.add(id);
        const value = this.values[id];
        if (isDesignAreaAnswer(value) && isEmptyDesign(value)) continue;
        // Only one location prints. An alternative still holding its authored
        // starting design (or nothing at all, which the server would fill with
        // that design) is set to an explicit empty answer.
        this.values[id] = { v: 1, layers: [] };
        this.setters.get(id)?.(this.values[id]);
      }
    }
  }

  /** The other locations of a design question's group, for the designer's picker. */
  private printLocationsFor(optionId: string): { group: string; list: { id: string; name: string; current: boolean }[] } | undefined {
    const group = this.designConfigFor(optionId).printLocation?.group;
    if (!group) return undefined;
    const ids = this.printLocationGroups().get(group) ?? [];
    if (ids.length < 2) return undefined;
    return {
      group,
      list: ids.map((id) => ({
        id,
        name: this.designConfigFor(id).printLocation?.name ?? id,
        current: id === optionId,
      })),
    };
  }

  /**
   * Moves the design from one location to another: scaled to fit the new
   * zone (see `movePrintLocationDesign`), the old zone emptied, the choice
   * remembered even when there was nothing to move.
   */
  private movePrintLocation(fromId: string, toId: string, value: DesignAreaAnswer): void {
    const group = this.designConfigFor(fromId).printLocation?.group;
    if (group) this.locationChoice.set(group, toId);
    let moved: DesignAreaAnswer = { v: 1, layers: [] };
    if (!isEmptyDesign(value)) {
      try {
        moved = movePrintLocationDesign(this.designConfigFor(fromId), this.designConfigFor(toId), value).answer;
      } catch {
        moved = { v: 1, layers: [] };
      }
    }
    this.values[fromId] = { v: 1, layers: [] };
    this.setters.get(fromId)?.(this.values[fromId]);
    this.set(toId, moved);
  }

  /** Shows one wizard step's options (related fields share a step), or everything when null. */
  showOnly(optionIds: string[] | null): void {
    this.stepFilter = optionIds === null ? null : new Set(optionIds);
    this.applyVisibility();
  }

  /**
   * Silences every player on the form. The popup closed, or the shopper moved
   * to another step: a song nobody can see any more — with no button left on
   * screen to stop it — must not keep playing.
   */
  stopMedia(): void {
    for (const stop of this.mediaStops) {
      try {
        stop();
      } catch {
        // One player that fails to stop must not leave the others playing.
      }
    }
  }

  /**
   * Sets an answer from outside the form — the on-canvas photo editor writes the
   * shopper's framing back through here so form state stays the single source.
   */
  /**
   * Reflects cut-out progress on the photo card for this question. False when
   * the card turned it down: it was about a photo that has since been removed
   * or replaced, whose cut-out finished late.
   */
  setPhotoState(optionId: string, state: PhotoState): boolean {
    return this.photoCards.get(optionId)?.(state) ?? true;
  }

  setValue(optionId: string, value: unknown): void {
    this.set(optionId, value);
  }

  /**
   * The shopper's framing of a photo, merged into the answer rather than
   * replacing it.
   *
   * Several controls write one photo answer: the upload card, the filter
   * chips, and framing the picture on the preview (or in its framer).
   * A plain overwrite means whichever moved last silently discards the others —
   * drag your photo and the filter you chose disappears. See `mergePhotoAnswer`.
   */
  setPhotoFraming(optionId: string, framing: unknown): void {
    this.set(optionId, mergePhotoAnswer(this.values[optionId], framing));
  }

  /**
   * Applies framing only when this photo has none yet.
   *
   * The automatic face placement calls this: it must land on a fresh upload but
   * never override a framing the shopper already chose by dragging or by
   * picking a face — their hand always beats the detector.
   */
  setPhotoFramingIfUnset(optionId: string, framing: { assetId: string; crop: unknown }): void {
    const current = this.values[optionId];
    const answer =
      typeof current === 'string'
        ? { assetId: current }
        : (current as { assetId?: string; crop?: unknown } | undefined);
    if (!answer?.assetId || answer.assetId !== framing.assetId) return; // a different photo by now
    if (answer.crop) return; // already framed
    this.setPhotoFraming(optionId, framing);
  }

  /**
   * Restores a committed personalization's answers — state AND what the shopper
   * sees in each control, so "Izmeni" from the cart continues where they left
   * off instead of showing a blank form over kept values.
   */
  applyInitial(values: Values): void {
    for (const option of this.options.product.options) {
      let value = withoutKeepOriginal(values[option.id]);
      // A collage comes back whole — crops, hand framings, faces, sizes, the
      // seed and the spares — cleaned by the one normaliser the server uses.
      const collage = this.collages.get(option.id);
      if (collage) {
        const answer = collage.host.normalize(value);
        value = answer && answer.photos.length > 0 ? answer : undefined;
      }
      if (value === undefined || value === null || value === '') continue;
      this.values[option.id] = value;
      // A restored answer is the shopper's own, wherever it came from.
      this.touched.add(option.id);
      this.setters.get(option.id)?.(value);
    }
    // A restored name reaches the font choices too: setters do not wake watchers.
    this.repaintFontSamples();
    this.applyConditions();
    this.syncCollages();
    this.options.onChange(this.current);
  }

  /**
   * The shopper switched variation ("Sa zvukom" ↔ "Bez zvuka"): options
   * conditioned on the variation appear or disappear immediately.
   */
  setVariation(variation: VariationContext): void {
    this.variation = variation;
    this.applyVariationAnswers();
    this.applyConditions();
    this.syncCollages();
    this.options.onChange(this.current);
    this.openDesign?.productChanged();
  }

  /**
   * Questions the variation answers (`config.fromAttribute`, e.g. "Boja
   * kutije"): their answer is the picked variation's value, set here and never
   * by the shopper — the server sets the same one from the bought variation.
   * No value for the attribute (nothing picked yet) leaves them unanswered, and
   * their layers draw as the template has them.
   */
  private applyVariationAnswers(): void {
    for (const option of this.options.product.options) {
      if (!fromAttributeOf(option)) continue;
      const answer = variationAnswer(option, this.variation.attributes);
      if (answer === undefined) delete this.values[option.id];
      else this.values[option.id] = answer;
      this.setters.get(option.id)?.(answer ?? '');
    }
  }

  /** The design question whose designer is open right now, if any. */
  private openDesign: { optionId: string; productChanged(): void; closeKeeping(): void } | undefined;

  /**
   * Closes an open designer, KEEPING what is in it (unlike Otkaži), and says
   * which question it was — so a product swap underneath (a variation with its
   * own template) can reopen the designer on the new form instead of leaving
   * a designer that writes into a form that is gone.
   */
  closeDesignerKeeping(): string | undefined {
    const open = this.openDesign;
    if (!open) return undefined;
    open.closeKeeping();
    return open.optionId;
  }

  /** Opens a design question's designer, when this form has that question. */
  openDesigner(optionId: string): boolean {
    const opener = this.designOpeners.get(optionId);
    if (!opener || !this.visibleOptions().some((option) => option.id === optionId)) return false;
    opener();
    return true;
  }

  /**
   * The designer's Options step: this form's product choices and the page's
   * variation selects, MOVED into the step rather than rebuilt — the same
   * controls with the same listeners and state, put back where they were when
   * the step closes.
   */
  private designProductOptions(zoneOptionId: string): DesignerProductOptions | undefined {
    const rows = (): HTMLElement[] => this.visibleOptions()
      .filter((option) => option.id !== zoneOptionId && PRODUCT_CHOICE_TYPES.has(option.type))
      .map((option) => this.rows.get(option.id))
      .filter((row): row is HTMLElement => Boolean(row) && !row!.hidden);
    const page = this.options.pageOptions;
    if (rows().length === 0 && !page?.available()) return undefined;
    return {
      title: DESIGN_TEXTS.optionsTab,
      mount: (host) => {
        const restore: Array<() => void> = [];
        if (page?.available()) {
          const pageHost = document.createElement('div');
          pageHost.className = 'pz-design-page-options';
          host.append(pageHost);
          restore.push(page.mount(pageHost));
        }
        for (const row of rows()) {
          const mark = document.createComment('pz-option-row');
          row.parentNode?.insertBefore(mark, row);
          host.append(row);
          restore.push(() => {
            mark.parentNode?.insertBefore(row, mark);
            mark.remove();
          });
        }
        return () => {
          for (const undo of restore.reverse()) {
            try {
              undo();
            } catch {
              /* a control that cannot go back stays where it is, still working */
            }
          }
        };
      },
    };
  }

  get currentVariation(): VariationContext {
    return this.variation;
  }

  /** The running personalization surcharge, from the answers so far. */
  priceTotal(): number {
    return this.priceLines().reduce((sum, line) => sum + line.amount, 0);
  }

  /**
   * The surcharge line by line, as the price box lists it: "Boja rama: Crna
   * +100", "Premium dužina (do 8 min) +400". The same sums the server bills;
   * zero lines are left out.
   */
  priceLines(): Array<{ label: string; amount: number }> {
    const lines: Array<{ label: string; amount: number }> = [];
    const add = (label: string, amount: number): void => {
      if (!Number.isFinite(amount) || Math.abs(amount) < 0.005) return;
      const same = lines.find((line) => line.label === label);
      if (same) same.amount += amount;
      else lines.push({ label, amount });
    };
    for (const option of this.visibleOptions()) {
      if (!this.isAnswered(option.id)) continue;
      let total = 0;
      const label = option.label?.trim() || 'Personalizacija';
      total += option.priceDelta;
      const answer = this.values[option.id];
      // A design area is priced by what the shopper put in it — a base fee plus
      // so much per text, photo, ornament and shape, capped. `designAreaPrice`
      // is the same function the server bills with, so the running total and the
      // order cannot disagree. Nothing else about a design is chargeable.
      if (option.type === DESIGN_AREA_OPTION_TYPE) {
        total += designAreaPrice(
          this.designConfigFor(option.id),
          isDesignAreaAnswer(answer) ? answer : null,
          { authoredLayerIds: this.authoredLayersOf(option.id) },
        );
        add(label, total);
        continue;
      }
      // The question's own surcharge reads as the question.
      add(label, total);
      // A multi-select swatch answers with a list; every picked choice charges,
      // each named by its choice.
      const picked = (Array.isArray(answer) ? answer : [answer]).map(String);
      for (const chosen of option.values) {
        if (picked.includes(chosen.value)) add(`${label}: ${chosen.label || chosen.value}`, chosen.priceDelta);
      }
      // Engraving sold by the letter — mirrors the server's computePriceDelta,
      // which is what actually bills; this only keeps the running total honest.
      const perChar = Number(option.config?.pricePerChar ?? 0);
      if (perChar > 0 && typeof answer === 'string') {
        // By character as the shopper sees it: an emoji is one, not two to eight.
        const letters = countedLetters(answer);
        add(`${label} (${letters} slova)`, perChar * letters);
      }
      // Sounds longer than the included length: the Premium price, once.
      const fee = this.soundFees.get(option.id);
      if (fee) add(fee.label, fee.amount);
    }
    return lines;
  }

  /**
   * Where a dialog this form opens has to live.
   *
   * The shadow root the form is in, so the dialog inherits the widget's styles
   * and (as a `<dialog>`) still reaches the browser's top layer — no ancestor
   * transform or `overflow: hidden` in the shop's theme can clip it. A form
   * mounted in the light DOM falls back to the body, and a form not in a
   * document at all (tests, an unmounted build) keeps its dialogs inside itself
   * rather than throwing.
   */
  private layerHost(): ShadowRoot | HTMLElement {
    const root = this.element.getRootNode?.() as (Node & { host?: Element }) | undefined;
    if (root && (root as ShadowRoot).host) return root as ShadowRoot;
    // nodeType 9 is the document itself: a light-DOM mount, where the body is
    // the only place a dialog is safe from the theme's own wrappers.
    if (root && root.nodeType === 9 && document.body) return document.body;
    return this.element;
  }

  private set(optionId: string, value: unknown): void {
    this.values[optionId] = value;
    this.answerVersion++;
    this.touched.add(optionId);
    // Another answer (a shirt colour) can change the picture behind an open
    // designer; the designer's own writes cannot.
    if (this.openDesign && this.openDesign.optionId !== optionId) this.openDesign.productChanged();
    // Answering clears the "you forgot this" mark at once — the shopper should
    // not have to press Next again to see that they fixed it.
    if (this.isAnswered(optionId)) this.rows.get(optionId)?.classList.remove('is-missing');
    for (const watcher of this.watchers.get(optionId) ?? []) watcher(value);
    this.applyConditions();
    // A layout swatch ("15 slika" ↔ "24 slike") changes which collage slots
    // are active: the photos move with it (relayoutCollage).
    this.syncCollages(optionId);
    this.options.onChange(this.current);
  }

  /** Registers a control's interest in another question's answer. */
  private watch(optionId: string, react: (value: unknown) => void): void {
    const list = this.watchers.get(optionId) ?? [];
    list.push(react);
    this.watchers.set(optionId, list);
  }

  /**
   * Shows or hides fields according to their conditions.
   *
   * Evaluated here as well as on the server: the shopper should never be asked for
   * something irrelevant, and the server should never trust that they weren't.
   */
  private applyConditions(): void {
    this.answerVersion++;
    for (const option of this.options.product.options) {
      const groups = option.conditions ?? [];
      if (!Array.isArray(groups) || groups.length === 0) continue;
      const visible = groups.some((group) =>
        (group.rules ?? []).every((rule) => this.ruleHolds(rule)),
      );
      if (visible) this.conditionHidden.delete(option.id);
      else {
        this.conditionHidden.add(option.id);
        // A question no longer asked is no longer missing: ticked again, it
        // comes back without the red frame of the "Dalje" before.
        this.rows.get(option.id)?.classList.remove('is-missing');
      }
    }
    this.applyPrintLocations();
    this.applyVisibility();
  }

  private ruleHolds(rule: ConditionRule): boolean {
    let actual: unknown;
    if (rule.subject === 'attribute') {
      // Attribute keys arrive in several spellings; compare without prefixes.
      const wanted = String(rule.attribute ?? '')
        .replace(/^attribute_/, '')
        .replace(/^pa_/, '');
      const entry = Object.entries(this.variation.attributes).find(
        ([key]) => key.replace(/^attribute_/, '').replace(/^pa_/, '') === wanted,
      );
      actual = entry?.[1];
    } else if (rule.subject === 'variation') {
      actual = this.variation.id == null ? undefined : String(this.variation.id);
    } else {
      actual = rule.optionId ? this.values[rule.optionId] : undefined;
    }

    const text = actual === undefined || actual === null ? '' : String(actual);
    // A comma-separated value means "any of these". Mirrors api/src/resolve.ts
    // `holds` exactly — the server re-evaluates every rule at commit.
    const wanted = String(rule.value ?? '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    switch (rule.operator) {
      case '!=':
        return !wanted.includes(text);
      case 'empty':
        return actual === undefined || actual === null || actual === '';
      case '!empty':
        return actual !== undefined && actual !== null && actual !== '';
      case 'contains':
        return wanted.some((w) => text.toLowerCase().includes(w.toLowerCase()));
      case 'minLength':
        return text.length >= Number(wanted[0] ?? 0);
      case 'length':
        return text.length === Number(wanted[0] ?? 0);
      default:
        return wanted.includes(text);
    }
  }

  /** A row shows when its conditions match AND the wizard is not on another step. */
  private applyVisibility(): void {
    for (const [id, row] of this.rows) {
      row.hidden =
        this.hiddenFields.has(id) ||
        this.conditionHidden.has(id) ||
        this.locationHidden.has(id) ||
        (this.stepFilter !== null && !this.stepFilter.has(id));
    }
  }

  /**
   * Fields Customily marks `hide_visually`.
   *
   * These are machine-only: a field that exists to hold a value the design needs
   * but that the shopper is never meant to answer. They are built and kept in the
   * answers — removing them would break the bindings that read them — and simply
   * never shown. Showing them asks the shopper to fill in something meaningless,
   * and on a wizard it adds a step that cannot be completed.
   */
  private readonly hiddenFields = new Set<string>();

  /**
   * The answer a field starts with.
   *
   * Customily marks one choice `selected`, and 273 of the owner's fields rely on
   * it — a swatch that opens on nothing shows the shopper a design with a hole in
   * it and no indication that a choice was already made for them.
   */
  private initialValue(option: OptionDef): unknown {
    const config = option.config ?? {};
    const preselected = option.values.find((v) => v.meta?.selected === true && v.meta?.hidden !== true);
    if (preselected) return preselected.value;
    // Customily's "Selected" checkbox on a Checkbox option: the box starts
    // ticked, and a ticked box is already an answer — a bound function fires
    // before the shopper touches anything.
    if (option.type === 'checkbox') {
      return config.startSelected === true || config.checked === true ? true : undefined;
    }
    // A Color Picker's "Default Color" is its starting answer, not merely the
    // swatch's paint: the layer it drives has to be that colour on first draw.
    if (option.type === 'color') {
      const preset = normalizeHex(config.defaultColor ?? config.default);
      return preset ?? undefined;
    }
    // A design zone starts as the design its author composed — see
    // `startingDesign`. Restoring from the cart overrides this in
    // `applyInitial`, which runs after the constructor's own pass.
    if (option.type === DESIGN_AREA_OPTION_TYPE) {
      return startingDesign(this.options.product, option.id);
    }
    // Customily's "Initial Image": the placeholder is filled before the shopper
    // uploads anything, and their own picture simply replaces it.
    if (option.type === 'image_upload' || option.type === 'vector_upload') {
      const initial = config.initialImage ?? config.initialAssetId;
      if (typeof initial === 'string' && initial !== '') return initial;
    }
    // A typing field starts EMPTY. Customily's "default" on a text field is
    // what the empty box suggests, not an answer already given: a shopper who
    // never touched the field would otherwise buy a plaque reading "Ana i
    // Marko" because the template happened to be authored with that example in
    // it. Only "Initial Value" (or `?@ime=Ana` in the page's URL, see
    // `wireText`) puts words in the box; the default becomes its placeholder.
    if (option.type === 'text' || option.type === 'textarea') {
      return typeof config.initialValue === 'string' && config.initialValue !== ''
        ? config.initialValue
        : undefined;
    }
    const defaultValue = config.default ?? config.defaultValue;
    if (typeof defaultValue === 'string' && defaultValue !== '') return defaultValue;
    if (typeof config.initialValue === 'string' && config.initialValue !== '') {
      return config.initialValue;
    }
    return undefined;
  }

  /**
   * Customily's "sections": every divider starts one. With section accordions
   * each section folds under its heading (the first stays open); with
   * navigation only one section shows at a time, behind Back / Next buttons
   * that refuse to move on past an empty required field. Idempotent, and a
   * no-op for forms without dividers or for shops that set neither.
   */
  groupSections(): void {
    const theme = this.options.product.theme ?? {};
    const accordions = theme.sectionAccordions === 'true';
    const nav = theme.sectionNav === 'true';
    if ((!accordions && !nav) || this.element.querySelector('.pz-section')) return;
    const rows = [...this.element.children] as HTMLElement[];
    if (!rows.some((r) => r.classList.contains('pz-divider'))) return;

    type Section = { head: HTMLElement | null; body: HTMLElement; wrap: HTMLElement };
    const sections: Section[] = [];
    let current: Section | null = null;
    const start = (head: HTMLElement | null): Section => {
      const wrap = document.createElement('section');
      wrap.className = 'pz-section';
      const body = document.createElement('div');
      body.className = 'pz-section-body';
      if (head) {
        head.classList.add('pz-section-head');
        wrap.appendChild(head);
      }
      wrap.appendChild(body);
      this.element.appendChild(wrap);
      const section = { head, body, wrap };
      sections.push(section);
      return section;
    };
    for (const row of rows) {
      if (row.classList.contains('pz-divider')) {
        current = start(row);
        continue;
      }
      if (!current) current = start(null);
      current.body.appendChild(row);
    }

    if (accordions && !nav) {
      sections.forEach((section, i) => {
        section.wrap.classList.toggle('is-open', i === 0 || !section.head);
        section.head?.addEventListener('click', () => section.wrap.classList.toggle('is-open'));
      });
      return;
    }

    // Navigation: one section at a time.
    let index = 0;
    const show = (next: number): void => {
      index = Math.max(0, Math.min(sections.length - 1, next));
      sections.forEach((section, i) => {
        section.wrap.hidden = i !== index;
        section.wrap.classList.add('is-open');
      });
      sections[index]?.wrap.scrollIntoView?.({ block: 'nearest' });
    };
    sections.forEach((section, i) => {
      const bar = document.createElement('div');
      bar.className = 'pz-section-nav';
      const error = document.createElement('p');
      error.className = 'pz-section-error';
      error.hidden = true;
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'pz-btn-ghost';
      back.textContent = 'Nazad';
      back.disabled = i === 0;
      back.addEventListener('click', () => show(i - 1));
      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'pz-btn';
      next.textContent = i === sections.length - 1 ? 'Gotovo' : 'Dalje';
      next.addEventListener('click', () => {
        const missing = [...this.rows]
          .filter(([id, row]) => section.body.contains?.(row) && !this.conditionHidden.has(id))
          .map(([id]) => this.options.product.options.find((o) => o.id === id))
          .filter((o): o is OptionDef => Boolean(o && o.required && !this.isAnswered(o.id)));
        if (missing.length > 0) {
          // A collage says what it needs ("Dodaj još 7 slika"), not just its name.
          error.textContent =
            missing.map((o) => this.missingMessage(o.id)).find(Boolean) ??
            (theme.requiredMessage ?? 'Popuni obavezna polja: ') + missing.map((o) => o.label).join(', ');
          error.hidden = false;
          // The message names the fields; the marks and the scroll put the
          // shopper in front of the first one. A message alone at the bottom of
          // a long section is a dead end on a phone.
          this.markMissing(missing.map((o) => o.id));
          const first = this.rows.get(missing[0]!.id);
          if (first) this.focusRow(first);
          return;
        }
        this.markMissing([]);
        error.hidden = true;
        if (i < sections.length - 1) show(i + 1);
      });
      bar.append(back, next);
      section.body.append(error, bar);
    });
    show(0);
  }

  private buildRow(option: OptionDef): HTMLElement {
    // Machine-only: Customily's hidden fields, and a question the variation answers.
    if (option.config?.hidden === true || fromAttributeOf(option)) this.hiddenFields.add(option.id);
    const row = document.createElement('div');
    row.className = 'pz-row';
    // Which question this row asks, in the DOM itself: the shopper guide finds
    // the row a focus event came from by walking up to the nearest one of these,
    // and a shop's own CSS can reach a single field by type.
    if (row.dataset) {
      row.dataset.option = option.id;
      row.dataset.type = option.type;
    }
    // A class of the shop's own, for its custom CSS to find this one field.
    const cssClass = typeof option.config?.cssClass === 'string' ? option.config.cssClass.replace(/[^A-Za-z0-9_-]/g, '') : '';
    if (cssClass) row.classList.add(cssClass);

    // A retype line the song card took over: it is asked there, in place, and a
    // second control for the same answer would fight the first over the setter
    // a cart restore writes through.
    if (this.absorbedLines.has(option.id)) {
      row.hidden = true;
      return row;
    }

    // A divider is a heading with a line, not a question.
    if (option.type === 'divider') {
      row.className = 'pz-row pz-divider';
      const heading = document.createElement('div');
      heading.className = 'pz-divider-label';
      heading.textContent = option.label;
      row.appendChild(heading);
      if (option.helpText) {
        const help = document.createElement('p');
        help.className = 'pz-hint';
        help.textContent = option.helpText;
        row.appendChild(help);
      }
      return row;
    }

    const label = document.createElement('label');
    label.className = 'pz-label';
    label.textContent = option.label;
    if (option.required) {
      const mark = document.createElement('span');
      mark.className = 'pz-required';
      mark.textContent = ' *';
      label.appendChild(mark);
    }
    if (option.priceDelta > 0) {
      const price = document.createElement('span');
      price.className = 'pz-price';
      price.textContent = ` +${this.options.formatPrice(option.priceDelta)}`;
      label.appendChild(price);
    }
    row.appendChild(label);

    // Accordions (a shop setting): each question folds under its label, the
    // first one open, so a long form reads as a list of headings.
    if (this.options.product.theme?.accordions === 'true') {
      row.classList.add('pz-accordion');
      if (this.rows.size === 0) row.classList.add('is-open');
      label.addEventListener('click', (e) => {
        e.preventDefault();
        row.classList.toggle('is-open');
      });
    }

    row.appendChild(this.buildControl(option, row));

    // The song picker places its help under its own field, above the card of
    // editable lines — see buildSpotifySearch.
    if (option.helpText && option.type !== 'spotify_track') {
      const help = document.createElement('p');
      help.className = 'pz-hint';
      help.textContent = option.helpText;
      row.appendChild(help);
    }
    return row;
  }

  /**
   * Wires the settings Customily keeps on a text field: a length limit with a
   * visible counter, emoji handling, and whitespace trimming.
   *
   * Emoji are kept unless the field turns them off (`allowEmojis: false`,
   * `emojiAllowed`), and drawn from the bundled Noto set (`@pz/scene` emoji.ts)
   * — in the preview exactly as in the print — whatever the shopper's phone shows.
   *
   * Trimming applies to the stored answer only. Trimming as the shopper types
   * makes it impossible to type a space between two words.
   */
  private wireText(
    option: OptionDef,
    input: HTMLInputElement | HTMLTextAreaElement,
    row: HTMLElement,
  ): void {
    const config = option.config ?? {};
    const maxLength = typeof config.maxLength === 'number' ? config.maxLength : undefined;
    const maxLines = typeof config.maxLines === 'number' && config.maxLines > 0 ? config.maxLines : undefined;
    const maxPerLine = typeof config.maxPerLine === 'number' && config.maxPerLine > 0 ? config.maxPerLine : undefined;
    const addChains = config.addChains === true;
    const allowEmojis = emojiAllowed(config);
    const trimSpaces = config.trimSpaces === true;
    // Customily's "Restrict input": only letters, only numbers, or a pattern of
    // the merchant's own; and its capitalisation rule, applied as they type.
    const restrict = typeof config.restrict === 'string' ? config.restrict : '';
    const textCase = typeof config.textCase === 'string' ? config.textCase : '';
    const allowed: RegExp | null =
      restrict === 'letters'
        ? /[^\p{L}\p{M}\s'’.-]/gu
        : restrict === 'lettersNoSpace'
          ? /[^\p{L}\p{M}'’.-]/gu
        : restrict === 'numbers'
          ? /[^0-9\s]/g
        : restrict === 'numbersNoSpace'
          ? /[^0-9]/g
          : restrict === 'custom' && typeof config.pattern === 'string' && config.pattern
            ? (() => {
                try {
                  // The pattern names what is ALLOWED; everything else is dropped.
                  return new RegExp(`(?!${config.pattern})[\\s\\S]`, 'gu');
                } catch {
                  return null;
                }
              })()
            : null;
    const recase = (text: string): string => {
      switch (textCase) {
        case 'upper':
          return text.toLocaleUpperCase();
        case 'lower':
          return text.toLocaleLowerCase();
        case 'title':
          return text.replace(/(^|\s)(\p{L})/gu, (m) => m.toLocaleUpperCase());
        default:
          return text;
      }
    };

    // The limit counts characters as the shopper sees them (a family emoji is
    // one), so it is enforced in `refresh` rather than by the browser's own
    // `maxLength`, which counts UTF-16 units: 👨‍👩‍👧 alone is eight of them.
    // An authored "default" on a typing field is a suggestion, never an answer
    // (see `initialValue`), so it shows as the placeholder — unless the field
    // has a placeholder of its own, which is the more deliberate setting.
    const suggestion = config.default ?? config.defaultValue;
    const placeholder =
      (typeof config.placeholder === 'string' ? config.placeholder : '') ||
      (typeof suggestion === 'string' ? suggestion : '');
    if (placeholder) input.placeholder = placeholder;

    let counter: HTMLSpanElement | undefined;
    if (maxLength) {
      counter = document.createElement('span');
      counter.className = 'pz-counter';
      row.appendChild(counter);
    }

    // Letters the product's font cannot draw ("Đorđe" on a font without
    // Serbian Latin), measured with the print font a moment after typing
    // stops. A warning, never a block: the shopper decides, and the print
    // worker flags the file for staff as well.
    const glyphNote = document.createElement('p');
    glyphNote.className = 'pz-hint pz-glyph-note';
    glyphNote.setAttribute('role', 'status');
    glyphNote.hidden = true;
    row.appendChild(glyphNote);
    let glyphTimer: number | undefined;
    let glyphSeq = 0;
    const checkGlyphs = (text: string): void => {
      window.clearTimeout(glyphTimer);
      const seq = ++glyphSeq;
      glyphTimer = window.setTimeout(() => {
        void this.missingLetters(option.id, text).then((missing) => {
          if (seq !== glyphSeq) return;
          // Letters and emoji have different fixes: write it without the
          // accent, or pick another emoji.
          const letters = missing.filter((g) => !isEmojiGrapheme(g));
          const emoji = missing.filter(isEmojiGrapheme);
          glyphNote.hidden = missing.length === 0;
          glyphNote.textContent = [
            letters.length ? TEXT_FIELD_TEXTS.missingLetters.replace('{letters}', letters.join(', ')) : '',
            emoji.length ? TEXT_FIELD_TEXTS.missingEmoji.replace('{letters}', emoji.join(' ')) : '',
          ].filter(Boolean).join(' ');
        });
      }, 450);
    };

    const refresh = (): void => {
      let cleaned = dropLoneSurrogates(allowEmojis ? input.value : stripEmoji(input.value));
      if (allowed) cleaned = cleaned.replace(allowed, '');
      cleaned = recase(cleaned);
      if (maxLength) cleaned = truncateText(cleaned, maxLength);
      if (cleaned !== input.value) {
        const caret = Math.max(0, (input.selectionStart ?? cleaned.length) - (input.value.length - cleaned.length));
        input.value = cleaned;
        try {
          input.setSelectionRange(caret, caret);
        } catch {
          // Some mobile keyboards refuse this mid-composition; the text is
          // already correct, so the caret is not worth an exception.
        }
      }
      // A textarea may cap its lines and the characters per line (Customily's
      // "Max number of lines" / "Max characters per line").
      if (input instanceof HTMLTextAreaElement && (maxLines || maxPerLine)) {
        let lines = input.value.split('\n');
        if (maxLines) lines = lines.slice(0, maxLines);
        // By character, so a line never ends in half an emoji.
        if (maxPerLine) lines = lines.map((line) => truncateText(line, maxPerLine));
        const limited = lines.join('\n');
        if (limited !== input.value) input.value = limited;
      }
      if (counter && maxLength) counter.textContent = `${textLength(input.value)}/${maxLength}`;
      const text = trimSpaces ? input.value.trim() : input.value;
      // "Add chains to nameplate": the braces are how nameplate fonts draw the
      // chain ends; they are part of the answer, never of what the shopper types.
      this.set(option.id, addChains && text ? `{${text}}` : text);
      checkGlyphs(text);
    };

    input.addEventListener('input', refresh);
    this.setters.set(option.id, (value) => {
      const raw = String(value);
      input.value = addChains ? raw.replace(/^\{([\s\S]*)\}$/, '$1') : raw;
      if (counter && maxLength) counter.textContent = `${textLength(input.value)}/${maxLength}`;
      checkGlyphs(input.value);
    });

    // A field with a variable name is pre-filled from the page URL
    // (`?@ime=Ana`), Customily's way of linking a personalized product from
    // an email or an ad with the name already typed in.
    const variable = typeof config.variable === 'string' ? config.variable.trim() : '';
    if (variable) {
      try {
        const given = new URLSearchParams(window.location.search).get(`@${variable}`);
        if (given) {
          input.value = recase(given);
          window.setTimeout(refresh, 0);
        }
      } catch {
        /* no URL to read */
      }
    }
  }

  /**
   * The letters of `text` that the font of a layer this text option prints on
   * cannot draw, measured by the print renderer. Empty when the renderer is not
   * loaded (the canvas fallback on a weak phone), when nothing is typed, or when
   * every letter exists. Each bound layer is measured in its own font.
   */
  private async missingLetters(optionId: string, text: string): Promise<string[]> {
    const measure = renderBundle()?.missingGlyphs;
    if (!measure || !text.trim()) return [];
    const product = this.options.product;
    const fonts = (product.fonts ?? []).map((font) => ({ id: font.id, url: font.url }));
    const found = new Set<string>();
    for (const binding of product.bindings) {
      if (binding.optionId !== optionId || binding.property !== 'text') continue;
      const side = product.sides.find((s) => s.id === binding.sideId);
      const element = side?.elements.find((el) => el.id === binding.elementId);
      if (!side || !element || element.type !== 'text') continue;
      try {
        // With the font's Serbian twin marked, so letters the twin can draw
        // are not reported: the print will switch to it on its own.
        const single = { ...side, elements: [{ ...element, value: text }] } as Scene;
        const letters = await measure({
          scene: product.fontTwins ? annotateFontFallbacks(single, product.fontTwins) : single,
          scale: 1,
          fonts,
          assets: [],
          mapboxToken: product.mapboxToken,
          elementId: element.id,
        });
        for (const letter of letters) found.add(letter);
      } catch {
        // A measurement that fails says nothing; the worker's check still runs.
      }
    }
    return [...found];
  }

  private buildControl(option: OptionDef, row: HTMLElement): HTMLElement {
    const config = option.config ?? {};

    switch (option.type) {
      case 'textarea': {
        const input = document.createElement('textarea');
        input.className = 'pz-input';
        input.rows = 3;
        this.wireText(option, input, row);
        return input;
      }

      case 'dropdown': {
        // A dropdown whose choices carry pictures shows them as small swatches
        // (Customily's "dropdown swatch"); a plain one is a native select.
        const offered = option.values.filter((v) => v.meta?.hidden !== true);
        if (config.style === 'dropdownSwatch' && offered.some((v) => v.thumbnailUrl)) {
          return this.buildSwatches(option, offered, true);
        }
        // Font choices are shown in their own font (@pz/scene choiceFontId),
        // which a native select cannot do: a phone's dropdown ignores fonts.
        if (offered.some((v) => this.choiceFont(option, v))) return this.buildSwatches(option, offered, false);
        const select = document.createElement('select');
        select.className = 'pz-input';
        const blank = document.createElement('option');
        blank.value = '';
        // Customily's own Placeholder default. An em dash said nothing about
        // what the list is for; "Choose an option" is what its shoppers read.
        blank.textContent =
          typeof config.placeholder === 'string' && config.placeholder
            ? config.placeholder
            : CHOICE_TEXTS.dropdownPlaceholder;
        select.appendChild(blank);
        for (const value of offered) {
          const item = document.createElement('option');
          item.value = value.value;
          item.textContent =
            value.priceDelta !== 0
              ? `${value.label} (${priceSign(value.priceDelta)}${this.options.formatPrice(Math.abs(value.priceDelta))})`
              : value.label;
          // "Disabled" on a Customily value: still listed, so the shopper sees
          // the choice exists, but it cannot be picked (sold out, coming soon).
          if (value.meta?.disabled === true) item.disabled = true;
          const tip = this.tooltipFor(value);
          if (tip) item.title = tip;
          select.appendChild(item);
        }
        select.addEventListener('change', () => this.set(option.id, select.value));
        this.setters.set(option.id, (value) => {
          select.value = String(value);
        });
        // Customily's "dropdown search": a filter box above a long list.
        if (this.options.product.theme?.dropdownSearch === 'true' && offered.length > 6) {
          const wrap = document.createElement('div');
          wrap.className = 'pz-dropdown-search';
          const filter = document.createElement('input');
          filter.type = 'search';
          filter.className = 'pz-input pz-dropdown-filter';
          filter.placeholder = 'Pretraži…';
          filter.setAttribute('aria-label', 'Pretraži opcije');
          filter.addEventListener('input', () => {
            const q = filter.value.trim().toLowerCase();
            for (const item of Array.from(select.options)) {
              if (!item.value) continue;
              item.hidden = q !== '' && !(item.textContent ?? '').toLowerCase().includes(q);
            }
            // The first visible match is offered straight away.
            if (q !== '') {
              const first = Array.from(select.options).find((o) => o.value && !o.hidden);
              if (first && select.value !== first.value) {
                select.value = first.value;
                this.set(option.id, select.value);
              }
            }
          });
          wrap.append(filter, select);
          return wrap;
        }
        return select;
      }

      case 'swatch':
        return this.buildSwatches(
          option,
          option.values.filter((v) => v.meta?.hidden !== true),
          config.style === 'small',
        );

      case 'clipart_multi':
        return this.buildClipartMulti(option);

      case 'ai_image':
        return this.buildAiImage(option);

      case 'divider': {
        // Built in buildRow; never reached.
        return document.createElement('span');
      }

      case 'checkbox': {
        const wrap = document.createElement('label');
        wrap.className = 'pz-check';
        const input = document.createElement('input');
        input.type = 'checkbox';
        // Customily's "Selected": the box starts ticked. `checked` is the older
        // name our own importer wrote; both mean the same thing.
        if (config.startSelected === true || config.checked === true) input.checked = true;
        input.addEventListener('change', () => this.set(option.id, input.checked));
        const text =
          typeof config.checkboxLabel === 'string' && config.checkboxLabel !== ''
            ? config.checkboxLabel
            : '';
        // A checkbox with no text of its own is still named for a screen
        // reader — the question's own label is right above it.
        if (text === '') input.setAttribute('aria-label', option.label);
        wrap.append(input, document.createTextNode(text));
        this.setters.set(option.id, (value) => {
          input.checked = Boolean(value);
        });
        return wrap;
      }

      case 'date':
        return this.buildDatePicker(option);

      case 'color':
        return this.buildColorPicker(option);

      case 'image_upload':
      case 'vector_upload': {
        // Customily's "Multiple Upload Image": one control, an explicit list of
        // Image Placeholder ids, and the shopper's selection fanned across them
        // in order. Without the list this is the ordinary single-photo field
        // (whose multi-select still spills into the form's other empty slots).
        const targets = targetElementIds(config);
        return targets.length > 0 ? this.buildMultiUpload(option, targets) : this.buildUpload(option);
      }

      case PHOTO_COLLAGE_OPTION_TYPE:
        return this.buildCollage(option);

      case DESIGN_AREA_OPTION_TYPE:
        return this.buildDesignArea(option);

      case 'map_location':
        return this.buildLocationSearch(option);

      case 'spotify_track':
        return this.buildSpotifySearch(option);

      case 'audio_source':
      case 'voice_record':
      case 'video_source':
        return createAudioControl({
          option,
          api: this.options.api,
          initial: this.options.initialAudios
            ? (!this.options.initialAudiosOption || this.options.initialAudiosOption === option.id ? this.options.initialAudios : undefined)
            : this.options.draftAudios?.[option.id],
          // The same rule the server bills by: the first sound question with one.
          productLength: this.options.product.options.find(
            (o) => (o.type === 'audio_source' || o.type === 'voice_record') && (o.config as { length?: unknown } | undefined)?.length,
          )?.config?.length as import('./types.js').AudioOptionConfig['length'],
          formatPrice: (amount) => this.options.formatPrice(amount),
          // Reported before the answer below, which is what redraws the total.
          onExtraPrice: (amount, label) => {
            if (amount > 0) this.soundFees.set(option.id, { amount, label: label || 'Premium dužina' });
            else this.soundFees.delete(option.id);
          },
          // The sound module this product is made for, so the checklist can show
          // the running total against what the hardware actually holds.
          profile: this.options.product.audioProfile,
          registerStop: (stop) => this.mediaStops.add(stop),
          registerImport: (accept) => this.mediaImporters.set(option.id, accept),
          // A singing gift that also has a Spotify picker: the song picked
          // there is offered to the sound card, so it is not typed twice.
          pickedSong: {
            current: () => {
              for (const o of this.options.product.options) {
                if (o.type !== 'spotify_track') continue;
                const value = this.values[o.id];
                const words = trackWords(value);
                if (!words || (!words.title && !words.artists)) continue;
                // The whole track, for the sound step's "Tvoja pesma" card: the
                // id it remembers, the length it prices, the sleeve it shows.
                const track = value as Partial<SpotifyTrackResult>;
                return {
                  ...words,
                  uri: track.uri,
                  durationMs: typeof track.durationMs === 'number' && track.durationMs > 0 ? track.durationMs : undefined,
                  cover: track.albumArt ?? track.thumbnail ?? undefined,
                };
              }
              return null;
            },
            subscribe: (listener) => {
              for (const o of this.options.product.options) {
                if (o.type === 'spotify_track') this.watch(o.id, () => listener());
              }
            },
          },
          onChange: (audios) => {
            this.options.onAudioChange(audios, option.id);
            // The sounds also have to count as this field's answer: without this
            // a required sound field can never validate, because the selections
            // travel outside the values map.
            const summary = audios
              .map((a) => a.title ?? a.url ?? 'zvuk')
              .join(' + ');
            this.set(option.id, summary || undefined);
          },
        });

      case 'text':
      default: {
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'pz-input';
        this.wireText(option, input, row);
        return input;
      }
    }
  }

  // ── The design area ───────────────────────────────────────────────────────

  /**
   * The layers a zone came with — the ones the shopper is not charged for.
   *
   * The card seeds the answer from the zone's authored composition, so without
   * this a template of three layers would open the product page already showing
   * three surcharges for work the shopper has not done. Identity is the layer
   * id: replacing the text of an authored line leaves it authored and free,
   * deleting it and adding one is a layer the shopper added and pays for.
   * Memoised with the config for the same reason — it is read on every repaint.
   */
  private authoredLayersOf(optionId: string): string[] {
    const cached = this.designAuthoredIds.get(optionId);
    if (cached) return cached;
    const zone = designZoneOf(this.options.product, optionId)?.zone;
    const ids = zone ? authoredLayerIds(zone) : [];
    this.designAuthoredIds.set(optionId, ids);
    return ids;
  }

  /** The zone's rules for one question; a stand-in when the binding is broken. */
  private designConfigFor(optionId: string): DesignAreaConfig {
    const cached = this.designConfigs.get(optionId);
    if (cached) return cached;
    // A question whose zone cannot be found still has to render something a
    // shopper can use rather than an empty box: the defaults are a 75 × 50 mm
    // area, which is what `resolveDesignAreaConfig` assumes throughout.
    const config = designZoneOf(this.options.product, optionId)?.zone.config ?? { sizeMm: { w: 75, h: 50 } };
    this.designConfigs.set(optionId, config);
    return config;
  }

  /** Where an asset's pixels are, whether it shipped, was uploaded or was cut out. */
  private assetUrlFor(id: string): string | undefined {
    return (
      this.assetUrls.get(id) ??
      this.options.product.assets?.find((asset) => asset.id === id)?.url ??
      this.options.resolveAssetUrl?.(id)
    );
  }

  /**
   * The designer's words: the built-in Serbian it carries itself, with whatever
   * the product overrode under `display.labels['designer.*']` on top.
   */
  private designerTexts(): Partial<DesignerTexts> {
    const labels = this.options.product.display?.labels ?? {};
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(labels)) {
      if (!key.startsWith('designer.') || typeof value !== 'string' || value.trim() === '') continue;
      out[key.slice('designer.'.length)] = value.trim().slice(0, 200);
    }
    return out as Partial<DesignerTexts>;
  }

  /**
   * The fonts a zone offers, and how to make one usable.
   *
   * `list` is the shop's fonts narrowed to the zone's own allow-list (empty =
   * all of them), which is also exactly what `designAreaProblems` accepts — a
   * font the picker offered and the commit refused would be the worst kind of
   * bug here. The alias (`pz-font-<id>`) matches `Preview.loadFonts`: two
   * records of one family (Regular and Bold) would otherwise share a FontFace
   * name and the browser would pick one face for both.
   */
  private designFonts(cfg: ResolvedDesignAreaConfig): DesignerFonts {
    const allowed = new Set(cfg.text.fontIds);
    const fonts = this.options.product.fonts ?? [];
    return {
      list: fonts
        .filter((font) => allowed.size === 0 || allowed.has(font.id))
        // `category` and `scripts` decide the picker's chips. They are passed
        // through rather than guessed here: the shop said what a face is (or
        // said nothing, and the picker's own heuristic takes over) and this is
        // not the place to have a second opinion about it.
        .map((font) => ({
          id: font.id,
          family: font.family,
          style: font.style,
          url: font.url,
          ...(isFontCategory(font.category) ? { category: font.category } : {}),
          ...(Array.isArray(font.scripts) && font.scripts.length ? { scripts: font.scripts } : {}),
        })),
      cssFamily: (fontId) => `pz-font-${fontId}`,
      ensure: (fontId) => this.ensureFontFace(fontId),
    };
  }

  /**
   * Registers a font with the page as `pz-font-<id>` (the alias
   * `Preview.loadFonts` uses), once per face.
   */
  private ensureFontFace(fontId: string): Promise<void> {
    const already = this.fontLoads.get(fontId);
    if (already) return already;
    const font = (this.options.product.fonts ?? []).find((one) => one.id === fontId);
    const load = (async (): Promise<void> => {
      if (!font || typeof FontFace === 'undefined' || typeof document === 'undefined') return;
      try {
        const face = new FontFace(`pz-font-${fontId}`, `url(${JSON.stringify(font.url)})`);
        await face.load();
        document.fonts?.add(face);
      } catch {
        // A face that will not load is not worth losing the designer or a
        // font choice over; the renderer fetches the file itself for the print.
      }
    })();
    this.fontLoads.set(fontId, load);
    return load;
  }

  /** The font a choice picks, when it picks one the definition carries (@pz/scene choiceFontId). */
  private choiceFont(option: OptionDef, value: OptionDef['values'][number]): string | null {
    const product = this.options.product;
    const bindsFont = (product.bindings ?? []).some((b) => b.optionId === option.id && b.property === 'font');
    return choiceFontId(value, bindsFont, (id) => (product.fonts ?? []).some((font) => font.id === id));
  }

  /**
   * What a font question's samples write: the words on the layer whose font
   * it sets — the shopper's answer to the text question bound to that layer,
   * else the layer's own default text — and whether the layer prints them in
   * capitals. `watch` is the text questions to follow while they are typed.
   */
  private fontSampleSource(option: OptionDef): { text: () => string; uppercase: boolean; watch: string[] } {
    const product = this.options.product;
    const bindings = product.bindings ?? [];
    const layers = new Set(bindings.filter((b) => b.optionId === option.id && b.property === 'font').map((b) => b.elementId));
    const watch = [...new Set(bindings.filter((b) => b.property === 'text' && layers.has(b.elementId)).map((b) => b.optionId))];
    const layer = product.sides
      .flatMap((side) => side.elements)
      .find((el): el is TextElement => el.type === 'text' && layers.has(el.id));
    const authored = typeof layer?.value === 'string' && layer.value.trim() ? layer.value : (layer?.default ?? '');
    return {
      text: () => {
        for (const id of watch) {
          const answer = this.values[id];
          if (typeof answer === 'string' && answer.trim()) return answer;
        }
        return authored;
      },
      uppercase: layer?.uppercase === true,
      watch,
    };
  }

  private repaintFontSamples(): void {
    for (const repaint of this.fontSampleRepaints) repaint();
  }

  /** The clipart catalogue, scoped to this product (or, in Studio, this template). */
  private designArtwork(zoneId: string | undefined): DesignerArtwork {
    const product = this.options.product;
    const scope = this.options.previewing
      ? { templateId: product.templateId }
      : { productId: product.productId, templateId: product.templateId };
    const api = this.options.api as Partial<Api>;
    const empty = { libraries: [] as ArtworkLibrary[], items: [] as ArtworkItem[], total: 0, offset: 0, limit: 0 };
    const ask = (
      query: Parameters<Api['artwork']>[0],
    ): ReturnType<Api['artwork']> =>
      api.artwork ? api.artwork({ ...scope, zone: zoneId, ...query }) : Promise.resolve(empty);
    // The tab list never changes while a designer is open; asking for it once
    // keeps switching tabs instant.
    let libraries: Promise<ArtworkLibrary[]> | undefined;
    return {
      libraries: () => (libraries ??= ask({ limit: 1 }).then((page) => page.libraries).catch(error => {
        libraries = undefined;
        throw error;
      })),
      search: (query) =>
        ask({
          library: query.libraryId,
          q: query.q,
          offset: query.offset,
          limit: query.limit ?? 60,
        }).then((page) => ({ items: page.items, total: page.total })),
    };
  }

  /** Uploading a picture into a design, and cutting its background out. */
  private designUploads(): DesignerUploads {
    const api = this.options.api as Partial<Api>;
    return {
      upload: async (file) => {
        const size = await pictureSize(file);
        const ready = await fitForUpload(file, size);
        const info = await this.options.api.upload(ready, ready.name || 'slika.jpg', 'image');
        // Drawn from the server's preview copy; the original prints.
        const shown = info.previewUrl ?? info.url;
        this.assetUrls.set(info.id, shown);
        this.options.onDesignAsset?.(info.id, shown);
        // The pixel size decides the quality badge and whether the design can be
        // committed at all, so it is never guessed: the server's own measurement
        // first, the file's header as the fallback.
        const measured = info.width && info.height ? { w: info.width, h: info.height } : (size ?? (await imageSize(file)));
        return { assetId: info.id, url: shown, w: measured?.w ?? 0, h: measured?.h ?? 0 };
      },
      removeBackground: async (assetId) => {
        if (!api.cutout) return undefined;
        const cut = await api.cutout(assetId, 'background');
        if (!cut.url) return undefined;
        // Keep the original available for another copy and for undo. Only the
        // render alias points to prepared pixels; answers keep the upload ID.
        this.assetUrls.set(designCutoutAssetId(assetId), cut.url);
        this.options.onDesignAsset?.(designCutoutAssetId(assetId), cut.url);
        return { url: cut.url };
      },
      /**
       * "Sa telefona": a pairing the designer draws as a QR code.
       *
       * `wait` resolves the first time the phone has sent anything — the panel
       * wants to show a picture as soon as one arrives, not at the end of the
       * whole batch — and resolves empty when the pairing runs out. Polling
       * rather than a socket: the phone may take a minute to be picked up, the
       * page may be backgrounded meanwhile, and a two-second poll of a tiny
       * JSON list costs less than holding a connection open for ten minutes.
       */
      ...(api.pairPhoneUpload && api.pairedPhotos
        ? {
          pairPhone: async () => {
            const pairing = await api.pairPhoneUpload!();
            let stopped = false;
            return {
              url: pairing.url,
              expiresAt: pairing.expiresAt,
              cancel: () => {
                stopped = true;
              },
              wait: async () => {
                while (!stopped && Date.now() < pairing.expiresAt) {
                  const page = await api.pairedPhotos!(pairing.token);
                  if (page.expired) return [];
                  if (page.assets.length > 0) {
                    // The designer draws them through the renderer, which knows
                    // assets by id — the same registration an ordinary upload
                    // does, so a paired photo behaves like any other from here.
                    for (const asset of page.assets) {
                      this.assetUrls.set(asset.assetId, asset.url);
                      this.options.onDesignAsset?.(asset.assetId, asset.url);
                    }
                    return page.assets;
                  }
                  await new Promise((resolve) => window.setTimeout(resolve, PHONE_POLL_MS));
                }
                return [];
              },
            };
          },
        }
        : {}),
    };
  }

  /**
   * The designer's window onto the print renderer.
   *
   * `renderRaw` and `measureText` are getters on purpose: the contract says the
   * property is absent until the Skia bundle exists, the bundle arrives on its
   * own schedule (and an older cached one has no `measureText` at all), and the
   * designer reads them on every layer it draws.
   */
  private designRenderAssets(scene: Scene): Array<{ id: string; url: string }> {
    const map = new Map((this.options.product.assets ?? []).map(a => [a.id, a.url]));
    for (const [id,url] of this.assetUrls) map.set(id,url);
    const remember = (id: string | undefined): void => {
      if (!id) return;
      for (const key of [id, designCutoutAssetId(id)]) { const url = this.assetUrlFor(key); if (url) map.set(key,url); }
    };
    for (const el of scene.elements) {
      if (el.type === 'sticker' || el.type === 'designArea') for (const layer of el.value?.layers ?? []) { if (layer.kind === 'image' || layer.kind === 'artwork') remember(layer.assetId); }
      if (el.type === 'image' || el.type === 'vector') remember(el.value ?? (el.source.kind === 'static' ? el.source.assetId : undefined));
    }
    return [...map].map(([id,url]) => ({id,url}));
  }

  private designRenderer(): DesignerRenderer {
    const product = this.options.product;
    const fonts = (): Array<{ id: string; url: string }> =>
      (product.fonts ?? []).map((font) => ({ id: font.id, url: font.url }));
    // The designer's own canvas switches to a font's Serbian twin exactly as
    // the product preview and the print do, so Cyrillic never shows as boxes.
    const twinned = (scene: Scene): Scene =>
      product.fontTwins ? annotateFontFallbacks(scene, product.fontTwins) : scene;
    const raw = async (
      scene: Scene,
      scale: number,
    ): Promise<{ pixels: Uint8Array; width: number; height: number } | undefined> =>
      renderBundle()?.renderRaw?.({
        scene: twinned(scene),
        scale,
        fonts: fonts(),
        assets: this.designRenderAssets(scene),
        mapboxToken: product.mapboxToken,
      });
    const measure = async (
      scene: Scene,
      elementId: string,
    ): Promise<{ width: number; height: number } | undefined> => {
      const metrics = await renderBundle()?.measureText?.({
        scene: twinned(scene),
        scale: 1,
        fonts: fonts(),
        assets: this.designRenderAssets(scene),
        mapboxToken: product.mapboxToken,
        elementId,
      });
      return metrics ? { width: metrics.width, height: metrics.height } : undefined;
    };
    return {
      get renderRaw() {
        return renderBundle()?.renderRaw ? raw : undefined;
      },
      get measureText() {
        return renderBundle()?.measureText ? measure : undefined;
      },
      registerAsset: (id, url) => {
        this.assetUrls.set(id, url);
        this.options.onDesignAsset?.(id, url);
      },
      assetUrl: (id) => this.assetUrlFor(id),
      onReady: (listener) => whenRendererReady(listener),
    };
  }

  /**
   * The design area: a card showing what the shopper made, and a button that
   * opens the full-screen designer over everything else.
   *
   * The card is deliberately a summary rather than a working canvas. A print
   * zone is metres of interaction — layers, handles, palettes, an artwork
   * catalogue — and none of it fits in a product page's option column, least of
   * all on a phone. So the page shows the design as it will print (the same
   * renderer, at thumbnail size), what is in it in words, what it costs, and one
   * button; the designer itself takes the whole screen through the layer stack,
   * which is what lets it open from the wizard as well and still be on top.
   *
   * Every change lands in the answers while the designer is still open, throttled
   * — that is what makes the product preview behind (and the wizard's compact
   * pane) show the design being built rather than only its result.
   */
  private buildDesignArea(option: OptionDef): HTMLElement {
    const config = option.config ?? {};
    const labels = this.options.product.display?.labels ?? {};
    const zone = designZoneOf(this.options.product, option.id)?.zone;
    const zoneConfig = this.designConfigFor(option.id);
    // The zone's own layers. The card, the running total, the designer's price
    // chip and the server's bill all have to agree about which of them the
    // shopper is charged for, so all four read this one list.
    const authoredIds = this.authoredLayersOf(option.id);
    const cfg = resolveDesignAreaConfig(zoneConfig);
    const texts = this.designerTexts();
    const label = (key: string, fallback: string): string => {
      const value = labels[key];
      return typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback;
    };
    const buttonText =
      typeof config.buttonText === 'string' && config.buttonText.trim() !== ''
        ? config.buttonText.trim()
        : undefined;

    const card = document.createElement('div');
    card.className = 'pz-design-card';

    const thumb = document.createElement('div');
    thumb.className = 'pz-design-thumb';
    // The card is the zone's own shape before anything is in it, so the page
    // does not jump the moment the first layer lands.
    thumb.style.setProperty('--pz-design-aspect', `${cfg.sizeMm.w} / ${cfg.sizeMm.h}`);
    const placeholder = document.createElement('div');
    placeholder.className = 'pz-design-empty';
    placeholder.textContent = formatZoneSize(cfg.sizeMm);
    let canvas: HTMLCanvasElement | undefined;
    thumb.appendChild(placeholder);

    const body = document.createElement('div');
    body.className = 'pz-design-body';
    const summary = document.createElement('p');
    summary.className = 'pz-design-summary';
    const size = document.createElement('p');
    size.className = 'pz-design-size';
    size.textContent = DESIGN_TEXTS.size(formatZoneSize(cfg.sizeMm));
    const price = document.createElement('p');
    price.className = 'pz-design-price';
    price.hidden = true;
    // Zazzle's "EZ Templates": the fields the zone's author marked as editable
    // on the product page, offered right here so the commonest personalization
    // — a name, a date, one photograph — never needs the full designer.
    const fields = document.createElement('div');
    fields.className = 'pz-design-fields';
    fields.hidden = true;
    // "Moji detalji" (Zazzle's Moment): fill this design from details
    // remembered on another product, or remember this one's for the next.
    const momentRow = document.createElement('div');
    momentRow.className = 'pz-moment';
    momentRow.hidden = true;
    const momentFill = document.createElement('button');
    momentFill.type = 'button';
    momentFill.className = 'pz-btn-ghost small pz-moment-fill';
    const momentKeep = document.createElement('button');
    momentKeep.type = 'button';
    momentKeep.className = 'pz-btn-ghost small pz-moment-keep';
    momentKeep.textContent = DESIGN_TEXTS.momentSave;
    const momentNote = document.createElement('span');
    momentNote.className = 'pz-hint';
    momentNote.setAttribute('role', 'status');
    momentRow.append(momentFill, momentKeep, momentNote);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pz-btn pz-design-open';
    for (const one of String(config.buttonClass ?? '').split(/\s+/)) {
      const clean = one.replace(/[^A-Za-z0-9_-]/g, '');
      if (clean) button.classList.add(clean);
    }
    body.append(summary, size, price, fields, momentRow, button);
    card.append(thumb, body);

    const answer = (): DesignAreaAnswer | null => {
      const value = this.values[option.id];
      return isDesignAreaAnswer(value) ? value : null;
    };

    let layer: LayerHandle | undefined;
    let handle: DesignerHandle | undefined;
    let thumbToken = 0;

    /**
     * The design as it will print, at card size, drawn by the print renderer.
     *
     * `retry` exists for one specific race: CanvasKit is a single engine with a
     * single queue, and the card repaints the moment the designer closes —
     * while the designer's own layer rasters may still be in flight or being
     * torn down. A render lost to that must not leave the shopper looking at a
     * size label where their design should be, so it is tried once more.
     */
    const drawThumb = (retry = true): void => {
      const value = answer();
      const bundle = renderBundle();
      if (isEmptyDesign(value) || !bundle?.renderRaw) {
        placeholder.hidden = false;
        if (canvas) canvas.hidden = true;
        return;
      }
      const again = (): void => {
        if (retry && !layer?.isOpen) window.setTimeout(() => drawThumb(false), 200);
      };
      const token = (thumbToken += 1);
      const pxPerMm = Math.max(1, Math.min(8, DESIGN_THUMB_PX / cfg.sizeMm.w));
      void bundle
        .renderRaw({
          scene: zoneScene(zoneConfig, value, pxPerMm),
          scale: 1,
          fonts: (this.options.product.fonts ?? []).map((font) => ({ id: font.id, url: font.url })),
          assets: this.designRenderAssets(zoneScene(zoneConfig, value, pxPerMm)),
          mapboxToken: this.options.product.mapboxToken,
        })
        .then((result) => {
          // A render that finished after a newer one started would paint the
          // design the shopper had two edits ago.
          if (token !== thumbToken) return;
          if (!result) return again();
          canvas ??= document.createElement('canvas');
          canvas.className = 'pz-design-preview';
          if (!blitPixels(canvas, result)) return;
          canvas.hidden = false;
          placeholder.hidden = true;
          if (canvas.parentElement !== thumb) thumb.appendChild(canvas);
        })
        .catch(() => {
          // One more go, then the size label stands: a thumbnail that could not
          // be drawn is not a design that could not be made.
          again();
        });
    };

    // ── The quick fields ────────────────────────────────────────────────────

    /**
     * Writes one template field back into the design.
     *
     * Every layer that shares the field's `key` is written at once — that is
     * what a key IS: one name printed on the front and on the lid is one
     * question. The result goes through `normalizeDesignAnswer`, the same
     * validator the designer and the commit use, so a card edit cannot produce
     * an answer the till would refuse.
     */
    const writeField = (layer: DesignLayer, patch: Partial<DesignLayer>): void => {
      const current = answer();
      if (!current) return;
      const key = layer.template?.key;
      const targets = new Set(
        key
          ? current.layers.filter((one) => one.template?.key === key).map((one) => one.id)
          : [layer.id],
      );
      const next = normalizeDesignAnswer({
        ...current,
        layers: current.layers.map((one) =>
          targets.has(one.id) ? ({ ...one, ...patch } as DesignLayer) : one,
        ),
      });
      if (!next) return;
      this.set(option.id, next);
    };

    /** The rows currently on the card, by the layer id each one writes to. */
    const fieldRows = new Map<string, { refresh(layer: DesignLayer): void }>();
    let fieldSignature = '';

    const buildTextField = (layer: DesignLayer & { kind: 'text' }): HTMLElement => {
      const wrap = document.createElement('label');
      wrap.className = 'pz-design-field';
      const caption = document.createElement('span');
      caption.className = 'pz-design-field-label';
      const labelText = document.createElement('span');
      labelText.textContent = layer.template?.label ?? '';
      const pill = document.createElement('span');
      pill.className = 'pz-design-required';
      pill.textContent = DESIGN_TEXTS.mustReplace;
      caption.append(labelText, pill);
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'pz-input';
      input.maxLength = cfg.text.maxChars;
      input.value = layer.text;
      input.addEventListener('input', () => {
        // Live, like every other text field on the page: the product preview
        // behind the card redraws as they type.
        writeField(layer, { text: input.value } as Partial<DesignLayer>);
      });
      wrap.append(caption, input);
      fieldRows.set(layer.id, {
        refresh: (fresh) => {
          if (fresh.kind !== 'text') return;
          labelText.textContent = fresh.template?.label ?? '';
          if (document.activeElement !== input && input.value !== fresh.text) {
            input.value = fresh.text;
          }
          pill.hidden = !isUnreplaced(fresh);
          wrap.classList.toggle('is-unreplaced', isUnreplaced(fresh));
        },
      });
      return wrap;
    };

    const buildPictureField = (layer: DesignLayer): HTMLElement => {
      const wrap = document.createElement('div');
      wrap.className = 'pz-design-field pz-design-field--image';
      const caption = document.createElement('span');
      caption.className = 'pz-design-field-label';
      const labelText = document.createElement('span');
      labelText.textContent = layer.template?.label ?? '';
      const pill = document.createElement('span');
      pill.className = 'pz-design-required';
      pill.textContent = DESIGN_TEXTS.mustReplace;
      caption.append(labelText, pill);

      const row = document.createElement('div');
      row.className = 'pz-design-field-row';
      const preview = document.createElement('img');
      preview.className = 'pz-design-field-thumb';
      preview.alt = '';
      const replace = document.createElement('button');
      replace.type = 'button';
      replace.className = 'pz-btn-ghost small';
      replace.textContent = DESIGN_TEXTS.replaceImage;
      const picker = document.createElement('input');
      picker.type = 'file';
      picker.accept = IMAGE_UPLOAD_ACCEPT;
      picker.hidden = true;
      const status = document.createElement('span');
      status.className = 'pz-status';
      status.hidden = true;

      replace.addEventListener('click', () => picker.click());
      picker.addEventListener('change', () => {
        const file = picker.files?.[0];
        picker.value = '';
        if (!file) return;
        replace.disabled = true;
        status.hidden = false;
        status.classList.remove('is-error');
        status.textContent = DESIGN_TEXTS.uploading;
        void this.designUploads()
          .upload(file)
          .then((uploaded) => {
            status.hidden = true;
            // A crop belongs to the picture it was made on; keeping it would
            // frame a different photograph by numbers from the old one.
            writeField(layer, {
              assetId: uploaded.assetId,
              sourcePx: { w: uploaded.w, h: uploaded.h },
              crop: undefined,
            } as Partial<DesignLayer>);
          })
          .catch((err: unknown) => {
            status.hidden = false;
            status.classList.add('is-error');
            status.textContent = err instanceof Error ? err.message : DESIGN_TEXTS.uploadFailed;
          })
          .finally(() => {
            replace.disabled = false;
          });
      });

      row.append(preview, replace, picker, status);
      wrap.append(caption, row);
      fieldRows.set(layer.id, {
        refresh: (fresh) => {
          labelText.textContent = fresh.template?.label ?? '';
          const assetId = 'assetId' in fresh ? fresh.assetId : '';
          const url = assetId ? this.assetUrlFor(assetId) : undefined;
          preview.hidden = !url;
          if (url && preview.src !== url) preview.src = url;
          pill.hidden = !isUnreplaced(fresh);
          wrap.classList.toggle('is-unreplaced', isUnreplaced(fresh));
        },
      });
      return wrap;
    };

    /**
     * Rebuilds the quick fields only when the SET of them changes.
     *
     * A card repaints on every live write — including the ones these very
     * fields make — and rebuilding the inputs each time would take the cursor
     * out of the one being typed in. So the rows are kept and refreshed, and
     * only a different set of fields (a design loaded from the cart, a layer
     * deleted in the designer) rebuilds them.
     */
    const paintFields = (): void => {
      const value = answer();
      const list = cardTemplateFields(value);
      const signature = list.map((layer) => `${layer.id}:${layer.kind}`).join('|');
      if (signature !== fieldSignature) {
        fieldSignature = signature;
        fieldRows.clear();
        replaceKids(fields, ...list.map((layer) =>
          layer.kind === 'text' ? buildTextField(layer) : buildPictureField(layer)));
      }
      fields.hidden = list.length === 0;
      for (const layer of list) fieldRows.get(layer.id)?.refresh(layer);
    };

    // ── Moji detalji ────────────────────────────────────────────────────────
    const storage = (): Storage | undefined => {
      try {
        return typeof localStorage === 'undefined' ? undefined : localStorage;
      } catch {
        return undefined;
      }
    };
    const paintMoment = (): void => {
      const value = answer();
      if (this.options.previewing || momentFieldsOf(value).length === 0) {
        momentRow.hidden = true;
        return;
      }
      const store = readMomentStore(storage());
      const offers = momentOffers(value, store);
      momentFill.hidden = offers.length === 0;
      momentFill.textContent = DESIGN_TEXTS.momentApply(offers.join(', '));
      // Worth remembering: something here the store does not already hold.
      const captured = captureMoment([value], (id) => this.assetUrlFor(id), store);
      const news = Object.entries(captured.fields).some(([key, entry]) =>
        JSON.stringify(store?.fields[key]) !== JSON.stringify(entry));
      momentKeep.hidden = !news;
      momentRow.hidden = momentFill.hidden && momentKeep.hidden && !momentNote.textContent;
    };
    momentFill.addEventListener('click', () => {
      const current = answer();
      const store = readMomentStore(storage());
      if (!current || !store) return;
      const { answer: filled, assets } = applyMoment(current, store);
      for (const asset of assets) {
        this.assetUrls.set(asset.assetId, asset.url);
        this.options.onDesignAsset?.(asset.assetId, asset.url);
      }
      momentNote.textContent = DESIGN_TEXTS.momentApplied;
      this.set(option.id, filled);
    });
    momentKeep.addEventListener('click', () => {
      const store = captureMoment([answer()], (id) => this.assetUrlFor(id), readMomentStore(storage()));
      momentNote.textContent = writeMomentStore(storage(), store) ? DESIGN_TEXTS.momentSaved : '';
      paintMoment();
    });

    const paint = (): void => {
      const value = answer();
      const empty = isEmptyDesign(value);
      card.classList.toggle('is-empty', empty);
      paintFields();
      paintMoment();
      summary.textContent = empty ? cfg.placeholder || DESIGN_TEXTS.empty : describeDesign(value);
      const amount = designAreaPrice(zoneConfig, value, { authoredLayerIds: authoredIds });
      price.hidden = amount <= 0;
      price.textContent = amount > 0 ? `${DESIGN_TEXTS.surcharge}: ${this.options.formatPrice(amount)}` : '';
      button.textContent =
        buttonText ??
        (empty
          ? label('designArea.open', DESIGN_TEXTS.open)
          : label('designArea.edit', DESIGN_TEXTS.edit));
      // Re-rendering the card while the designer is over it would burn WASM
      // work on a picture nobody can see; it is redrawn when the layer closes.
      if (!layer?.isOpen) drawThumb();
    };

    // ── Writing the design back while it is being made ──────────────────────
    let liveTimer: number | undefined;
    let lastWrite = 0;
    const writeLive = (value: DesignAreaAnswer): void => {
      const now = Date.now();
      const since = now - lastWrite;
      const commit = (): void => {
        lastWrite = Date.now();
        liveTimer = undefined;
        this.set(option.id, value);
      };
      // Leading edge: dropping a layer shows on the product at once. Then no
      // more than one write per interval, however fast the shopper drags.
      if (since >= DESIGN_LIVE_MS) {
        if (liveTimer !== undefined) window.clearTimeout(liveTimer);
        commit();
        return;
      }
      if (liveTimer !== undefined) window.clearTimeout(liveTimer);
      liveTimer = window.setTimeout(commit, DESIGN_LIVE_MS - since);
    };
    const stopLive = (): void => {
      if (liveTimer !== undefined) window.clearTimeout(liveTimer);
      liveTimer = undefined;
    };

    /** The one failure a shopper can act on: the designer did not build. */
    const failCard = (): HTMLElement => {
      const wrap = document.createElement('div');
      wrap.className = 'pz-designer-failed';
      const message = document.createElement('p');
      message.className = 'pz-status is-error';
      message.textContent = DESIGN_TEXTS.failed;
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'pz-btn';
      close.textContent = DESIGN_TEXTS.close;
      close.addEventListener('click', () => layer?.close());
      wrap.append(message, close);
      return wrap;
    };

    // ── The product behind the designer ─────────────────────────────────────
    // Drawn by the host from the mockup; redrawn (debounced) when another
    // answer or the variation changes what the product looks like.
    let backdropToken = 0;
    let backdropTimer: number | undefined;
    const refreshBackdrop = (): void => {
      const draw = this.options.designBackdrop;
      if (!draw || !handle?.setProductBackdrop) return;
      const token = (backdropToken += 1);
      void draw(option.id, { ...this.values })
        .then((backdrop) => {
          if (token === backdropToken) handle?.setProductBackdrop?.(backdrop);
        })
        .catch(() => undefined);
    };
    const scheduleBackdrop = (): void => {
      window.clearTimeout(backdropTimer);
      backdropTimer = window.setTimeout(refreshBackdrop, 250);
    };

    const open = (): void => {
      if (layer?.isOpen) return;
      // A device that skipped the WASM upgrade still gets the real renderer
      // here: this is the one screen where an approximation is not good enough.
      void this.options.loadRenderer?.();
      const opened = answer();
      const host = document.createElement('div');
      host.className = 'pz-designer';
      layer = createLayer({
        host: this.layerHost(),
        className: 'pz-designer-layer',
        content: host,
        label: option.label || texts.title || DESIGN_TEXTS.dialogLabel,
        // A full-screen designer has no backdrop to tap, and Escape is the
        // designer's own key (it deselects the layer being worked on). Leaving
        // either wired would throw away an unfinished design on a stray press.
        closeOnBackdrop: false,
        closeOnEscape: false,
        onClose: () => {
          stopLive();
          window.clearTimeout(backdropTimer);
          backdropToken += 1;
          if (this.openDesign?.optionId === option.id) this.openDesign = undefined;
          try {
            handle?.destroy();
          } catch (err) {
            console.warn('[personalizy] the designer did not clean up', err);
          }
          handle = undefined;
          const dead = layer;
          layer = undefined;
          dead?.destroy();
          paint();
          button.focus?.();
        },
      });
      layer.open();
      const create = designerFactory ?? createDesigner;
      const productOptions = this.designProductOptions(option.id);
      const locations = this.printLocationsFor(option.id);
      try {
        handle = create({
          ...(productOptions ? { productOptions } : {}),
          ...(locations
            ? {
              printLocations: locations.list,
              switchPrintLocation: (id: string, value: DesignAreaAnswer) => {
                if (!locations.list.some((location) => location.id === id)) return;
                stopLive();
                this.movePrintLocation(option.id, id, value);
                layer?.close();
                this.designOpeners.get(id)?.();
              },
            }
            : {}),
          host,
          config: zoneConfig,
          value: opened,
          zoneName: zone?.name || option.label,
          fonts: this.designFonts(cfg),
          artwork: this.designArtwork(zone?.id),
          uploads: this.designUploads(),
          renderer: this.designRenderer(),
          texts,
          currency: { format: (amount) => this.options.formatPrice(amount) },
          authoredLayerIds: authoredIds,
          coarse: pointerIsCoarse(),
          share: this.options.onDesignShare ? async value => {
            stopLive(); this.set(option.id, value);
            return this.options.onDesignShare!();
          } : undefined,
          ...(this.options.library
            ? {
              library: {
                // The open side's live design lands in the answers first, so
                // the saved snapshot is the whole product as it is right now.
                save: async (value: DesignAreaAnswer, name: string) => {
                  stopLive();
                  this.set(option.id, value);
                  await this.options.library!.save(name);
                },
                list: () => this.options.library!.list(),
                remove: (id: string) => this.options.library!.remove(id),
                suggestName: () => this.options.library!.suggestName(),
              },
            }
            : {}),
          surfaces: this.visibleOptions().filter(item => item.type === DESIGN_AREA_OPTION_TYPE).map(item => ({ id: item.id, name: designZoneOf(this.options.product, item.id)?.zone.name || item.label, current: item.id === option.id })),
          switchSurface: (id, value) => {
            const next = this.designOpeners.get(id);
            if (!next || !this.visibleOptions().some(item => item.id === id)) return;
            stopLive(); this.set(option.id, value); layer?.close(); next();
          },
          onChange: (value) => writeLive(value),
          onDone: (value) => {
            stopLive();
            lastWrite = Date.now();
            this.set(option.id, value);
            layer?.close();
          },
          onCancel: () => {
            stopLive();
            // Back to the design as it was when the shopper opened it: the live
            // writes have already changed the answer underneath them.
            this.set(option.id, opened ?? undefined);
            layer?.close();
          },
        });
      } catch (err) {
        console.warn('[personalizy] the designer could not be built', err);
        replaceKids(host, failCard());
        return;
      }
      this.openDesign = {
        optionId: option.id,
        productChanged: scheduleBackdrop,
        closeKeeping: () => {
          stopLive();
          if (handle) this.set(option.id, handle.getValue());
          layer?.close();
        },
      };
      refreshBackdrop();
      // The layer has just been put in the top layer; the designer only has its
      // real size once the browser has laid it out.
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
        window.setTimeout(() => {
          try {
            handle?.relayout();
          } catch {
            /* a designer that cannot re-measure still works at its first size */
          }
        }, 0);
      }
    };

    this.designOpeners.set(option.id, open);
    button.addEventListener('click', () => open());

    // A restored design (from the cart, a draft or a share link) repaints the
    // card, and a designer left open takes the value too.
    this.setters.set(option.id, (value) => {
      if (handle && isDesignAreaAnswer(value)) {
        try {
          handle.setValue(value);
        } catch {
          /* the card still shows the truth */
        }
      }
      paint();
    });
    // …and so does every write through `set` — the designer's own live changes,
    // and anything else that answers this question from outside the card.
    this.watch(option.id, () => paint());

    // The Skia bundle is several megabytes and arrives after the form is
    // usable — routinely after a design restored from the cart has already been
    // painted. Without this the shopper comes back to their own design and sees
    // only a size label where the picture of it should be.
    whenRendererReady(() => {
      if (!layer?.isOpen) drawThumb();
    });

    paint();
    return card;
  }

  /**
   * Picture, colour or text choices as buttons. `multiple` lets the shopper
   * pick several (the answer is then a list); a click on a picked choice
   * unpicks it, so a mind can be changed without a reset.
   */
  private buildSwatches(option: OptionDef, offered: OptionDef['values'], small: boolean): HTMLElement {
    const config = option.config ?? {};
    const multiple = config.multiple === true;
    const group = document.createElement('div');
    group.className = small ? 'pz-swatches pz-swatches--small' : 'pz-swatches';
    // Customily's swatch layout: one scrolling row instead of wrapping lines.
    if (this.options.product.theme?.swatchLayout === 'horizontal') group.classList.add('pz-swatches--scroll');
    group.setAttribute('role', multiple ? 'group' : 'radiogroup');
    // Picture choices sit in an even grid: every tile the same size whatever
    // its caption, as many to a row as fit — the shop's tile size scaled by
    // the question's own size. Captions follow the question's setting; on
    // `auto` the ones that only count the choices ("Pin 1" … "Pin 7") are
    // left off, but the tile keeps its size — the name stays on the button
    // for screen readers. The rules live in @pz/scene choice-display.ts.
    const display = choiceDisplayOf(config, { compact: small });
    const pictures = offered.length > 0 && offered.every((v) => Boolean(v.thumbnailUrl));
    const bare = pictures && !showsCaptions(display, offered.map((v) => captionOf(v.label)));
    // A lead every caption repeats ("You are my Missing Piece - ") is dropped,
    // so the tile shows what tells the choices apart; the tooltip keeps all.
    const captions = captionsOf(offered.map((v) => captionOf(v.label)));
    if (pictures) {
      group.classList.add('pz-swatches--tiles');
      group.style.setProperty('--pz-tile-scale', String(CHOICE_SIZE_SCALE[display.pictureSize]));
    }
    if (bare) group.classList.add('pz-swatches--bare');
    // Font choices are drawn in their own font, with the shopper's words in
    // each (@pz/scene choiceFontId / fontChoiceSample). Their names follow the
    // same caption rule: "Font 1" … "Font 5" only count, so they are left off.
    const fontOf = offered.map((v) => this.choiceFont(option, v));
    const fontSource = fontOf.some(Boolean) ? this.fontSampleSource(option) : null;
    const fontCaptions = showsCaptions(display, offered.map((v) => captionOf(v.label)));
    const samples: Array<{ sample: HTMLElement; caption: HTMLElement | null; fontId: string; name: string }> = [];
    if (fontSource) group.classList.add('pz-swatches--fonts');
    const picked = new Set<string>();
    const paint = (): void => {
      for (const el of group.querySelectorAll<HTMLElement>('.pz-swatch')) {
        const on = picked.has(el.dataset.value ?? '');
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-checked', String(on));
      }
    };
    for (const [index, value] of offered.entries()) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pz-swatch';
          button.dataset.value = value.value;
          button.title = this.tooltipFor(value);
          button.setAttribute('role', multiple ? 'checkbox' : 'radio');
          button.setAttribute('aria-checked', 'false');
          // Customily's per-value "Disabled": the choice stays visible — that it
          // exists at all is information — but it cannot be picked.
          if (value.meta?.disabled === true) {
            button.disabled = true;
            button.classList.add('is-disabled');
            button.setAttribute('aria-disabled', 'true');
          }
          const swatchColour = swatchColorOf(value);
          const fontId = fontOf[index];
          if (!value.thumbnailUrl && fontId && fontSource) {
            // A font choice: the shopper's words in that font, so the typeface
            // is seen while picking rather than tried out on the preview. The
            // sample is decoration; the button is named by the choice.
            button.classList.add('pz-swatch--font');
            const sample = document.createElement('span');
            sample.className = 'pz-swatch-sample';
            sample.setAttribute('aria-hidden', 'true');
            if (fontSource.uppercase) sample.style.textTransform = 'uppercase';
            const name = document.createElement('span');
            name.className = fontCaptions ? 'pz-swatch-label' : 'pz-sr';
            name.textContent = (fontCaptions ? captions[index] : '') || captionOf(value.label) || value.label;
            button.append(sample, name);
            samples.push({
              sample,
              caption: fontCaptions ? name : null,
              fontId,
              name: captionOf(value.label) || value.label,
            });
          } else if (!value.thumbnailUrl && swatchColour) {
            // A colour swatch. Its colour is the choice — showing the word "Zlatna"
            // on a blank button asks the shopper to imagine it.
            button.classList.add('pz-swatch--color');
            const dot = document.createElement('span');
            dot.className = 'pz-swatch-dot';
            dot.style.background = swatchColour;
            button.appendChild(dot);
            const name = document.createElement('span');
            name.className = 'pz-swatch-name';
            name.textContent = value.label;
            button.appendChild(name);
          } else if (value.thumbnailUrl) {
            // WAPF-style picture choices: a big image with its name and price
            // underneath, so the choice reads at a glance.
            button.classList.add('pz-swatch--img');
            // The picture sits in a frame with a backdrop of its own, so white
            // art on a transparent ground (a white pin) does not vanish into
            // the white tile, nor black lettering into a black one. The frame
            // learns what the art needs once its pixels are read. The choice's
            // own colour (bgColor) is what it stands for — the title's ink —
            // and is never painted behind it.
            const frame = document.createElement('span');
            frame.className = 'pz-swatch-pic';
            if (display.pictureFit === 'whole') frame.classList.add('is-whole');
            const image = document.createElement('img');
            // Read once the picture has arrived: the pixel probe is then
            // answered from the browser's cache rather than a second download.
            image.addEventListener(
              'load',
              () => applyTileArt(frame, image, { backdrop: display.pictureBackdrop, fit: display.pictureFit }),
              { once: true },
            );
            image.src = value.thumbnailUrl;
            image.alt = value.label;
            frame.appendChild(image);
            button.appendChild(frame);
            if (!bare) {
              const caption = document.createElement('span');
              caption.className = 'pz-swatch-label';
              // The price gets its own line; strip it from a label that inlines it.
              caption.textContent = captions[index] || captionOf(value.label);
              button.appendChild(caption);
            }
          } else {
            // Text Swatch: the plain-text pill, the leanest of the family.
            button.classList.add('pz-swatch--text');
            const caption = document.createElement('span');
            caption.className = 'pz-swatch-label';
            caption.textContent = value.label.replace(/\s*\([+-][^)]*\)\s*/g, '').trim() || value.label;
            button.appendChild(caption);
          }
          // The price rides under every shape of swatch, not only picture
          // tiles, and a discount reads as a discount rather than as "+-400".
          if (value.priceDelta !== 0) {
            const price = document.createElement('span');
            price.className = 'pz-swatch-price';
            price.textContent = `${priceSign(value.priceDelta)}${this.options.formatPrice(Math.abs(value.priceDelta))}`;
            button.appendChild(price);
          }
          button.addEventListener('click', () => {
            if (value.meta?.disabled === true) return;
            if (multiple) {
              if (picked.has(value.value)) picked.delete(value.value);
              else picked.add(value.value);
              paint();
              this.set(option.id, picked.size > 0 ? [...picked] : undefined);
              return;
            }
            picked.clear();
            picked.add(value.value);
            paint();
            this.set(option.id, value.value);
          });
          group.appendChild(button);
    }
    this.setters.set(option.id, (value) => {
      picked.clear();
      for (const one of Array.isArray(value) ? value : [value]) picked.add(String(one));
      paint();
    });
    if (fontSource) {
      const twins = this.options.product.fontTwins;
      const repaint = (): void => {
        const text = fontSource.text();
        for (const one of samples) {
          const words = fontChoiceSample(text, one.name);
          // In the font's Serbian twin when the words need it, as the print is.
          const drawn = drawnFontId(one.fontId, words, twins);
          one.sample.textContent = words;
          one.sample.style.fontFamily = `"pz-font-${drawn}", sans-serif`;
          // A long name is set a little smaller, so more of it fits the tile.
          one.sample.classList.toggle('is-long', [...words].length > 12);
          void this.ensureFontFace(drawn);
          // While the sample IS the name, the name under it says nothing more
          // on screen; it stays on the button for screen readers.
          one.caption?.classList.toggle('pz-sr', words === one.name);
        }
      };
      repaint();
      for (const id of fontSource.watch) this.watch(id, repaint);
      this.fontSampleRepaints.push(repaint);
    }
    return group;
  }

  /**
   * Several clipart from one collection — Customily's multi-image control.
   *
   * The shopper taps pictures to add them (up to the author's limit) and the
   * chosen ones land on the design's slots in order, where the movable-layer
   * overlay lets them be dragged about. The answer is the list of asset ids.
   */
  private buildClipartMulti(option: OptionDef): HTMLElement {
    const config = option.config ?? {};
    const libraryId = typeof config.libraryId === 'string' ? config.libraryId : '';
    const max = Math.max(1, Math.min(50, Number(config.max ?? 5) || 5));
    const pictures = this.options.product.libraries?.[libraryId] ?? [];
    // A multi-pick grid is the compact size unless the question says otherwise.
    const display = choiceDisplayOf(config, { compact: true });
    const art = { backdrop: display.pictureBackdrop, fit: display.pictureFit };
    const wrap = document.createElement('div');
    wrap.className = 'pz-multi';

    const chosen = document.createElement('div');
    chosen.className = 'pz-multi-chosen';
    const counter = document.createElement('span');
    counter.className = 'pz-hint';
    const grid = document.createElement('div');
    grid.className = 'pz-multi-grid';
    grid.style.setProperty('--pz-tile-scale', String(CHOICE_SIZE_SCALE[display.pictureSize]));

    let picks: string[] = [];
    const paint = (): void => {
      replaceKids(chosen);
      for (const [i, id] of picks.entries()) {
        const picture = pictures.find((p) => p.id === id);
        const item = document.createElement('span');
        item.className = 'pz-multi-item';
        if (picture) {
          const img = document.createElement('img');
          img.addEventListener('load', () => applyTileArt(item, img, art), { once: true });
          img.src = picture.url;
          img.alt = picture.name;
          item.appendChild(img);
        }
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'x';
        remove.title = 'Ukloni';
        remove.textContent = '✕';
        remove.addEventListener('click', () => {
          picks = picks.filter((_, j) => j !== i);
          paint();
          this.set(option.id, picks.length ? [...picks] : undefined);
        });
        item.appendChild(remove);
        chosen.appendChild(item);
      }
      counter.textContent = `Izabrano ${picks.length} od ${max}`;
      grid.classList.toggle('is-full', picks.length >= max);
    };

    for (const picture of pictures) {
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'pz-multi-cell';
      cell.title = picture.name || 'Dodaj';
      const img = document.createElement('img');
      // White clipart on the white cell needs the same backdrop as a pin tile.
      img.addEventListener('load', () => applyTileArt(cell, img, art), { once: true });
      img.src = picture.url;
      img.alt = picture.name;
      img.loading = 'lazy';
      cell.appendChild(img);
      cell.addEventListener('click', () => {
        if (picks.length >= max) return;
        picks = [...picks, picture.id];
        paint();
        this.set(option.id, [...picks]);
      });
      grid.appendChild(cell);
    }
    if (pictures.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'pz-hint';
      empty.textContent = 'Kolekcija je prazna.';
      grid.appendChild(empty);
    }

    this.setters.set(option.id, (value) => {
      picks = Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
      paint();
    });

    paint();
    wrap.append(chosen, counter, grid);
    return wrap;
  }

  /**
   * A picture from the shopper's words — Customily's text-to-image. The
   * server owns the prompt; the shopper supplies only the subject. The result
   * is an ordinary photo answer, so framing, cut-outs and printing all work
   * unchanged.
   */
  private buildAiImage(option: OptionDef): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'pz-imagine';
    const row = document.createElement('div');
    row.className = 'pz-range';
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'pz-input';
    input.style.flex = '1';
    input.placeholder = typeof option.config?.placeholder === 'string' ? option.config.placeholder : 'npr. pas u svemirskom odelu';
    input.maxLength = 200;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pz-btn';
    button.style.padding = '9px 16px';
    button.textContent = 'Nacrtaj';
    row.append(input, button);

    const card = document.createElement('div');
    card.className = 'pz-file-card';
    card.hidden = true;
    const thumb = document.createElement('img');
    thumb.alt = '';
    const name = document.createElement('span');
    name.className = 'name';
    const remove = removeButton(UPLOAD_TEXTS.remove);
    remove.addEventListener('click', () => {
      card.hidden = true;
      status.textContent = '';
      this.set(option.id, undefined);
    });
    card.append(fileThumb(thumb), name, remove);

    const status = document.createElement('p');
    status.className = 'pz-hint';

    const run = async (): Promise<void> => {
      const text = input.value.trim();
      if (text.length < 2) {
        status.textContent = 'Opiši šta želiš na slici.';
        return;
      }
      button.disabled = true;
      status.textContent = 'Crtam… (do 30 s)';
      try {
        const product = this.options.product;
        const result = await this.options.api.imagine({
          productId: product.productId === 'preview' ? undefined : product.productId,
          templateId: product.templateId,
          optionId: option.id,
          text,
        });
        thumb.src = result.url;
        name.textContent = text;
        card.hidden = false;
        button.textContent = 'Nacrtaj ponovo';
        status.textContent = '';
        this.options.onAsset?.(result.assetId, result.url, option.id);
        this.set(option.id, result.assetId);
      } catch (err) {
        status.textContent = err instanceof Error ? err.message : 'Crtanje nije uspelo.';
      } finally {
        button.disabled = false;
      }
    };
    button.addEventListener('click', () => void run());
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void run();
      }
    });

    this.setters.set(option.id, (value) => {
      const assetId = typeof value === 'string' ? value : (value as { assetId?: string } | undefined)?.assetId;
      if (!assetId) return;
      const url = this.options.resolveAssetUrl?.(assetId);
      if (url) thumb.src = url;
      name.textContent = 'Sačuvana slika';
      card.hidden = false;
      button.textContent = 'Nacrtaj ponovo';
    });

    wrap.append(row, card, status);
    return wrap;
  }

  /**
   * The song picker.
   *
   * A read-only field that opens a modal, search as you type inside it, and one
   * tap on a row fills the field with "Perfect, Ed Sheeran" — the flow every
   * shopper who has bought a song plaque before has already been trained on by
   * the shops we are measured against. The whole track travels as the answer, so
   * the code layer, the title, the artist and the sleeve all fill from one pick.
   *
   * The form deliberately shows no artwork and no player: the album cover belongs
   * on the product preview, where the shopper is deciding whether to buy it, and
   * a play button is a promise Spotify no longer keeps for apps registered after
   * November 2024 — an inert control reads as a broken product.
   *
   * The search runs through our API on the shop's own Spotify credentials, so the
   * storefront never carries them.
   */
  private buildSpotifySearch(option: OptionDef): HTMLElement {
    const config = option.config ?? {};
    const wrap = document.createElement('div');
    wrap.className = 'pz-song';

    // A text input rather than a button, because once a song is picked this has
    // to read as the answer; read-only because only a real Spotify track will do,
    // and a field that accepts typing promises that free text is an answer.
    const field = document.createElement('input');
    field.type = 'text';
    field.className = 'pz-input pz-spotify-field';
    field.readOnly = true;
    field.placeholder =
      typeof config.placeholder === 'string' && config.placeholder
        ? config.placeholder
        : SONG_TEXTS.fieldPlaceholder;
    field.setAttribute('aria-haspopup', 'dialog');
    wrap.appendChild(field);
    // The merchant's help is about picking, so it belongs under the field —
    // not under the card of lines that only appears once a song is picked.
    if (option.helpText) {
      const help = document.createElement('p');
      help.className = 'pz-hint';
      help.textContent = option.helpText;
      wrap.appendChild(help);
    }

    /** The shop has no Spotify credentials: explain instead of failing with a 502. */
    const configured = this.options.product.spotify?.configured !== false;
    const apiBase = this.options.api.baseUrl.replace(/\/+$/, '');

    // ── The picked song, as a card ──────────────────────────────────────────
    //
    // Once a song is picked the field gives way to what the shopper actually
    // chose, in one short row: the sleeve, the title, the artist, a play button
    // and two small links to change or remove it — short, so the lines of text
    // below stay on a phone's first screen. Which album and how long show only
    // where there is room. The code is not repeated here: the preview right
    // beside the card already shows it, as it will print. The play button opens Spotify's own embedded player for that one song, on
    // demand: Spotify's player is the sanctioned way to let someone listen, and
    // nothing of Spotify's loads on the page until it is asked for. The Spotify
    // logo beside the artist is the link back Spotify requires next to its
    // metadata and art.
    const card = document.createElement('div');
    card.className = 'pz-song-card';
    card.hidden = true;
    card.setAttribute('role', 'group');
    card.setAttribute('aria-label', SONG_TEXTS.cardLabel);
    const cover = document.createElement('img');
    cover.className = 'pz-song-card-cover';
    cover.alt = '';
    const body = document.createElement('div');
    body.className = 'pz-song-card-body';
    const cardTitle = document.createElement('b');
    cardTitle.className = 'pz-song-card-title';
    const cardArtist = document.createElement('span');
    cardArtist.className = 'pz-song-card-artist';
    const cardMeta = document.createElement('small');
    cardMeta.className = 'pz-song-card-meta';
    const actions = document.createElement('div');
    actions.className = 'pz-song-card-actions';
    const changeButton = document.createElement('button');
    changeButton.type = 'button';
    changeButton.className = 'pz-song-act pz-song-change';
    changeButton.textContent = SONG_TEXTS.change;
    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'pz-song-act pz-song-remove';
    removeButton.textContent = SONG_TEXTS.remove;
    actions.append(changeButton, removeButton);
    // A round play button; its words stay in the DOM for a screen reader.
    const listenButton = document.createElement('button');
    listenButton.type = 'button';
    listenButton.className = 'pz-song-listen';
    listenButton.textContent = SONG_TEXTS.listen;
    listenButton.title = SONG_TEXTS.listen;
    listenButton.setAttribute('aria-expanded', 'false');
    const spotifyLink = document.createElement('a') as HTMLAnchorElement;
    spotifyLink.className = 'pz-song-card-spotify';
    spotifyLink.textContent = SONG_TEXTS.openOnSpotify;
    spotifyLink.title = SONG_TEXTS.openOnSpotify;
    spotifyLink.setAttribute('target', '_blank');
    spotifyLink.setAttribute('rel', 'noopener noreferrer');
    const artistLine = document.createElement('span');
    artistLine.className = 'pz-song-card-by';
    artistLine.append(spotifyLink, cardArtist);
    body.append(cardTitle, artistLine, cardMeta, actions);
    const embed = document.createElement('div');
    embed.className = 'pz-song-embed';
    embed.hidden = true;
    card.append(cover, body, listenButton, embed);
    wrap.appendChild(card);

    // A product with no sound of its own says so under the card: the code is
    // printed, the product itself stays silent. A shop can word it per option.
    const hasSound = this.options.product.options.some((o) =>
      ['audio_source', 'voice_record', 'video_source'].includes(o.type));
    const noteText =
      typeof config.songNote === 'string' ? config.songNote.trim() : hasSound ? '' : SONG_TEXTS.noSound;
    const note = document.createElement('p');
    note.className = 'pz-hint pz-song-note';
    note.textContent = noteText;
    note.hidden = true;
    if (noteText) wrap.appendChild(note);

    /** The song whose card is showing. */
    let shown: Partial<SpotifyTrackResult> | undefined;

    const closePlayer = (): void => {
      // Removing the iframe is what stops the music; hiding it would not.
      embed.innerHTML = '';
      embed.hidden = true;
      listenButton.textContent = SONG_TEXTS.listen;
      listenButton.title = SONG_TEXTS.listen;
      listenButton.setAttribute('aria-expanded', 'false');
    };
    this.mediaStops.add(closePlayer);

    const showCard = (track: Partial<SpotifyTrackResult> | undefined): void => {
      closePlayer();
      shown = track && typeof track.uri === 'string' ? track : undefined;
      card.hidden = !shown;
      field.hidden = Boolean(shown);
      note.hidden = !shown || !noteText;
      if (!shown) return;
      // No sleeve: an empty tile keeps the card's shape, never a broken image.
      const art = shown.albumArt ?? shown.thumbnail ?? shown.imageUrl ?? '';
      if (art) cover.src = art;
      else cover.removeAttribute?.('src');
      cardTitle.textContent = shown.name ?? '';
      cardArtist.textContent = shown.artists ?? shown.artist ?? '';
      cardMeta.textContent = songMeta(shown);
      const id = shown.id || shown.uri!.split(':').pop() || '';
      spotifyLink.href = shown.link || `https://open.spotify.com/track/${id}`;
      listenButton.hidden = !/^[A-Za-z0-9]+$/.test(id);
    };

    listenButton.addEventListener('click', () => {
      if (!embed.hidden) {
        closePlayer();
        return;
      }
      const id = shown?.id || shown?.uri?.split(':').pop() || '';
      if (!/^[A-Za-z0-9]+$/.test(id)) return;
      const frame = document.createElement('iframe');
      frame.className = 'pz-song-embed-frame';
      frame.src = `https://open.spotify.com/embed/track/${id}?utm_source=generator`;
      frame.setAttribute('title', `Spotify: ${shown?.name ?? ''}`);
      frame.setAttribute('allow', 'autoplay; clipboard-write; encrypted-media; picture-in-picture');
      frame.setAttribute('loading', 'lazy');
      embed.innerHTML = '';
      embed.appendChild(frame);
      embed.hidden = false;
      listenButton.textContent = SONG_TEXTS.hideListen;
      listenButton.title = SONG_TEXTS.hideListen;
      listenButton.setAttribute('aria-expanded', 'true');
    });

    let layer: LayerHandle | undefined;
    let modal: HTMLElement | undefined;
    let searchInput: HTMLInputElement | undefined;
    let results: HTMLElement | undefined;
    let status: HTMLParagraphElement | undefined;
    let debounce: number | undefined;
    let sequence = 0;
    /** The song already picked, so reopening the modal marks its row. */
    let pickedUri: string | undefined;
    /** What the current pick offers the lines below the field. */
    let pickedWords: { title: string; artists: string } | undefined;

    // ── What the product will say, editable in place ────────────────────────
    //
    // The pick fills these in as real answers rather than as placeholders: the
    // shopper reads the words that will be printed and can change any of them
    // right here, with the preview following each keystroke. Their own rows are
    // hidden — this IS the question — so the form stays one card per decision.
    const lines: Array<{
      option: OptionDef;
      role: 'title' | 'artist';
      input: HTMLInputElement;
      /** What the pick last wrote here, so an edit of the shopper's is recognisable. */
      lastAuto: string;
    }> = [];
    const panel = document.createElement('div');
    panel.className = 'pz-song-text';
    panel.hidden = true;
    const heading = document.createElement('div');
    heading.className = 'pz-song-text-title';
    heading.textContent = SONG_TEXTS.textHeading;
    panel.appendChild(heading);
    const resetButton = document.createElement('button');
    resetButton.type = 'button';
    resetButton.className = 'pz-btn-ghost pz-song-reset';
    resetButton.textContent = SONG_TEXTS.reset;
    resetButton.hidden = true;

    /** The card shows once a song exists; the reset only once a line differs from it. */
    const refreshCard = (): void => {
      panel.hidden = lines.length === 0 || !pickedUri;
      resetButton.hidden =
        panel.hidden || !lines.some((line) => line.input.value !== line.lastAuto);
    };

    /**
     * Writes the picked song's words into the lines.
     *
     * `auto` (a fresh pick) leaves a line the shopper typed in alone — changing
     * the song must not throw away "Naša pesma". `empty` (a cart restore) leaves
     * anything already there, because the saved answer is the shopper's own.
     * `all` is the reset button.
     */
    const fillLines = (mode: 'auto' | 'empty' | 'all'): void => {
      for (const line of lines) {
        const words = (line.role === 'title' ? pickedWords?.title : pickedWords?.artists) ?? '';
        const was = line.lastAuto;
        line.lastAuto = words;
        if (!words) continue;
        const takes =
          mode === 'all'
            ? true
            : mode === 'empty'
              ? line.input.value === ''
              : line.input.value === '' || line.input.value === was;
        if (!takes) continue;
        line.input.value = words;
        this.set(line.option.id, words);
      }
      refreshCard();
    };

    for (const absorbed of this.songLines.get(option.id) ?? []) {
      const miniRow = document.createElement('div');
      miniRow.className = 'pz-song-line';
      // The question this line asks, in the DOM: whatever points at a field —
      // the shopper guide, the focus highlight — walks up to the nearest one.
      if (miniRow.dataset) {
        miniRow.dataset.option = absorbed.option.id;
        miniRow.dataset.type = 'text';
      }
      const lineLabel = document.createElement('label');
      lineLabel.className = 'pz-label pz-song-line-label';
      lineLabel.textContent = absorbed.option.label;
      miniRow.appendChild(lineLabel);
      // Built the ordinary way, so the length limit, the counter, the input
      // restrictions and the restore setter all keep working.
      const input = this.buildControl(absorbed.option, miniRow) as HTMLInputElement;
      miniRow.appendChild(input);
      panel.appendChild(miniRow);
      const line = { option: absorbed.option, role: absorbed.role, input, lastAuto: '' };
      lines.push(line);
      input.addEventListener('input', () => refreshCard());
      // A required line left empty takes the song's words back: empty prints
      // them anyway (the pick fills the layer), and an empty required answer
      // would only bounce the order.
      input.addEventListener('change', () => {
        if (!absorbed.option.required || input.value.trim() !== '' || !line.lastAuto) return;
        input.value = line.lastAuto;
        this.set(absorbed.option.id, line.lastAuto);
        refreshCard();
      });
      // A restore writes through the control's own setter, and the setters run
      // in no guaranteed order — the card has to notice whichever lands last.
      const restore = this.setters.get(absorbed.option.id);
      this.setters.set(absorbed.option.id, (value) => {
        restore?.(value);
        refreshCard();
      });
    }

    if (lines.length > 0) {
      const foot = document.createElement('div');
      foot.className = 'pz-song-text-foot';
      const hint = document.createElement('p');
      hint.className = 'pz-hint';
      hint.textContent = SONG_TEXTS.prefilled;
      resetButton.addEventListener('click', () => fillLines('all'));
      foot.append(hint, resetButton);
      panel.appendChild(foot);
      wrap.appendChild(panel);
    }

    /** Marks the picked row, whenever the list or the pick changes. */
    const markPicked = (): void => {
      for (const row of Array.from(
        results?.querySelectorAll<HTMLElement>('.pz-spotify-row') ?? [],
      )) {
        row.classList.toggle('is-active', row.getAttribute('data-uri') === pickedUri);
      }
    };

    /** Placeholder rows while Spotify answers, so the list does not jump. */
    const showSkeleton = (): void => {
      if (!results) return;
      results.innerHTML = '';
      for (let i = 0; i < SONG_SKELETON_ROWS; i++) {
        const row = document.createElement('div');
        row.className = 'pz-track pz-track--ghost';
        const art = document.createElement('span');
        art.className = 'pz-ghost-art';
        const lines = document.createElement('span');
        lines.className = 'pz-ghost-lines';
        lines.append(document.createElement('i'), document.createElement('i'));
        row.append(art, lines);
        results.appendChild(row);
      }
    };

    /** Clears the list and says one thing instead. */
    const showMessage = (text: string): void => {
      if (!results || !status) return;
      results.innerHTML = '';
      status.textContent = text;
    };

    const pick = (track: SpotifyTrackResult): void => {
      pickedUri = track.uri;
      const label = songLabel(track.name, track.artists);
      // The whole track travels as the answer: the code layer takes the URI, the
      // title and artist layers take the text, the sleeve slot takes the artwork.
      // Sending only the URI is why those layers used to print blank.
      this.set(option.id, {
        uri: track.uri,
        id: track.id,
        name: track.name,
        artists: track.artists,
        artist: track.artist,
        album: track.album,
        albumArt: track.albumArt,
        thumbnail: trackThumb(track),
        year: track.year,
        durationMs: track.durationMs,
        previewUrl: track.previewUrl,
        link: track.link,
        label,
      });
      field.value = label;
      pickedWords = trackWords(track) ?? pickedWords;
      fillLines('auto');
      markPicked();
      rememberSong(track);
      showCard(track);
      closeModal();
    };

    changeButton.addEventListener('click', () => openModal());

    // Removing the song takes back what the pick wrote into the lines, and only
    // that: words the shopper typed themselves stay theirs.
    removeButton.addEventListener('click', () => {
      for (const line of lines) {
        if (line.input.value !== '' && line.input.value === line.lastAuto) {
          line.input.value = '';
          this.set(line.option.id, '');
        }
        line.lastAuto = '';
      }
      pickedUri = undefined;
      pickedWords = undefined;
      field.value = '';
      this.set(option.id, undefined);
      showCard(undefined);
      refreshCard();
      markPicked();
      field.focus?.();
    });

    /**
     * What the dialog shows before a search: the songs picked on this device,
     * then the shop's own "Popularno" list — so the common case (the song
     * everybody picks for a wedding) is one tap, not a search.
     */
    const suggestions = (this.options.product.spotify?.suggestions ?? []).filter(
      (t): t is SpotifyTrackResult & Record<string, unknown> =>
        Boolean(t && typeof t.uri === 'string' && typeof t.name === 'string'),
    ) as SpotifyTrackResult[];
    const showSuggestions = (): void => {
      if (!results || !status) return;
      results.innerHTML = '';
      status.textContent = '';
      if (!configured) return;
      const recent = readRecentSongs();
      const recentUris = new Set(recent.map((t) => t.uri));
      const sections: Array<[string, SpotifyTrackResult[]]> = [
        [SONG_TEXTS.recent, recent],
        [SONG_TEXTS.popular, suggestions.filter((t) => !recentUris.has(t.uri))],
      ];
      for (const [title, tracks] of sections) {
        if (tracks.length === 0) continue;
        const heading = document.createElement('div');
        heading.className = 'pz-spotify-heading';
        heading.textContent = title;
        results.appendChild(heading);
        for (const track of tracks) results.appendChild(this.songRow(track, pick, pickedUri));
      }
    };

    const run = async (query: string): Promise<void> => {
      if (!configured) {
        showMessage(SONG_TEXTS.unconfigured);
        return;
      }
      const seq = ++sequence;
      showSkeleton();
      if (status) status.textContent = '';
      try {
        const response = await fetch(
          `${apiBase}/v1/storefront/spotify/search?q=${encodeURIComponent(query)}&limit=${SONG_RESULT_COUNT}`,
        );
        const body = (await response.json().catch(() => ({}))) as {
          tracks?: SpotifyTrackResult[];
          code?: string;
        };
        // A slower earlier request must not overwrite a newer answer.
        if (seq !== sequence) return;
        if (!response.ok) {
          showMessage(songErrorText(body.code));
          return;
        }
        if (!results || !status) return;
        results.innerHTML = '';
        status.textContent = body.tracks?.length ? '' : SONG_TEXTS.empty;
        for (const track of body.tracks ?? []) {
          results.appendChild(this.songRow(track, pick, pickedUri));
        }
      } catch {
        // The network itself failed (offline, blocked): the same sentence as
        // any other failure; whatever the browser said is not for the shopper.
        if (seq !== sequence) return;
        showMessage(SONG_TEXTS.failed);
      }
    };

    /** Searching as the shopper types is the whole difference from a form field. */
    const onType = (): void => {
      window.clearTimeout(debounce);
      const query = searchInput?.value.trim() ?? '';
      if (query.length < SONG_MIN_QUERY) {
        // An answer to an abandoned query must not land in an emptied list.
        sequence++;
        // Back to the suggestions the dialog opened with, not to an empty box.
        showSuggestions();
        return;
      }
      debounce = window.setTimeout(() => void run(query), SONG_DEBOUNCE_MS);
    };

    /** Built on first open: a form with six song fields carries no hidden dialogs. */
    const buildModal = (): void => {
      if (modal) return;

      const card = document.createElement('div');
      card.className = 'pz-spotify-card';

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'pz-spotify-close';
      close.textContent = '✕';
      close.setAttribute('aria-label', SONG_TEXTS.close);
      close.addEventListener('click', () => closeModal());

      searchInput = document.createElement('input');
      searchInput.type = 'search';
      searchInput.className = 'pz-input pz-spotify-search';
      searchInput.placeholder = SONG_TEXTS.searchPlaceholder;
      searchInput.autocomplete = 'off';
      searchInput.setAttribute('aria-label', SONG_TEXTS.searchPlaceholder);
      searchInput.addEventListener('input', onType);
      // Enter searches at once: waiting out the debounce after a deliberate
      // Enter reads as a dead key.
      searchInput.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        window.clearTimeout(debounce);
        const query = searchInput?.value.trim() ?? '';
        if (query.length >= SONG_MIN_QUERY) void run(query);
      });

      results = document.createElement('div');
      results.className = 'pz-spotify-results';
      status = document.createElement('p');
      status.className = 'pz-hint pz-spotify-status';

      card.append(close, searchInput, results, status);

      // Escape, the focus trap, the dimmed backdrop, the page's scroll lock and
      // the keyboard-aware height all come from the layer, which puts the
      // dialog in the browser's top layer where no theme wrapper can clip it.
      layer = createLayer({
        host: this.layerHost(),
        className: 'pz-spotify-modal',
        content: card,
        label: option.label || SONG_TEXTS.dialogLabel,
        onClose: () => {
          window.clearTimeout(debounce);
          // Focus goes back where it came from, or a keyboard shopper is left
          // at the top of the page. The layer restores it too; doing it by name
          // also covers the fallback path and a focus that had wandered. With a
          // song picked the field is hidden behind the card, whose "Promeni" is
          // the same control.
          if (field.hidden) changeButton.focus?.();
          else field.focus?.();
        },
      });
      modal = layer.element;
    };

    const openModal = (): void => {
      buildModal();
      if (!modal || !searchInput || !layer) return;
      layer.open();
      // The modal keeps its list and its query between openings, so "not that
      // one, the other one" costs a tap rather than retyping the search.
      searchInput.focus();
      searchInput.select();
      // First open, or reopened after clearing the search: offer the
      // suggestions instead of an empty dialog.
      if (searchInput.value.trim().length < SONG_MIN_QUERY) showSuggestions();
      markPicked();
      if (!configured) showMessage(SONG_TEXTS.unconfigured);
    };

    const closeModal = (): void => {
      layer?.close();
    };

    field.addEventListener('click', () => openModal());
    field.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      openModal();
    });

    // A restored answer (editing from the cart) puts the song back in the field.
    this.setters.set(option.id, (value) => {
      const track = value as (Partial<SpotifyTrackResult> & { label?: string }) | undefined;
      if (!track || typeof track !== 'object' || typeof track.uri !== 'string') return;
      pickedUri = track.uri;
      field.value =
        typeof track.label === 'string' && track.label
          ? track.label
          : songLabel(track.name ?? '', track.artists ?? '');
      // Only the lines the shopper left empty: a line saved with words of their
      // own is restored by its own setter, and must win whichever runs first.
      pickedWords = trackWords(track) ?? pickedWords;
      fillLines('empty');
      markPicked();
      showCard(track);
    });

    return wrap;
  }

  /** One result row; picking it answers the option with the whole track. */
  private songRow(
    track: SpotifyTrackResult,
    onPick: (track: SpotifyTrackResult) => void,
    pickedUri?: string,
  ): HTMLElement {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = track.uri === pickedUri ? 'pz-spotify-row is-active' : 'pz-spotify-row';
    // What the row carries, readable from the DOM: the same handles Customily's
    // rows expose, which is what a shop's own script or a bug report can point at.
    item.setAttribute('data-track', track.name);
    item.setAttribute('data-artist', track.artists);
    item.setAttribute('data-albumimage', track.albumArt ?? '');
    item.setAttribute('data-link', track.link ?? '');
    item.setAttribute('data-uri', track.uri);

    const thumb = trackThumb(track);
    if (thumb) {
      const art = document.createElement('img');
      art.className = 'pz-spotify-thumb';
      art.src = thumb;
      art.alt = '';
      art.loading = 'lazy';
      item.appendChild(art);
    }
    const text = document.createElement('span');
    text.className = 'pz-spotify-meta';
    const name = document.createElement('b');
    name.className = 'pz-spotify-name';
    name.textContent = track.name;
    const by = document.createElement('small');
    by.className = 'pz-spotify-artist';
    by.textContent = track.artists;
    text.append(name, by);
    // Album · year · length: what tells the album cut from the single and the
    // "Best of" reissue when all three carry the same title and artist.
    const meta = songMeta(track);
    if (meta) {
      const line = document.createElement('span');
      line.className = 'pz-spotify-album';
      line.textContent = meta;
      text.appendChild(line);
    }
    item.appendChild(text);
    const tag =
      track.albumType === 'single' ? SONG_TEXTS.single
        : track.albumType === 'compilation' ? SONG_TEXTS.compilation
          : '';
    if (tag || track.explicit) {
      const tags = document.createElement('span');
      tags.className = 'pz-spotify-tags';
      if (track.explicit) {
        const e = document.createElement('span');
        e.className = 'pz-spotify-tag pz-spotify-explicit';
        e.textContent = 'E';
        e.setAttribute('title', SONG_TEXTS.explicit);
        e.setAttribute('aria-label', SONG_TEXTS.explicit);
        tags.appendChild(e);
      }
      if (tag) {
        const t = document.createElement('span');
        t.className = 'pz-spotify-tag';
        t.textContent = tag;
        tags.appendChild(t);
      }
      item.appendChild(tags);
    }

    item.addEventListener('click', () => onPick(track));
    return item;
  }

  /**
   * The place picker.
   *
   * Customily's flow, one for one: a read-only field that opens a dialog, a
   * geocoder at the top of it suggesting places while the shopper types, the map
   * itself underneath — dragged and zoomed by hand, drawn in the style the
   * shopper picked — and CANCEL / SELECT along the bottom. A design with a pin
   * shows one on the map, as any web map does: a search drops it on the place,
   * it stays on that place through pans and zooms, and dragging it corrects it.
   *
   * The field never takes typing. Only a place with coordinates is an answer,
   * and a field that accepts free text promises otherwise.
   *
   * Geocoding goes through Mapbox when the shop has a token (the same token the
   * tiles already use, so a shop with maps always has one), because OSM has
   * already blocked this project's tile use and its Nominatim policy is the same
   * volunteer infrastructure — a map product cannot depend on it. Nominatim
   * remains only as the no-token fallback.
   */
  private buildLocationSearch(option: OptionDef): HTMLElement {
    const config = option.config ?? {};
    const wrap = document.createElement('div');
    wrap.className = 'pz-map';

    // Read-only for the same reason the song field is: it has to read as the
    // answer once one exists, and only the dialog can produce one.
    const field = document.createElement('input');
    field.type = 'text';
    field.className = 'pz-input pz-map-field';
    field.readOnly = true;
    field.placeholder =
      typeof config.placeholder === 'string' && config.placeholder
        ? config.placeholder
        : MAP_TEXTS.fieldPlaceholder;
    field.setAttribute('aria-haspopup', 'dialog');
    wrap.appendChild(field);

    /** Tiles and geocoding both ride on the shop's own public Mapbox token. */
    const token = this.options.product.mapboxToken ?? '';
    const mapElement = this.mapElementFor(option);
    const mapSide = this.options.product.sides?.find((side) => side.elements.includes(mapElement!));
    const pinForView = () => mapElement && mapSide
      ? mapPickerPin(applyShopperPlacements(mapSide, readPlacements(this.values)).elements, mapElement.id)
      : undefined;
    const pickerPin = () => {
      const pin = pinForView();
      if (!pin) return undefined;
      let assetId = pin.artwork?.assetId;
      for (const binding of this.options.product.bindings) {
        if (binding.elementId !== pin.artwork?.elementId || binding.property !== 'image') continue;
        const answer = this.values[binding.optionId];
        const choice = this.options.product.options.find((o) => o.id === binding.optionId)
          ?.values.find((v) => v.id === binding.optionValueId);
        if (binding.optionValueId && answer !== binding.optionValueId && (!choice || answer !== choice.value)) continue;
        if (typeof binding.transform?.assetId === 'string') assetId = binding.transform.assetId;
      }
      const url = assetId ? this.assetUrlFor(assetId) : undefined;
      return {
        x: pin.x, y: pin.y, label: MAP_TEXTS.pinLabel,
        artwork: url && pin.artwork ? { url, w: pin.artwork.w, h: pin.artwork.h } : undefined,
      };
    };
    const styleOptionId = mapElement ? this.mapStyleOptionFor(mapElement.id) : undefined;
    // Nothing chosen yet: the dialog opens where the template author put the
    // map, and a template with no map at all opens on the world view.
    const start = mapElement ? mapLocationOf(mapElement) : MAP_DEFAULTS;

    /** The answer the dialog's map holds now: its frame centre and zoom. */
    let view = { lat: start.lat, lng: start.lng, zoom: clampMapZoom(start.zoom) };
    /** Set once the shopper zooms by hand; until then a pick lands at street zoom. */
    let zoomedByHand = false;
    /** What the shopper last approved — what CANCEL goes back to. */
    let taken = { ...view, label: '' };

    let layer: LayerHandle | undefined;
    let modal: HTMLElement | undefined;
    /** True only while a SELECT is closing the dialog — every other exit cancels. */
    let approved = false;
    /** Stops the viewport listeners that keep the map's size honest. */
    let stopResize: (() => void) | undefined;
    let search: HTMLInputElement | undefined;
    let list: HTMLElement | undefined;
    let status: HTMLParagraphElement | undefined;
    let stage: HTMLElement | undefined;
    let map: TileMap | undefined;
    let pinSignature = '';
    let engine: TileEngine | undefined;
    let debounce: number | undefined;
    let sequence = 0;

    /** The style the map draws in: a style swatch bound to this same layer wins. */
    const styleName = (): string => {
      const chosen = styleOptionId ? this.values[styleOptionId] : undefined;
      if (typeof chosen === 'string' && chosen.trim()) return chosen.trim();
      const own = mapElement?.config.style;
      return typeof own === 'string' ? own : '';
    };

    /**
     * The tile engine, fetched the first time the dialog opens.
     *
     * A shopper who never opens it never runs it, and a shop whose bundle
     * cannot reach it still gets a working geocoder and a working answer.
     *
     * It comes through the package's `./tile-map` and `./map-styles` subpaths,
     * not its barrel: the barrel drags CanvasKit, opentype and the QR encoder
     * into the storefront bundle (676 kB against 236 kB), for a dialog that
     * only draws raster tiles.
     */
    const loadEngine = async (): Promise<TileEngine | undefined> => {
      if (engine) return engine;
      if (tileEngine) {
        engine = tileEngine;
        return engine;
      }
      try {
        const [tiles, styles] = await Promise.all([
          import('@pz/scene-render/tile-map'),
          import('@pz/scene-render/map-styles'),
        ]);
        engine = {
          createTileMap: tiles.createTileMap,
          canCreateTileMap: tiles.canCreateTileMap,
          resolveMapStyle: styles.resolveMapStyle,
        } as TileEngine;
      } catch {
        engine = undefined;
      }
      return typeof engine?.createTileMap === 'function' ? engine : undefined;
    };

    /** Draws (or re-draws) the map in the dialog, in the current style. */
    const mountMap = async (): Promise<void> => {
      if (!token || !stage) return;
      const loaded = await loadEngine();
      if (!loaded || !stage) return;
      // The engine says for itself whether this environment can host a map;
      // meeting a thrown error would take the dialog down with it.
      if (loaded.canCreateTileMap && !loaded.canCreateTileMap()) return;
      const source = loaded.resolveMapStyle(styleName()).source;
      const pin = pickerPin();
      const signature = JSON.stringify(pin);
      if (map && signature !== pinSignature) {
        map.destroy();
        map = undefined;
      }
      pinSignature = signature;
      if (map) {
        // Reopened: the container had no size while it was hidden, so the map
        // has to measure itself again before it can draw a tile.
        map.setStyle(source);
        map.setView(view.lat, view.lng, view.zoom);
        map.resize();
        return;
      }
      try {
        map = loaded.createTileMap(stage, {
          token,
          source,
          lat: view.lat,
          lng: view.lng,
          zoom: view.zoom,
          // The design's own framing: a Customily map shows the ground of its
          // 500 × 500 frame here, as Customily's own dialog does, at any size.
          viewport: mapViewportOf(mapElement?.config) ?? (pin && mapElement
            ? Math.max(mapElement.transform.w, mapElement.transform.h) : undefined),
          pin,
          // Dragging and pinching are the answer as much as the search box is.
          onChange: (next) => {
            if (next.zoom !== view.zoom) zoomedByHand = true;
            view = { lat: next.lat, lng: next.lng, zoom: next.zoom };
          },
        });
      } catch {
        map = undefined;
      }
    };

    /** The line under the search box when nothing else needs saying. */
    const idleStatus = (): string => (token
      ? (pinForView() ? MAP_TEXTS.pinHint : '')
      : MAP_TEXTS.unconfigured);

    const clearList = (): void => {
      if (!list) return;
      replaceKids(list);
      list.hidden = true;
    };

    /** Moves the map to a picked suggestion and puts its name in the search box. */
    const pick = (place: MapPlace): void => {
      sequence++;
      window.clearTimeout(debounce);
      // Customily lands on a place at street zoom. Only a shopper who zoomed in
      // further themselves keeps that framing — a template's own deep zoom
      // would drop them onto one street with nothing around it to recognise.
      const zoom = zoomedByHand && view.zoom > MAP_PLACE_ZOOM ? view.zoom : MAP_PLACE_ZOOM;
      view = { lat: place.lat, lng: place.lng, zoom: clampMapZoom(zoom) };
      if (search) search.value = place.label;
      clearList();
      // The pin (or the centre) lands on the place; the answer is the frame
      // that prints it there.
      map?.setLocation(view.lat, view.lng, view.zoom);
      if (map) view = map.getView();
    };

    const showPlaces = (places: MapPlace[]): void => {
      if (!list || !status) return;
      replaceKids(list, ...places.map((place) => this.placeRow(place, pick)));
      list.hidden = places.length === 0;
      status.textContent = places.length === 0 ? MAP_TEXTS.empty : idleStatus();
    };

    const run = async (query: string): Promise<void> => {
      const seq = ++sequence;
      try {
        const places = token ? await mapboxPlaces(query, token) : await nominatimPlaces(query);
        // A slower earlier request must not overwrite a newer answer.
        if (seq !== sequence) return;
        showPlaces(places);
      } catch {
        if (seq !== sequence || !status) return;
        clearList();
        status.textContent = MAP_TEXTS.failed;
      }
    };

    /** Suggesting while the shopper types is the whole difference from a form field. */
    const onType = (): void => {
      window.clearTimeout(debounce);
      const query = search?.value.trim() ?? '';
      if (query.length < MAP_MIN_QUERY) {
        // An answer to an abandoned query must not land in an emptied list.
        sequence++;
        clearList();
        if (status) status.textContent = idleStatus();
        return;
      }
      debounce = window.setTimeout(() => void run(query), MAP_DEBOUNCE_MS);
    };

    const close = (): void => {
      layer?.close();
    };

    /** Puts the map back to what the shopper last approved. */
    const revert = (): void => {
      sequence++;
      view = { lat: taken.lat, lng: taken.lng, zoom: taken.zoom };
      if (search) search.value = taken.label;
      clearList();
      if (status) status.textContent = idleStatus();
      map?.setView(view.lat, view.lng, view.zoom);
    };

    /** CANCEL, Escape and the backdrop: the map goes back to what was approved. */
    const cancelOut = (): void => {
      revert();
      close();
    };

    /** SELECT: the map's centre and zoom become the answer, its text the field. */
    const confirm = (): void => {
      const now = map?.getView() ?? view;
      view = { lat: now.lat, lng: now.lng, zoom: clampMapZoom(now.zoom) };
      const typed = search?.value.trim() ?? '';
      // A shopper who only dragged the map or the pin named no place; the
      // pin's coordinates are then what the cart line and the order show.
      const spot = map?.getLocation?.() ?? view;
      const label = typed || coordinateLabel(spot.lat, spot.lng);
      taken = { ...view, label };
      field.value = label;
      if (search) search.value = label;
      clearList();
      this.set(option.id, {
        lat: round5(view.lat),
        lng: round5(view.lng),
        zoom: round3(view.zoom),
        label,
      });
      approved = true;
      close();
    };

    /** Mapbox's own overlay buttons: a step of zoom per press, top right. */
    const zoomButton = (glyph: string, delta: number, name: string): HTMLElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pz-map-zoom';
      button.textContent = glyph;
      button.setAttribute('aria-label', name);
      button.addEventListener('click', () => {
        zoomedByHand = true;
        map?.zoomBy(delta);
        const now = map?.getView();
        view = now ? { lat: now.lat, lng: now.lng, zoom: now.zoom } : { ...view, zoom: clampMapZoom(view.zoom + delta) };
      });
      return button;
    };

    /** Built on first open, then kept: reopening shows the place already picked. */
    const buildModal = (): void => {
      if (modal) return;

      const card = document.createElement('div');
      card.className = 'pz-map-card';

      search = document.createElement('input');
      search.type = 'search';
      search.className = 'pz-input pz-map-search';
      search.placeholder = MAP_TEXTS.searchPlaceholder;
      search.autocomplete = 'off';
      search.value = taken.label;
      search.setAttribute('aria-label', MAP_TEXTS.searchPlaceholder);
      search.addEventListener('input', onType);
      search.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        // Enter searches at once — and never submits the shop's product form.
        event.preventDefault();
        window.clearTimeout(debounce);
        const query = search?.value.trim() ?? '';
        if (query.length >= MAP_MIN_QUERY) void run(query);
      });

      list = document.createElement('div');
      list.className = 'pz-map-suggestions';
      list.hidden = true;

      stage = document.createElement('div');
      stage.className = 'pz-map-canvas';
      const zooms = document.createElement('div');
      zooms.className = 'pz-map-zooms';
      zooms.append(
        zoomButton('+', 1, MAP_TEXTS.zoomIn),
        zoomButton('−', -1, MAP_TEXTS.zoomOut),
      );
      stage.appendChild(zooms);

      status = document.createElement('p');
      status.className = 'pz-hint pz-map-status';
      status.textContent = idleStatus();

      const actions = document.createElement('div');
      actions.className = 'pz-map-actions';
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'pz-btn pz-map-cancel';
      cancel.textContent = MAP_TEXTS.cancel;
      cancel.addEventListener('click', () => cancelOut());
      const select = document.createElement('button');
      select.type = 'button';
      select.className = 'pz-btn pz-map-select';
      select.textContent = MAP_TEXTS.select;
      select.addEventListener('click', () => confirm());
      actions.append(cancel, select);

      // No token: no tiles to draw and no geocoder worth the shopper's time.
      // A grey box would read as a broken map, so the dialog says what is wrong.
      if (!token) stage.hidden = true;

      card.append(search, list, stage, status, actions);

      // Escape, the focus trap, the backdrop, the scroll lock and the
      // keyboard-aware sizing come from the layer; leaving by any of those
      // routes is a cancel, so the map goes back to what was last approved.
      layer = createLayer({
        host: this.layerHost(),
        className: 'pz-map-modal',
        content: card,
        label: option.label || MAP_TEXTS.dialogLabel,
        onClose: () => {
          window.clearTimeout(debounce);
          stopResize?.();
          stopResize = undefined;
          if (!approved) revert();
          approved = false;
          field.focus?.();
        },
      });
      modal = layer.element;
    };

    /**
     * The map has to measure itself again whenever the box it draws in changes
     * size — and it changes size a lot: the dialog is laid out one frame after
     * it opens, the on-screen keyboard shrinks it (the layer republishes
     * `--pz-vvh`, and the sheet's height follows it), and rotating the phone
     * changes everything. Tiles drawn against a stale size show as a map that
     * is cut off or padded with grey.
     */
    const onViewportChange = (): void => map?.resize();

    const watchSize = (): void => {
      if (stopResize) return;
      const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
      window.addEventListener?.('resize', onViewportChange);
      window.addEventListener?.('orientationchange', onViewportChange);
      vv?.addEventListener('resize', onViewportChange);
      stopResize = () => {
        window.removeEventListener?.('resize', onViewportChange);
        window.removeEventListener?.('orientationchange', onViewportChange);
        vv?.removeEventListener('resize', onViewportChange);
      };
    };

    const openModal = (): void => {
      buildModal();
      if (!modal || !layer) return;
      layer.open();
      search?.focus();
      search?.select();
      void mountMap();
      watchSize();
      // The dialog gets its real size only after the browser has laid it out;
      // measuring in the same tick gives the map a zero-width box.
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          if (layer?.isOpen) map?.resize();
        });
      }
    };

    field.addEventListener('click', () => openModal());
    field.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      openModal();
    });

    // The style swatch bound to this same map repaints the dialog's map too, not
    // only the preview behind it — the shopper is choosing the style while
    // looking at the map it applies to.
    if (styleOptionId) {
      this.watch(styleOptionId, () => {
        if (!map || !engine) return;
        map.setStyle(engine.resolveMapStyle(styleName()).source);
      });
    }

    // Edit-from-cart: the kept place comes back into the field and into the
    // dialog's map, so reopening it shows where the shopper had been.
    this.setters.set(option.id, (value) => {
      const place = value as { lat?: unknown; lng?: unknown; zoom?: unknown; label?: unknown } | undefined;
      if (!place || typeof place !== 'object') return;
      const lat = Number(place.lat);
      const lng = Number(place.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const zoom = Number(place.zoom);
      view = { lat, lng, zoom: clampMapZoom(Number.isFinite(zoom) ? zoom : view.zoom) };
      const label =
        typeof place.label === 'string' && place.label ? place.label : coordinateLabel(lat, lng);
      taken = { ...view, label };
      field.value = label;
      if (search) search.value = label;
      map?.setView(view.lat, view.lng, view.zoom);
    });

    return wrap;
  }

  /** One geocoder suggestion: the place in bold, the rest of its address under it. */
  private placeRow(place: MapPlace, onPick: (place: MapPlace) => void): HTMLElement {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'pz-map-suggestion';
    // What the row carries, readable from the DOM — the handles a shop's own
    // script or a bug report can point at.
    item.setAttribute('data-lat', String(place.lat));
    item.setAttribute('data-lng', String(place.lng));
    const name = document.createElement('b');
    name.className = 'pz-map-place';
    name.textContent = place.name;
    item.appendChild(name);
    if (place.detail) {
      const detail = document.createElement('small');
      detail.className = 'pz-map-detail';
      detail.textContent = place.detail;
      item.appendChild(detail);
    }
    item.addEventListener('click', () => onPick(place));
    return item;
  }

  /**
   * The map layer this location question drives.
   *
   * Migrated Customily sets bind the map through 'image', new ones through
   * 'location'; both are accepted here for the same reason the preview and the
   * canvas editor accept both.
   */
  private mapElementFor(option: OptionDef): DeferredElement | undefined {
    const ids = new Set(
      this.options.product.bindings
        .filter(
          (binding) =>
            binding.optionId === option.id &&
            (binding.property === 'location' || binding.property === 'image'),
        )
        .map((binding) => binding.elementId),
    );
    if (ids.size === 0) return undefined;
    for (const side of this.options.product.sides ?? []) {
      for (const element of side.elements ?? []) {
        if (element.type === 'map' && ids.has(element.id)) return element;
      }
    }
    return undefined;
  }

  /** The style swatch, if any, that repaints this same map layer. */
  private mapStyleOptionFor(elementId: string): string | undefined {
    return this.options.product.bindings.find(
      (binding) => binding.property === 'mapStyle' && binding.elementId === elementId,
    )?.optionId;
  }

  /**
   * Fans a multi-file selection out across the form's empty photo slots.
   *
   * The slot the shopper touched is filled first (replacing its photo — that is
   * what dropping onto it means), then empty slots after it in form order, then
   * empty ones before it. Slots already holding photos are never overwritten,
   * and files that do not fit are counted out loud rather than dropped silently.
   */
  private async spillPhotos(
    startOptionId: string,
    files: File[],
    status: HTMLElement,
  ): Promise<void> {
    const slots = this.options.product.options.map((o) => ({
      id: o.id,
      // A fan-out field owns its own slots and fills them by the author's list;
      // it must never also be a landing place for another field's spill.
      isPhoto: o.type === 'image_upload' && targetElementIds(o.config ?? {}).length === 0,
      answered: this.isAnswered(o.id),
      hidden: this.conditionHidden.has(o.id),
    }));
    const targets = planPhotoSpill(slots, startOptionId, files.length);
    const leftover = files.length - targets.length;

    const jobs = targets.map((optionId, i) => ({ optionId, file: files[i]! }));
    // A phone on mobile data: a small window keeps it moving without stalls.
    await runLimited(jobs, 2, async ({ optionId, file }) => {
      const upload = this.photoUploaders.get(optionId);
      if (upload) await upload(file);
    });

    if (leftover > 0) {
      status.textContent = `Primljeno je ${targets.length} od ${files.length} slika — na ovaj proizvod staje ${targets.length}.`;
    }
  }

  /**
   * Where a picked file goes: across this option's own bound placeholders when
   * the author listed them, across the form's empty photo slots otherwise.
   *
   * Only a photo field spills. A vector slot takes one logo — fanning an SVG
   * into the photo slots of a collage would be nonsense — so it uploads the
   * first file and says nothing about the rest.
   */
  private async receive(option: OptionDef, files: File[], status: HTMLElement): Promise<void> {
    if (option.type === 'vector_upload') {
      const first = files[0];
      if (first) await this.photoUploaders.get(option.id)?.(first);
      return;
    }
    await this.spillPhotos(option.id, files, status);
  }

  /**
   * May the shopper reframe the picture this question fills?
   *
   * True unless every element it is bound to is locked against both moving and
   * scaling — the "Lock movement"/"Lock scaling" pair on Customily's Image
   * Behavior panel. Unbound questions (a preview harness, a form built before
   * its bindings) stay framable: silence must not take an ability away.
   */
  private canFrame(optionId: string): boolean {
    const bound = this.options.product.bindings.filter(
      (b) => b.optionId === optionId && b.property === 'image',
    );
    if (bound.length === 0) return true;
    const ids = new Set(bound.map((b) => b.elementId));
    let seen = false;
    for (const side of this.options.product.sides ?? []) {
      for (const element of side.elements ?? []) {
        if (!ids.has(element.id)) continue;
        seen = true;
        const shopper = element.shopper;
        if (!shopper || shopper.move !== false || shopper.scale !== false) return true;
      }
    }
    return !seen;
  }

  /**
   * Customily's "Multiple Upload Image": one button, many placeholders.
   *
   * The option names the Image Placeholder ids it feeds (`config.
   * targetElementIds`), and a multi-file selection lands on them IN THAT ORDER —
   * first file to the first id — rather than in whatever order the form happens
   * to list its questions. That is the whole difference from our own spill: this
   * one is authored, so a 9-photo magnet fills top-left first every time.
   *
   * The answer is an array aligned to `targetElementIds`: index i is the asset
   * for that id, `null` where the shopper left a slot empty.
   */
  private buildMultiUpload(option: OptionDef, targets: string[]): HTMLElement {
    const config = option.config ?? {};
    const wrap = document.createElement('div');
    wrap.className = 'pz-multiupload';

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptFor(option.type);
    input.multiple = true;
    input.hidden = true;

    const drop = uploadButton(config, option.type === 'vector_upload');
    drop.addEventListener('click', () => input.click());
    drop.addEventListener('dragover', (e) => {
      e.preventDefault();
      drop.classList.add('is-over');
    });
    drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
    drop.addEventListener('drop', (e) => {
      e.preventDefault();
      drop.classList.remove('is-over');
      const files = [...(e.dataTransfer?.files ?? [])];
      if (files.length > 0) void fanOut(files);
    });

    const status = document.createElement('p');
    // Its own hook beside .pz-hint: the row also holds a help line and a print-
    // quality note, and “the first hint” is not a stable way to find this one.
    status.className = 'pz-hint pz-upload-status';
    status.setAttribute('role', 'status');
    const grid = document.createElement('div');
    grid.className = 'pz-multiupload-grid';

    // One card per placeholder, so the shopper can see which of the nine is
    // still empty — and drop a replacement onto exactly that one.
    let picked: Array<string | null> = targets.map(() => null);
    const pending = new Map<number, File>();
    const slotStates = new Map<number, string>();
    const retryBatch = document.createElement('button');
    retryBatch.type = 'button';
    retryBatch.className = 'pz-btn-ghost small pz-upload-retry';
    retryBatch.textContent = 'Pokušaj ponovo';
    retryBatch.hidden = true;
    const cells = targets.map((elementId, index) => {
      const cell = document.createElement('div');
      cell.className = 'pz-multiupload-cell';
      cell.dataset.elementId = elementId;
      cell.dataset.index = String(index);
      const thumb = document.createElement('img');
      thumb.alt = '';
      thumb.hidden = true;
      const label = document.createElement('span');
      label.className = 'pz-multiupload-slot';
      label.textContent = UPLOAD_TEXTS.slot(index + 1, targets.length);
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'x';
      clear.textContent = '✕';
      clear.title = UPLOAD_TEXTS.remove;
      clear.hidden = true;
      clear.addEventListener('click', () => {
        if (drop.classList.contains('is-busy')) return;
        pending.delete(index);
        slotStates.delete(index);
        retryBatch.hidden = pending.size === 0;
        void dropPending(this.pendingScope(), option.id, `slot:${index}`);
        picked[index] = null;
        paint();
        publish();
        // The quality line was about the photos; with none left it is stale.
        if (picked.every((one) => one === null)) status.textContent = '';
      });
      // Which photo goes where is the shopper's call (the big middle square
      // of a collage is not "whichever file came first"): ‹ › swap a photo
      // with its neighbour — a tap on a phone, a key press on a keyboard — and
      // on a desktop a photo can also be dragged onto another slot.
      const moveButton = (step: -1 | 1): HTMLButtonElement => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `pz-multiupload-move is-${step < 0 ? 'back' : 'forward'}`;
        button.textContent = step < 0 ? '‹' : '›';
        button.title = step < 0 ? UPLOAD_TEXTS.moveBack : UPLOAD_TEXTS.moveForward;
        button.setAttribute('aria-label', button.title);
        button.hidden = true;
        button.addEventListener('click', () => swap(index, index + step));
        return button;
      };
      const back = moveButton(-1);
      const forward = moveButton(1);
      cell.setAttribute('draggable', 'false');
      cell.addEventListener('dragstart', (e) => {
        e.dataTransfer?.setData('text/pz-slot', String(index));
        cell.classList.add('is-dragging');
      });
      cell.addEventListener('dragend', () => cell.classList.remove('is-dragging'));
      cell.addEventListener('dragover', (e) => {
        if (e.dataTransfer?.types.includes('text/pz-slot')) e.preventDefault();
      });
      cell.addEventListener('drop', (e) => {
        const from = Number(e.dataTransfer?.getData('text/pz-slot'));
        if (!Number.isInteger(from)) return;
        e.preventDefault();
        e.stopPropagation();
        swap(from, index);
      });
      cell.append(thumb, label, back, forward, clear);
      const progress = document.createElement('span');
      progress.className = 'pz-multiupload-progress';
      progress.hidden = true;
      cell.appendChild(progress);
      return { cell, thumb, clear, back, forward, progress };
    });
    for (const { cell } of cells) grid.appendChild(cell);

    const count = document.createElement('p');
    count.className = 'pz-hint pz-multiupload-count';

    /** Exchanges two slots' photos (an empty slot is a photo of nothing). */
    const swap = (a: number, b: number): void => {
      if (drop.classList.contains('is-busy') || pending.has(a) || pending.has(b)) return;
      if (a === b || a < 0 || b < 0 || a >= picked.length || b >= picked.length) return;
      [picked[a], picked[b]] = [picked[b]!, picked[a]!];
      paint();
      publish();
      // Keyboard shoppers keep their place: focus follows the photo they moved.
      const moved = cells[b]?.[a < b ? 'forward' : 'back'];
      (moved && !moved.hidden ? moved : cells[b]?.cell)?.focus?.();
    };

    const paint = (): void => {
      cells.forEach(({ cell, thumb, clear, back, forward, progress }, index) => {
        const assetId = picked[index];
        const busy = drop.classList.contains('is-busy');
        const state = slotStates.get(index);
        progress.hidden = !state;
        progress.textContent = state ?? '';
        cell.classList.toggle('is-uploading', pending.has(index) && busy);
        cell.classList.toggle('is-upload-failed', pending.has(index) && !busy);
        clear.disabled = back.disabled = forward.disabled = busy;
        const url = assetId ? (this.assetUrls.get(assetId) ?? this.options.resolveAssetUrl?.(assetId)) : undefined;
        cell.classList.toggle('is-filled', Boolean(assetId));
        cell.setAttribute('draggable', assetId && !busy ? 'true' : 'false');
        thumb.hidden = !url;
        if (url && thumb.src !== url) thumb.src = url;
        clear.hidden = !assetId && !pending.has(index);
        back.hidden = !assetId || index === 0;
        forward.hidden = !assetId || index === picked.length - 1;
      });
      const filled = picked.filter((one) => one !== null).length;
      count.textContent = UPLOAD_TEXTS.count(filled, picked.length);
    };

    const publish = (): void => {
      // Trailing empties carry no information; a wholly empty answer is no
      // answer at all, so a required field still reads as unanswered.
      const trimmed = [...picked];
      while (trimmed.length > 0 && trimmed[trimmed.length - 1] === null) trimmed.pop();
      this.set(option.id, trimmed.length > 0 ? trimmed : undefined);
      this.options.flushDraft?.();
    };

    const fanOut = async (files: File[], resumed?: Array<{ file: File; at: number }>): Promise<void> => {
      if (drop.classList.contains('is-busy')) return;
      drop.classList.add('is-busy');
      drop.disabled = true;
      retryBatch.hidden = true;
      this.activePhotoUploads.add(option.id);
      this.collageSendingChanged(option.id);
      status.textContent = 'Otprema se…';
      const free = picked.map((one, i) => (one === null && !pending.has(i) ? i : -1)).filter(i => i >= 0);
      const jobs = resumed ?? files.slice(0, free.length).map((file, i) => ({ file, at: free[i]! }));
      const scope = this.pendingScope();
      // Keep the whole selection, including queued files, before sending.
      const kept = jobs.map(({ file, at }) => {
        pending.set(at, file);
        slotStates.set(at, 'Na čekanju');
        return keepPending(scope, option.id, file, `slot:${at}`);
      });
      paint();
      // Genuine failures stay their own honest sentence; the quality advice for
      // everything that DID upload is the one calm line (see the owner's rule
      // in photoquality.ts), never joined per-file and never numbered.
      const failures: string[] = [];
      const notices: string[] = [];
      let uploaded = 0;
      let warned = false;
      try {
        await Promise.all(kept);
        await runLimited(jobs, 2, async ({ file, at }) => {
          try {
            slotStates.set(at, 'Pripremam…');
            paint();
            // Header-read size, and shrunk only when over the server's limits
            // (see the single upload): no camera photo is decoded whole here.
            const size = await pictureSize(file);
            // A small picture is advice, never a refusal (see the single upload).
            if (tooSmall(file, size, config)) warned = true;
            const ready = await fitForUpload(file, size);
            const result = await this.options.api.upload(ready, ready.name, 'image', progress => {
              slotStates.set(at, progress.phase === 'processing' ? 'Obrađujem…' :
                `Šaljem ${progress.total ? Math.min(100, Math.round(progress.loaded / progress.total * 100)) : 0}%`);
              paint();
            });
            const shown = result.previewUrl ?? result.url;
            this.assetUrls.set(result.id, shown);
            this.options.onAsset?.(result.id, shown, option.id, result.width && result.height ? { w: result.width, h: result.height } : size);
            picked[at] = result.id;
            pending.delete(at);
            slotStates.delete(at);
            uploaded++;
            // Commit each confirmed photo immediately, in its reserved slot.
            paint();
            publish();
            await dropPending(scope, option.id, `slot:${at}`);
            status.textContent = `Sačuvano ${uploaded} od ${jobs.length} novih slika. Ostale se pripremaju…`;
            notices.push(
              ...(result.notices ?? []).filter((n) => n !== 'converted').map(n => IMAGE_UPLOAD_MESSAGES[n]),
            );
          } catch (err) {
            slotStates.set(at, 'Pokušaj ponovo');
            failures.push(file.name + ': ' + (err instanceof Error ? err.message : 'Otpremanje nije uspelo.'));
            paint();
          }
        });
        paint();
        publish();
        if (files.length > jobs.length) failures.push('Nema dovoljno slobodnih mesta za sve izabrane slike.');
        const quality = uploaded > 0 ? [warned ? PHOTO_TEXTS.warn : `✓ ${PHOTO_TEXTS.ok}`] : [];
        status.textContent = [...new Set([...quality, ...notices, ...failures])].join(' ');
      } finally {
        drop.classList.remove('is-busy');
        drop.disabled = false;
        this.activePhotoUploads.delete(option.id);
        this.collageSendingChanged(option.id);
        retryBatch.hidden = pending.size === 0;
        paint();
      }
    };
    retryBatch.addEventListener('click', () => void fanOut([], [...pending].map(([at, file]) => ({ at, file }))));
    this.multiUploadResumers.set(option.id, async pictures => {
      const jobs: Array<{ file: File; at: number }> = [];
      for (const picture of pictures) {
        const at = /^slot:\d+$/.test(picture.index ?? '') ? Number(picture.index!.slice(5)) : -1;
        if (at >= 0 && at < picked.length && !picked[at]) jobs.push({ file: picture.file, at });
        else await dropPending(this.pendingScope(), option.id, picture.index);
      }
      if (jobs.length) await fanOut([], jobs);
    });

    input.addEventListener('change', () => {
      const files = [...(input.files ?? [])];
      input.value = '';
      if (files.length > 0) void fanOut(files);
    });

    // A restored order puts every picture back in its own slot.
    this.setters.set(option.id, (value) => {
      const list = Array.isArray(value) ? value : [value];
      picked = targets.map((_, i) => {
        const one = list[i];
        if (typeof one === 'string' && one !== '') return one;
        if (one && typeof one === 'object' && typeof (one as { assetId?: unknown }).assetId === 'string') {
          return (one as { assetId: string }).assetId;
        }
        return null;
      });
      paint();
    });

    paint();
    wrap.append(drop, input, count, grid, status, retryBatch);
    return wrap;
  }

  /**
   * Customily's Color Picker: a swatch that IS the colour, opening the
   * browser's own picker, with the hex beside it for anyone who knows the
   * number they want. `config.defaultColor` is the starting paint.
   */
  private buildColorPicker(option: OptionDef): HTMLElement {
    const config = option.config ?? {};
    const start = normalizeHex(config.defaultColor ?? config.default) ?? '#000000';

    const wrap = document.createElement('div');
    wrap.className = 'pz-colorpick';

    // The native input is the swatch: it paints itself with its own value, and
    // clicking it opens the platform picker — no colour wheel of ours to
    // maintain, and it is the accessible control on every browser.
    const input = document.createElement('input');
    input.type = 'color';
    input.className = 'pz-color';
    input.value = start;
    input.setAttribute('aria-label', option.label || CHOICE_TEXTS.colorLabel);

    const hex = document.createElement('input');
    hex.type = 'text';
    hex.className = 'pz-input pz-colorpick-hex';
    hex.value = start.toUpperCase();
    hex.maxLength = 7;
    hex.setAttribute('aria-label', CHOICE_TEXTS.hexLabel);
    hex.setAttribute('spellcheck', 'false');

    const apply = (value: string, echo: 'hex' | 'swatch'): void => {
      const clean = normalizeHex(value);
      if (!clean) return;
      if (echo === 'hex') hex.value = clean.toUpperCase();
      else input.value = clean;
      this.set(option.id, clean);
    };
    input.addEventListener('input', () => apply(input.value, 'hex'));
    hex.addEventListener('input', () => apply(hex.value, 'swatch'));
    hex.addEventListener('change', () => {
      // A half-typed hex reverts rather than sticking as nonsense.
      hex.value = input.value.toUpperCase();
    });

    this.setters.set(option.id, (value) => {
      const clean = normalizeHex(value);
      if (!clean) return;
      input.value = clean;
      hex.value = clean.toUpperCase();
    });

    wrap.append(input, hex);
    return wrap;
  }

  /**
   * A date picked on a calendar of our own, in Serbian: the field reads
   * "14.10.2025.", the month is "Oktobar 2025", the week starts on Monday.
   *
   * The browser's own date field spells the date the way the SHOPPER'S browser
   * language does: an English phone showed mm/dd/yyyy on a Serbian shop, and a
   * 03/04 there is a different day. The answer is still the ISO day
   * (yyyy-mm-dd), which the calendar layer, the cart and the order sheet read.
   */
  private buildDatePicker(option: OptionDef): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'pz-datepick';
    const two = (n: number): string => String(n).padStart(2, '0');
    const today = new Date();

    const field = document.createElement('button');
    field.type = 'button';
    field.className = 'pz-input pz-date-field';
    field.setAttribute('aria-expanded', 'false');
    field.setAttribute('aria-label', option.label);
    const fieldText = document.createElement('span');
    const fieldIcon = document.createElement('span');
    fieldIcon.className = 'pz-date-icon';
    fieldIcon.innerHTML = CALENDAR_ICON;
    field.append(fieldText, fieldIcon);

    const panel = document.createElement('div');
    panel.className = 'pz-cal';
    panel.hidden = true;
    const head = document.createElement('div');
    head.className = 'pz-cal-head';
    const arrow = (label: string, path: string): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pz-cal-nav';
      button.setAttribute('aria-label', label);
      button.innerHTML = `<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${path}"/></svg>`;
      return button;
    };
    const prev = arrow(DATE_TEXTS.previous, 'M12.5 4.5L7 10l5.5 5.5');
    const next = arrow(DATE_TEXTS.next, 'M7.5 4.5L13 10l-5.5 5.5');
    // Month and year jump straight to a far date (an anniversary years back).
    const monthList = document.createElement('select');
    monthList.className = 'pz-cal-select';
    monthList.setAttribute('aria-label', DATE_TEXTS.month);
    DATE_TEXTS.months.forEach((name, i) => {
      const one = document.createElement('option');
      one.value = String(i);
      one.textContent = name;
      monthList.appendChild(one);
    });
    const yearList = document.createElement('select');
    yearList.className = 'pz-cal-select';
    yearList.setAttribute('aria-label', DATE_TEXTS.year);
    for (let y = today.getFullYear() + 10; y >= 1900; y--) {
      const one = document.createElement('option');
      one.value = String(y);
      one.textContent = String(y);
      yearList.appendChild(one);
    }
    head.append(prev, monthList, yearList, next);
    const grid = document.createElement('div');
    grid.className = 'pz-cal-grid';
    panel.append(head, grid);
    wrap.append(field, panel);

    /** The chosen day as [year, month 0-11, day], or undefined. */
    let chosen: [number, number, number] | undefined;
    /** The month on screen. */
    let view = { year: today.getFullYear(), month: today.getMonth() };

    const showField = (): void => {
      fieldText.textContent = chosen ? `${two(chosen[2])}.${two(chosen[1] + 1)}.${chosen[0]}.` : DATE_TEXTS.placeholder;
      field.classList.toggle('is-empty', !chosen);
    };
    const draw = (): void => {
      monthList.value = String(view.month);
      yearList.value = String(view.year);
      grid.innerHTML = '';
      for (const letter of DATE_TEXTS.weekdays) {
        const cell = document.createElement('span');
        cell.className = 'pz-cal-dow';
        cell.textContent = letter;
        grid.appendChild(cell);
      }
      // Monday first: Sunday (0) is the seventh column.
      const lead = (new Date(view.year, view.month, 1).getDay() + 6) % 7;
      for (let i = 0; i < lead; i++) grid.appendChild(document.createElement('span'));
      const days = new Date(view.year, view.month + 1, 0).getDate();
      for (let d = 1; d <= days; d++) {
        const cell = document.createElement('button');
        cell.type = 'button';
        cell.className = 'pz-cal-day';
        cell.textContent = String(d);
        cell.dataset.day = String(d);
        cell.setAttribute('aria-label', `${d}. ${DATE_TEXTS.months[view.month]} ${view.year}.`);
        if (chosen && chosen[0] === view.year && chosen[1] === view.month && chosen[2] === d) {
          cell.classList.add('is-chosen');
          cell.setAttribute('aria-pressed', 'true');
        }
        if (today.getFullYear() === view.year && today.getMonth() === view.month && today.getDate() === d) {
          cell.classList.add('is-today');
        }
        cell.addEventListener('click', () => {
          chosen = [view.year, view.month, d];
          this.set(option.id, `${view.year}-${two(view.month + 1)}-${two(d)}`);
          showField();
          close();
        });
        grid.appendChild(cell);
      }
    };
    const open = (): void => {
      view = chosen ? { year: chosen[0], month: chosen[1] } : { year: today.getFullYear(), month: today.getMonth() };
      draw();
      panel.hidden = false;
      field.setAttribute('aria-expanded', 'true');
    };
    const close = (): void => {
      panel.hidden = true;
      field.setAttribute('aria-expanded', 'false');
    };
    const shift = (by: number): void => {
      const at = new Date(view.year, view.month + by, 1);
      view = { year: at.getFullYear(), month: at.getMonth() };
      draw();
    };
    field.addEventListener('click', () => (panel.hidden ? open() : close()));
    prev.addEventListener('click', () => shift(-1));
    next.addEventListener('click', () => shift(1));
    monthList.addEventListener('change', () => {
      view = { ...view, month: Number(monthList.value) };
      draw();
    });
    yearList.addEventListener('change', () => {
      view = { ...view, year: Number(yearList.value) };
      draw();
    });
    wrap.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Escape' && !panel.hidden) {
        close();
        field.focus();
      }
    });

    this.setters.set(option.id, (value) => {
      const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
      chosen = match ? [Number(match[1]), Number(match[2]) - 1, Number(match[3])] : undefined;
      showField();
      if (!panel.hidden) open();
    });
    showField();
    return wrap;
  }

  private buildUpload(option: OptionDef): HTMLElement {
    const wrap = document.createElement('div');
    wrap.style.display = 'grid';
    wrap.style.gap = '8px';

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptFor(option.type);
    // One gesture may carry the whole product: a 30-slot collage is filled by
    // selecting 30 files here, not by opening 30 pickers.
    input.multiple = option.type !== 'vector_upload';
    input.hidden = true;

    const config = option.config ?? {};
    // Customily's storefront: the label IS the button, and clicking it opens
    // the operating system's file picker straight away — no dropzone modal in
    // between. `config.buttonText` and `config.buttonClass` are its own two
    // fields, and the class is the shop's styling hook.
    const drop = uploadButton(config, option.type === 'vector_upload');
    drop.addEventListener('click', () => this.openPicker(option.id, input));
    // "Slikaj": the in-page camera, on phones. The phone's own camera app
    // (reached through the picker) can make Android close the page behind it;
    // this one never leaves the page.
    const camera =
      option.type !== 'vector_upload' && cameraAvailable() && pointerIsCoarse()
        ? cameraButton(() => void this.openCamera(option, input, status))
        : undefined;
    // Drag-and-drop from the desktop, the way people expect a photo slot to work.
    drop.addEventListener('dragover', (e) => {
      e.preventDefault();
      drop.classList.add('is-over');
    });
    drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
    drop.addEventListener('drop', (e) => {
      e.preventDefault();
      drop.classList.remove('is-over');
      const files = [...(e.dataTransfer?.files ?? [])];
      if (files.length > 0) void this.receive(option, files, status);
    });

    const card = document.createElement('div');
    card.className = 'pz-file-card';
    card.hidden = true;
    const thumb = document.createElement('img');
    thumb.alt = '';
    const thumbBox = fileThumb(thumb);
    const name = document.createElement('span');
    name.className = 'name';
    // Another picture straight from the card, without removing this one first.
    const replace = document.createElement('button');
    replace.type = 'button';
    replace.className = 'pz-file-replace';
    replace.innerHTML = REPLACE_ICON + `<span>${escapeText(UPLOAD_TEXTS.replace)}</span>`;
    replace.addEventListener('click', () => this.openPicker(option.id, input));
    const info = document.createElement('div');
    info.className = 'pz-file-info';
    info.append(name, replace);
    const remove = removeButton(UPLOAD_TEXTS.remove);
    remove.addEventListener('click', () => {
      input.value = '';
      clearCard();
      this.set(option.id, undefined);
      void dropPending(this.pendingScope(), option.id);
    });
    card.append(thumbBox, info, remove);
    /** The photo the card is showing; cut-out news about any other is stale. */
    let cardAsset: string | undefined;
    // No photo filter chips (Customily's "Apply filters"), whatever
    // `config.applyFilters` says: the owner took them away, so a photo goes to
    // print as uploaded. An old answer that already carries a `filter` keeps
    // it untouched — preview, resolve and print still read it the same way.

    const status = document.createElement('p');
    // Its own hook beside .pz-hint: the row also holds a help line and a print-
    // quality note, and “the first hint” is not a stable way to find this one.
    status.className = 'pz-hint pz-upload-status';
    // "Try again" after a failed send: the same picture, kept in memory (and
    // in IndexedDB), so the shopper never has to find it again.
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'pz-btn-ghost small pz-upload-retry';
    retry.textContent = UPLOAD_TEXTS.retry;
    retry.hidden = true;
    let failedFile: File | undefined;
    retry.addEventListener('click', () => {
      const again = failedFile;
      if (again) void handle(again, true);
    });

    const photoAction = document.createElement('button');
    photoAction.type = 'button';
    photoAction.className = 'pz-btn-ghost small pz-photo-action';
    photoAction.hidden = true;

    const importNote = document.createElement('p');
    importNote.className = 'pz-hint pz-import-notice';
    importNote.setAttribute('role', 'status');
    importNote.hidden = true;

    // Customily places the picture and opens its cropper only where the
    // placeholder allows it; a locked slot simply takes the file. Ours frames on
    // the preview itself, so "open the cropper" is this invitation appearing.
    const canFrame = this.canFrame(option.id);
    const editHint = document.createElement('p');
    editHint.className = 'pz-hint pz-edit-hint';
    editHint.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="width:15px;height:15px;vertical-align:-3px;margin-right:5px"><path d="M10 3v14M3 10h14M10 3l-2 2M10 3l2 2M10 17l-2-2M10 17l2-2M3 10l2-2M3 10l2 2M17 10l-2-2M17 10l-2 2"/></svg>' +
      UPLOAD_TEXTS.framingHint;
    editHint.hidden = true;

    /** Back to "no photo": nothing about the last picture may linger. */
    const clearCard = (): void => {
      cardAsset = undefined;
      card.hidden = true;
      card.classList.remove('is-ok', 'is-cutting', 'is-revealing');
      photoAction.hidden = true;
      photoAction.onclick = null;
      thumb.removeAttribute?.('src');
      name.textContent = '';
      drop.hidden = false;
      if (camera) camera.hidden = false;
      editHint.hidden = true;
      status.textContent = '';
      importNote.hidden = true;
      importNote.textContent = '';
    };

    this.photoCards.set(option.id, (state) => {
      if (!cardAsset || (state.assetId && state.assetId !== cardAsset)) return false;
      photoAction.hidden = !state.action;
      photoAction.textContent = state.action?.label ?? '';
      photoAction.onclick = state.action ? () => state.action!.run() : null;
      card.classList.toggle('is-cutting', state.phase === 'cutting');
      if (state.phase === 'cutting') {
        status.textContent = state.message ?? 'Uklanjam pozadinu…';
        return true;
      }
      if (state.phase === 'ready') {
        status.textContent = state.advice ?? '';
        if (state.advice) card.classList.remove('is-ok');
        // Cross-faded rather than swapped: the background vanishing is the
        // thing the shopper is waiting to see, and a hard cut hides it.
        if (state.url && thumb.src !== state.url) {
          card.classList.add('is-revealing');
          thumb.src = state.url;
          window.setTimeout(() => card.classList.remove('is-revealing'), 420);
        }
        return true;
      }
      // A cut-out that could not be made is not the shopper's problem to fix;
      // their photo is already uploaded and their order will still be right.
      status.textContent = state.message ?? '';
      return true;
    });

    const handle = async (file: File, resumed = false): Promise<void> => {
      if (drop.classList.contains('is-busy')) return;
      drop.classList.add('is-busy');
      this.activePhotoUploads.add(option.id);
      this.collageSendingChanged(option.id);
      retry.hidden = true;
      status.textContent = resumed ? UPLOAD_TEXTS.resuming : 'Proveravam sliku…';
      const scope = this.pendingScope();
      try {
        // Kept before anything that could run the phone out of memory: if the
        // page dies from here on, the next load finishes this upload itself.
        if (!resumed) await keepPending(scope, option.id, file);
        // Its pixel size from the file's header — no decode of a camera photo.
        const size = await pictureSize(file);
        // Measured before anything leaves the phone, so the advice is instant.
        // Advice only: the shop always lets the shopper upload and order what
        // they have — a small picture is their call, and the merchant sees the
        // same measurement on the order. Nothing below refuses the file.
        const check = await inspect(file, size, this.options.product, option.id);
        // The field's own recommended size in pixels (Customily's "Min. Image
        // Width/Height") is a second, independent reason to warn, in the same
        // one calm voice — see the owner's rule in photoquality.ts.
        const small = tooSmall(file, size, config);
        const warned = check?.verdict === 'warn' || small;

        status.textContent = 'Otprema se…';
        // The original's resolution and alpha are kept (the server converts
        // phone formats); only a picture over the server's limits is shrunk
        // here, instead of being refused.
        const ready = await fitForUpload(file, size);
        const result = await this.options.api.upload(ready, ready.name, 'image', progress => {
          status.textContent = progress.phase === 'processing' ? 'Slika je poslata. Pripremam pregled…' :
            `Šaljem sliku… ${progress.total ? Math.min(100, Math.round(progress.loaded / progress.total * 100)) : 0}%`;
        });
        // What the page draws: the server's preview copy of a big photo.
        const shown = result.previewUrl ?? result.url;
        this.assetUrls.set(result.id, shown);
        card.classList.remove('is-cutting', 'is-revealing');
        cardAsset = result.id;
        thumb.src = shown;
        name.textContent = result.filename ?? file.name;
        card.hidden = false;
        drop.hidden = true;
        if (camera) camera.hidden = true;
        // A warning survives the upload: the shopper may still want to change
        // their mind once they see it on the product. Either way, one calm
        // line, never numbers — the card looks the same either way too.
        status.textContent = warned ? PHOTO_TEXTS.warn : `✓ ${PHOTO_TEXTS.ok}`;
        importNote.textContent = (result.notices ?? [])
          .filter((n) => n !== 'converted')
          .map(n => IMAGE_UPLOAD_MESSAGES[n]).join(' ');
        importNote.hidden = !importNote.textContent;
        card.classList.toggle('is-ok', !warned);
        // The framing invitation only when the slot can actually be framed: an
        // element the author locked down places the picture and that is that.
        editHint.hidden = !canFrame;
        const original = result.width && result.height ? { w: result.width, h: result.height } : size;
        this.options.onAsset?.(result.id, shown, option.id, original);
        // A fresh upload starts at the default framing; the shopper adjusts it by
        // dragging the photo on the preview itself, which writes {assetId, crop}
        // back through setValue.
        this.set(option.id, result.id);
        // Saved at once, then the kept copy of the file can go.
        this.options.flushDraft?.();
        failedFile = undefined;
        void dropPending(scope, option.id);
      } catch (err) {
        status.textContent = err instanceof Error ? err.message : 'Otpremanje nije uspelo.';
        // The picture is still here: one tap sends it again.
        failedFile = file;
        retry.hidden = false;
      } finally {
        drop.classList.remove('is-busy');
        this.activePhotoUploads.delete(option.id);
        this.collageSendingChanged(option.id);
      }
    };

    input.addEventListener('change', () => {
      donePicking();
      const files = [...(input.files ?? [])];
      input.value = '';
      if (files.length > 0) void this.receive(option, files, status);
    });
    // The picker closed without a picture.
    input.addEventListener('cancel', () => donePicking());

    this.photoUploaders.set(option.id, handle);

    // A restored order shows its photo again, framing intact; a cleared answer
    // (a reset, a restore without this photo) empties the card completely.
    this.setters.set(option.id, (value) => {
      const assetId =
        typeof value === 'string'
          ? value
          : value && typeof value === 'object' && 'assetId' in value
            ? String((value as { assetId: unknown }).assetId)
            : '';
      if (!assetId) {
        clearCard();
        return;
      }
      cardAsset = assetId;
      const url = this.options.resolveAssetUrl?.(assetId);
      if (url) thumb.src = url;
      name.textContent = 'Sačuvana fotografija';
      card.hidden = false;
      drop.hidden = true;
      if (camera) camera.hidden = true;
      editHint.hidden = !canFrame;
    });

    if (camera) {
      const pick = document.createElement('div');
      pick.className = 'pz-pick-row';
      pick.append(drop, camera);
      wrap.append(pick, input, card, editHint, status, retry, photoAction, importNote);
    } else {
      wrap.append(drop, input, card, editHint, status, retry, photoAction, importNote);
    }
    // The card replaces the buttons once a picture is in; the camera follows.
    this.photoButtons.set(option.id, { drop, camera, status });
    return wrap;
  }

  // ── Photo collage ("Kutije sa slikama", docs/photo-collage.md) ────────────

  /**
   * One question, many photos: the step (pick them all at once, watch them
   * arrive, see where things stand), its intake (uploads, retries, recovery)
   * and its host (the answer, the undo stack, the board). The answer is a
   * `CollageAnswer` changed only by the pure `@pz/scene` commands. It never
   * takes part in the photo spill, and no cut-out, style, lightbox or editor
   * selection ever runs for one of its photos.
   */
  private buildCollage(option: OptionDef): HTMLElement {
    let step: CollageStep | undefined;
    // Whether the page last heard this collage as "still sending". Photos
    // start and stop being on their way without an answer changing — a pick
    // (nothing has landed yet), a batch whose last photo failed — and the page
    // (the required meter, "Dalje", Studio's `missing` and `busy`) must hear
    // both: the question is on hold now, or answered now.
    let pageSawSending = false;
    const host = new CollageHost({
      option,
      product: this.options.product,
      values: () => this.current,
      version: () => this.answerVersion,
      read: () => {
        const value = this.values[option.id];
        return isCollageAnswer(value) ? value : null;
      },
      // An answer without a single photo is no answer (and no draft line).
      store: (next) => {
        this.set(option.id, next && next.photos.length > 0 ? next : undefined);
        // `set` told the page just now, with the intake as it is.
        pageSawSending = intake.sending;
      },
      layerHost: () => this.layerHost(),
      ...(this.options.collage?.render ? { render: this.options.collage.render } : {}),
      resolveUrl: (id) => this.assetUrls.get(id) ?? this.options.resolveAssetUrl?.(id),
      onAsset: (media) => {
        this.assetUrls.set(media.assetId, media.drawUrl);
        this.options.onCollageAsset?.(option.id, media);
      },
      pickPhotos: (target) => step?.pick(target),
      onBoard: (open) => {
        this.options.collage?.onBoard?.(option.id, open);
        if (!open) step?.refresh();
      },
      announce: (text) => step?.announce(text),
      onRelayout: () => step?.note(host.status().spares > 0 ? COLLAGE_TEXTS.relayout : ''),
    });
    const intake = new CollageIntake({
      optionId: option.id,
      scope: this.pendingScope(),
      upload: (file, name) => this.options.api.upload(file, name, 'collage'),
      host,
      flushDraft: () => this.options.flushDraft?.(),
      onUpdate: () => {
        step?.refresh();
        const sending = intake.sending;
        if (sending === pageSawSending) return;
        pageSawSending = sending;
        this.collageSendingChanged(option.id);
      },
    });
    step = createCollageStep({
      option,
      host,
      intake,
      openPicker: (input) => this.openPicker(option.id, input),
      toast: (message, action) =>
        void showToast(this.layerHost(), { message, kind: 'info', ...(action ? { action } : {}) }),
    });
    this.collages.set(option.id, { host, intake, step });
    // A restored answer (cart, draft, a reset): the undo stack belonged to the
    // old one, and the step draws what is there now.
    this.setters.set(option.id, () => {
      host.restored();
      step?.refresh();
    });
    return step.element;
  }

  /**
   * After an answer changed: each collage (but `except`, the one that just
   * changed itself) checks whether its active slots moved — a layout switch —
   * and re-seats its photos (`relayoutCollage`, photos that no longer fit wait
   * in "Rezerva"). Re-entrant: a re-seat is itself an answer change.
   */
  private syncCollages(except?: string): void {
    for (const [optionId, collage] of this.collages) {
      if (optionId === except) continue;
      try {
        // Only a layout switch changes what the step says; typing a quote does not.
        if (collage.host.afterAnswersChanged()) collage.step.refresh();
      } catch {
        /* a collage that cannot be re-seated keeps its answer as it is */
      }
    }
  }

  /** The collage questions the shopper can see now (the wizard's step, or the whole form). */
  collagesShown(): string[] {
    const out: string[] = [];
    for (const optionId of this.collages.keys()) {
      const row = this.rows.get(optionId);
      if (row && this.rowIsReachable(optionId, row)) out.push(optionId);
    }
    return out;
  }

  /** The collage question whose ACTIVE slot this element is, if any. */
  collageOf(elementId: string): string | undefined {
    for (const [optionId, collage] of this.collages) {
      if (this.conditionHidden.has(optionId)) continue;
      if (collage.host.activeIds().includes(elementId)) return optionId;
    }
    return undefined;
  }

  /** "Raspored slika" for one collage, focused on one of its slots when given. */
  openCollageBoard(optionId: string, focus?: string): Promise<void> {
    const collage = this.collages.get(optionId);
    return collage ? collage.host.openBoard(focus) : Promise.resolve();
  }

  /** "24 slike": the filled ACTIVE slots, for the summary and the cart line. */
  collageSummary(optionId: string): string {
    const collage = this.collages.get(optionId);
    const value = this.values[optionId];
    if (!collage || !isCollageAnswer(value)) return '';
    return describeCollage(value, collage.host.activeIds());
  }

  /** Photos still sending in a collage, multi-slot field or single photo field. */
  collageSending(): boolean {
    if (this.activePhotoUploads.size > 0) return true;
    for (const collage of this.collages.values()) if (collage.intake.sending) return true;
    return false;
  }

  /**
   * A collage's photos started or stopped being on their way without an
   * answer change the page heard. Answered now: the row's "you forgot this"
   * mark goes. Either way the page hears the answers again (the required
   * meter, "Dalje", the cart, Studio's `missing` and `busy`).
   */
  private collageSendingChanged(optionId: string): void {
    if (this.isAnswered(optionId)) this.rows.get(optionId)?.classList.remove('is-missing');
    this.options.onChange(this.current);
  }

  /**
   * Before this form is replaced by another template's ("Change Template"):
   * every collage stops sending, and the photos still on their way are handed
   * back by question, for the next form's `adoptCollageUploads`. The photos
   * that already landed travel with the answer (and `collageDraft`).
   */
  releaseCollageUploads(): Map<string, File[]> {
    const out = new Map<string, File[]>();
    for (const [optionId, collage] of this.collages) {
      const files = collage.intake.handOver();
      if (files.length > 0) out.set(optionId, files);
    }
    return out;
  }

  /** The photos another form was still sending, sent again here — for the same question, if this form has it. */
  adoptCollageUploads(handed: Map<string, File[]> | undefined): void {
    for (const [optionId, files] of handed ?? []) {
      const collage = this.collages.get(optionId);
      if (collage && files.length > 0) void collage.step.receive(files);
    }
  }

  /**
   * What a draft keeps of each collage beyond its answer: the tray's
   * thumbnails, the framer copies and each photo's source fingerprint (so a
   * resumed or re-picked file is never sent twice). The draw copies travel
   * with the draft's `assets`, like every other picture.
   */
  collageDraft(): Record<string, CollageDraftMedia> {
    const out: Record<string, CollageDraftMedia> = {};
    for (const [optionId, collage] of this.collages) {
      const answer = collage.host.answer();
      if (!answer || answer.photos.length === 0) continue;
      const inAnswer = new Set(answer.photos.map((p) => p.assetId));
      const sources = Object.fromEntries(Object.entries(collage.intake.sources()).filter(([id]) => inAnswer.has(id)));
      out[optionId] = { ...collage.host.mediaMaps(), sources };
    }
    return out;
  }

  /** A draft's collage media (see `collageDraft`), put back before its answers. */
  restoreCollageDraft(saved: unknown): void {
    if (!saved || typeof saved !== 'object') return;
    const record = (value: unknown): Record<string, string> | undefined =>
      value && typeof value === 'object' ? (value as Record<string, string>) : undefined;
    for (const [optionId, raw] of Object.entries(saved as Record<string, unknown>)) {
      const collage = this.collages.get(optionId);
      if (!collage || !raw || typeof raw !== 'object') continue;
      const media = raw as { thumbs?: unknown; framers?: unknown; sources?: unknown };
      collage.host.restoreMedia({ thumbs: record(media.thumbs), framers: record(media.framers) });
      collage.intake.restoreSources(record(media.sources));
    }
  }

  /**
   * A restored order's `collageThumbs` (asset id → 256 px thumbnail), for
   * every collage's tray, and its `collageFramers` (asset id → the 2400 px
   * copy the framer opens); the draw copies come in the restore's `assets`.
   * Without the framers a reopened order would frame on the 1024 px draw copy.
   */
  restoreCollageThumbs(thumbs: Record<string, string> | undefined, framers?: Record<string, string>): void {
    if (!thumbs && !framers) return;
    for (const collage of this.collages.values()) collage.host.restoreMedia({ thumbs, framers });
  }

  /** Each photo question's buttons and status line, for notices from outside. */
  private readonly photoButtons = new Map<string, { drop: HTMLElement; camera?: HTMLElement; status: HTMLElement }>();

  /** Where this product's in-flight pictures are kept (see photo-intake). */
  private pendingScope(): string {
    const product = this.options.product;
    return `${product.productId}:${product.templateId}`;
  }

  /**
   * Opens the file picker for a question — after saving the answers and noting
   * that a picker is open, because on a phone short of memory the page may not
   * survive the camera app the picker can start.
   */
  private openPicker(optionId: string, input: HTMLInputElement): void {
    this.options.flushDraft?.();
    markPicking(this.pendingScope(), optionId);
    // Back on the page with nothing picked: the note is no longer needed.
    const back = (): void => {
      window.removeEventListener('focus', back);
      window.setTimeout(donePicking, 2000);
    };
    window.addEventListener('focus', back);
    input.click();
  }

  /** The in-page camera for a question; the ordinary picker when it cannot start. */
  private async openCamera(option: OptionDef, input: HTMLInputElement, status: HTMLElement): Promise<void> {
    this.options.flushDraft?.();
    const root = this.element.getRootNode();
    const host = root instanceof ShadowRoot ? root : document.body;
    const file = await takePhoto({ host, facing: 'user', onGallery: () => this.openPicker(option.id, input) });
    if (file) await this.receive(option, [file], status);
  }

  /**
   * Finishes the uploads a reload interrupted: pictures picked on this product
   * whose upload never confirmed (see `keepPending`). A kept picture is the
   * shopper's LAST action on its question — it is dropped the moment its
   * upload confirms — so it wins over an older photo the draft put back.
   */
  async resumePending(): Promise<void> {
    const scope = this.pendingScope();
    const pending = await pendingFor(scope);
    // A collage's kept photos go back as ONE batch ("Nastavljam slanje slika
    // koje nisu stigle…"), each under its own index, ahead of single photos.
    const batches = new Map<string, PendingPicture[]>();
    for (const picture of pending) {
      if (!this.collages.has(picture.optionId)) continue;
      batches.set(picture.optionId, [...(batches.get(picture.optionId) ?? []), picture]);
    }
    for (const [optionId, list] of batches) {
      const collage = this.collages.get(optionId)!;
      await collage.intake.resume(list).catch(() => undefined);
      collage.step.refresh();
    }
    for (const [optionId, resume] of this.multiUploadResumers) {
      const pictures = pending.filter(p => p.optionId === optionId);
      if (pictures.length) await resume(pictures);
    }
    for (const picture of pending) {
      if (this.collages.has(picture.optionId) || this.multiUploadResumers.has(picture.optionId)) continue;
      const upload = this.photoUploaders.get(picture.optionId);
      if (!upload) {
        void dropPending(scope, picture.optionId);
        continue;
      }
      await upload(picture.file, true);
    }
  }

  /**
   * The page was reloaded while this question's picker (or the phone's
   * camera) was open: the picture never arrived. Says so on the question, in
   * words that say what to do next, and brings it into view.
   */
  noteInterruptedPick(optionId: string): void {
    const collage = this.collages.get(optionId);
    if (collage) {
      if (collage.step.started()) return;
      collage.step.noteInterrupted();
      this.rows.get(optionId)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      return;
    }
    const buttons = this.photoButtons.get(optionId);
    if (!buttons || this.isAnswered(optionId)) return;
    buttons.status.textContent = inAppBrowser() ? UPLOAD_TEXTS.interruptedInApp : UPLOAD_TEXTS.interrupted;
    buttons.status.classList.add('is-notice');
    buttons.drop.closest('.pz-row')?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }
}

/** What a draft keeps of one collage beyond its answer (see `OptionForm.collageDraft`). */
export interface CollageDraftMedia {
  /** Asset id → 256 px thumbnail. */
  thumbs: Record<string, string>;
  /** Asset id → the 2400 px preview copy the framer opens. */
  framers: Record<string, string>;
  /** Asset id → the picked file's fingerprint (`name|size|lastModified`). */
  sources: Record<string, string>;
}

/** The "Slikaj" button: a camera glyph and the word. */
function cameraButton(onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'pz-drop pz-drop-camera';
  button.innerHTML =
    '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h2l1.2-1.8h4.6L13.5 6h2A1.5 1.5 0 0 1 17 7.5v7A1.5 1.5 0 0 1 15.5 16h-11A1.5 1.5 0 0 1 3 14.5z"/><circle cx="10" cy="10.8" r="2.8"/></svg>' +
    `<span>${escapeText(UPLOAD_TEXTS.cameraButton)}</span>`;
  button.addEventListener('click', onClick);
  return button;
}

/** What the shopper has decided about their own photo, beyond which file it is. */
export interface PhotoAnswer {
  assetId: string;
  crop?: { cx: number; cy: number; zoom: number };
  filter?: string;
  keepOriginal?: true;
}

/**
 * Folds one control's contribution into a photo answer without losing the rest.
 *
 * A photo is answered by four controls that do not know about each other — the
 * upload card, the filter chips, "keep the original", and framing, which is
 * itself written from two places (dragging the picture on the preview, and
 * choosing a face in a group shot). Every one of them used to build a whole new
 * answer object, so the last one to move silently reverted the others: pick a
 * face and your sepia is gone; drag the photo and the background comes back.
 *
 * Merging is keyed on the asset. A *different* photo is a fresh start — the
 * previous picture's crop would frame the wrong part of it, and its filter was a
 * decision about an image that is no longer there.
 */
export function mergePhotoAnswer(current: unknown, incoming: unknown): unknown {
  const asObject = (value: unknown): Partial<PhotoAnswer> | undefined =>
    typeof value === 'string'
      ? { assetId: value }
      : value && typeof value === 'object'
        ? (value as Partial<PhotoAnswer>)
        : undefined;

  const next = asObject(incoming);
  const previous = asObject(current);
  if (!next?.assetId) return incoming;
  if (!previous?.assetId || previous.assetId !== next.assetId) return incoming;

  const merged: Record<string, unknown> = { ...previous, ...next };
  // `crop` is the one field the incoming side may legitimately clear: the photo
  // editor sends a bare asset id to mean "back to the default framing".
  if (typeof incoming === 'string' || !('crop' in next)) delete merged.crop;
  return merged;
}

function escapeText(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** "+" for a surcharge, "−" for a discount, on a price badge. */
function priceSign(delta: number): string {
  return delta < 0 ? '−' : '+';
}

/**
 * A swatch value's own colour.
 *
 * Customily calls the field "Swatch Color"; our imported values carry it as
 * `meta.bgColor`, values built from a colour Library as `meta.color`, and the
 * option contract may hand it over as a plain `color` on the value itself.
 * All three are the same thing, so all three are read.
 */
function swatchColorOf(value: {
  color?: unknown;
  meta?: Record<string, unknown>;
}): string | undefined {
  for (const candidate of [value.meta?.bgColor, value.meta?.color, value.color]) {
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate.trim();
  }
  return undefined;
}

/** A picture choice's caption: its name without an inlined "(+400 RSD)". */
function captionOf(label: string): string {
  return label.replace(/\s*\(\+[^)]*\)\s*/g, '').trim();
}

/** A hex colour, `#rgb` or `#rrggbb`, normalised to lowercase `#rrggbb`; null if it is neither. */
function normalizeHex(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  const short = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(text);
  if (short) return `#${short[1]!}${short[1]!}${short[2]!}${short[2]!}${short[3]!}${short[3]!}`.toLowerCase();
  const long = /^#?([0-9a-f]{6})$/i.exec(text);
  return long ? `#${long[1]!.toLowerCase()}` : null;
}

/**
 * Customily's "Image Placeholder IDs" list, as the option config carries it.
 *
 * Tolerant on purpose: the importer writes strings, a hand-edited option set may
 * carry numbers, and an empty row in the Studio list must not become an empty
 * target that silently swallows a photo.
 */
function targetElementIds(config: Record<string, unknown>): string[] {
  const raw = config.targetElementIds;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((one) => (typeof one === 'number' ? String(one) : typeof one === 'string' ? one.trim() : ''))
    .filter((one) => one !== '');
}

/** What a file picker accepts, per option type. */
function acceptFor(type: string): string {
  // A vector slot takes logos as they come from the designer: SVG, PDF, AI, EPS.
  return type === 'vector_upload'
    ? ARTWORK_UPLOAD_ACCEPT
    : IMAGE_UPLOAD_ACCEPT;
}

/**
 * The upload control's button — Customily's own, one for one.
 *
 * Its text is `config.buttonText` (Customily's "Button Text" field, defaulting
 * to what its storefront prints), and `config.buttonClass` ("Button Class") is
 * added verbatim so a shop's CSS can find it. The class is filtered to what a
 * CSS class may contain: the value reaches us from an imported option set, and
 * it is written into a class attribute.
 */
/** The date question's three lists, in Serbian (Latin), day first. */
/** The date question's calendar, in Serbian (Latin); the week starts on Monday. */
export const DATE_TEXTS = {
  placeholder: 'Izaberi datum',
  month: 'Mesec',
  year: 'Godina',
  previous: 'Prethodni mesec',
  next: 'Sledeći mesec',
  months: ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'],
  weekdays: ['P', 'U', 'S', 'Č', 'P', 'S', 'N'],
};

const CALENDAR_ICON =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4.5" width="14" height="12.5" rx="2"/><path d="M3 8.5h14M7 3v3M13 3v3"/></svg>';

const REPLACE_ICON =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15.5 8A6 6 0 0 0 4.7 6.3L3.5 7.5M3.5 3.5v4h4"/><path d="M4.5 12a6 6 0 0 0 10.8 1.7l1.2-1.2M16.5 16.5v-4h-4"/></svg>';
const REMOVE_ICON =
  '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M5.5 5.5l9 9M14.5 5.5l-9 9"/></svg>';

/** The round ✕ of a file card. */
function removeButton(label: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'x';
  button.innerHTML = REMOVE_ICON;
  button.title = label;
  button.setAttribute('aria-label', label);
  return button;
}

/**
 * A file card's picture in its own frame. A cut-out (a head on transparency)
 * is cropped to what is actually drawn, so the face fills the frame instead of
 * sitting small in one corner of an empty square.
 */
function fileThumb(img: HTMLImageElement): HTMLElement {
  const box = document.createElement('span');
  box.className = 'pz-file-thumb';
  box.appendChild(img);
  img.addEventListener?.('load', () => trimTransparentEdges(img, box));
  return box;
}

function trimTransparentEdges(img: HTMLImageElement, box: HTMLElement): void {
  const src = img.currentSrc || img.src;
  // Our own trimmed copy coming back in: nothing more to do.
  if (!src || src.startsWith('data:') || typeof Image === 'undefined') return;
  box.classList.remove('is-cutout');
  const probe = new Image();
  probe.crossOrigin = 'anonymous';
  probe.onload = () => {
    try {
      if ((img.currentSrc || img.src) !== src) return; // another picture since
      const scale = Math.min(1, 240 / Math.max(probe.naturalWidth, probe.naturalHeight));
      const w = Math.max(1, Math.round(probe.naturalWidth * scale));
      const h = Math.max(1, Math.round(probe.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const g = canvas.getContext('2d', { willReadFrequently: true });
      if (!g) return;
      g.drawImage(probe, 0, 0, w, h);
      const data = g.getImageData(0, 0, w, h).data;
      let x0 = w;
      let y0 = h;
      let x1 = -1;
      let y1 = -1;
      let seeThrough = false;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const alpha = data[(y * w + x) * 4 + 3]!;
          if (alpha < 250) seeThrough = true;
          if (alpha > 24) {
            if (x < x0) x0 = x;
            if (x > x1) x1 = x;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
          }
        }
      }
      if (!seeThrough || x1 < 0) return; // a plain photo keeps its cover crop
      const cw = x1 - x0 + 1;
      const ch = y1 - y0 + 1;
      const out = document.createElement('canvas');
      out.width = cw;
      out.height = ch;
      out.getContext('2d')?.drawImage(canvas, x0, y0, cw, ch, 0, 0, cw, ch);
      box.classList.add('is-cutout');
      img.src = out.toDataURL('image/png');
    } catch {
      // A picture the canvas may not read: shown as it is.
    }
  };
  probe.src = src;
}

function uploadButton(config: Record<string, unknown>, vector: boolean): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'pz-drop';
  for (const one of String(config.buttonClass ?? '').split(/\s+/)) {
    const clean = one.replace(/[^A-Za-z0-9_-]/g, '');
    if (clean) button.classList.add(clean);
  }
  const text =
    typeof config.buttonText === 'string' && config.buttonText.trim() !== ''
      ? config.buttonText.trim()
      : vector
        ? UPLOAD_TEXTS.vectorButton
        : UPLOAD_TEXTS.imageButton;
  button.innerHTML =
    '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="14" height="12" rx="2"/><circle cx="7.5" cy="8.5" r="1.4"/><path d="M3.5 14.5l4-4 3 3 2.5-2.5 3.5 3.5"/></svg>' +
    `<span>${escapeText(text)}</span>`;
  return button;
}

/**
 * Whether a picture is under the option's minimum — advice, never a refusal.
 *
 * Only raster images are measured: Customily shows no minimum on a Vector
 * Upload at all, and an SVG has no pixel size to compare. A file the browser
 * cannot decode passes — a decoder quirk must not become a lost sale. The
 * caller turns a `true` into the one calm, numberless sentence (see the
 * owner's rule in photoquality.ts); the shop always lets the shopper upload
 * what they have.
 */
function tooSmall(file: File, size: PictureSize | undefined, config: Record<string, unknown>): boolean {
  const minW = Math.max(0, Number(config.minWidth) || 0);
  const minH = Math.max(0, Number(config.minHeight) || 0);
  if (minW <= 0 && minH <= 0) return false;
  if (file.type === 'image/svg+xml' || !size) return false;
  return size.w < minW || size.h < minH;
}

/** One track as the search endpoint returns it. */
interface SpotifyTrackResult {
  id: string;
  uri: string;
  name: string;
  artists: string;
  artist: string;
  album: string;
  albumArt: string | null;
  thumbnail: string | null;
  /** What the thumbnail was called before this payload grew; see the API side. */
  imageUrl?: string | null;
  year: string;
  durationMs: number;
  previewUrl: string | null;
  link: string;
  /** `album`, `single` or `compilation`; absent from an older API. */
  albumType?: string;
  explicit?: boolean;
  isrc?: string | null;
}

/** The picker-row artwork, whichever name the API answered with. */
function trackThumb(track: SpotifyTrackResult): string | null {
  return track.thumbnail ?? track.imageUrl ?? track.albumArt ?? null;
}

/**
 * Measures a photo against what its slot needs, before it is uploaded.
 *
 * Returns undefined when there is nothing to measure against — a template with
 * no print-side photo element, or a browser that will not hand back pixels. An
 * unmeasurable photo is never refused: silence here must mean "carry on", not
 * "blocked", or a browser quirk becomes a lost sale.
 */
async function inspect(
  file: File,
  size: PictureSize | undefined,
  product: ProductDefinition,
  optionId: string,
): Promise<PhotoAssessment | undefined> {
  const requirement = requirementFor(product, optionId);
  // The resolution comes from the header; without it there is nothing to say.
  if (!requirement || !size) return undefined;
  let bitmap: ImageBitmap | undefined;
  try {
    // Sharpness from a small copy only: a camera photo is never decoded whole
    // here (it cost the page its memory — see photo-intake.ts). A browser that
    // cannot make a small copy safely skips the sharpness hint.
    bitmap = await smallBitmap(file, size, 512);
    const pixels = bitmap ? measurementPixels(bitmap) : undefined;
    const sharpness = pixels ? sharpnessOf(pixels) : undefined;
    return assessPhoto(size, requirement, sharpness);
  } catch {
    return undefined;
  } finally {
    bitmap?.close();
  }
}

/** Pixel size of a picture file, from its header; null when the header cannot be read. */
async function imageSize(file: File): Promise<{ w: number; h: number } | null> {
  return (await pictureSize(file)) ?? null;
}

/**
 * A photo answer without the retired "keep the original" flag.
 *
 * Shoppers were once offered a checkbox to skip the background removal; it
 * read as a technicality nobody understood, and it is gone. The original upload
 * is always stored (the order sheet links it beside every cut-out), so the
 * merchant can still fall back to it. An answer restored from before then
 * would otherwise skip the cut with nothing on screen to say so or undo it.
 */
export function withoutKeepOriginal<T>(value: T): T {
  if (!value || typeof value !== 'object' || !('keepOriginal' in value) || !('assetId' in value)) return value;
  const { keepOriginal: _dropped, ...rest } = value as Record<string, unknown>;
  void _dropped;
  return rest as T;
}
